import React, { useState, useEffect, useRef, Suspense } from 'react';
import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import logo from "../../Assets/Images/EGLogo.jpg";
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, BarController, Title, Tooltip, Legend, Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
    Satellite, Leaf, TrendingUp, AlertTriangle,
    Droplets, Thermometer, Bug, Activity, Zap, Radio,
    Clock, MapPin, ChevronRight, Trash2, Bot
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import "../../Assets/Styles/AgriDashboard.css";
import {
    CROP_CALENDAR, CROP_YIELD_PARAMS,
    getCropName, CROP_KEYS
} from './CropData';
import { useNavigate } from "react-router-dom";

// ════════════════════════════════════════════════════════════════
// i18n — LANGUAGE TRANSLATIONS
// Supported: en (English), mr (Marathi), hi (Hindi), te (Telugu)
// ════════════════════════════════════════════════════════════════
const LANGUAGES = [
    { code: 'mr', label: 'मर', name: 'मराठी', flag: '🇮🇳' },
    { code: 'hi', label: 'हि', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'te', label: 'తె', name: 'తెలుగు', flag: '🇮🇳' },
    { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
];

const T = {
    en: {
        // Brand
        tagline: 'Satellite Intelligence Terminal',
        live: 'LIVE',
        // Topbar KPIs
        crop: 'CROP', ndvi: 'CROP HEALTH', moisture: 'MOISTURE', organic: 'ORGANIC', highAlerts: 'HIGH ALERTS',
        // Info bar KPIs
        rootZone: 'Root Zone', soilPh: 'Soil pH', elevation: 'Elevation',
        airTemp: 'Air Temp', humidity: 'Humidity', rain30d: 'Rain 30D',
        yieldEst: 'Yield Est.', alerts: 'Alerts',
        // Sidebar sections
        geoLocation: 'Geo Location', cropIdentification: 'Crop',
        spectralIndices: 'Crop Status', soilMoisture: 'Soil Moisture',
        soilField: 'Soil & Weather', alertsWarnings: 'Alerts & Warnings',
        organicGoals: 'Organic Goals', yieldEstimate: 'Yield Estimate',
        cropCalendar: 'Crop Calendar', currentStage: 'Current Stage',
        yieldDeepTitle: 'Yield Analysis', yieldRange: 'Yield Range',
        yieldConfidence: 'Confidence', yieldMethods: 'Methods Used',
        recentScans: 'RECENT SCANS',
        // Buttons
        smartFarmScan: 'SMART FARM SCAN', scanning: 'SCANNING…',
        // Chart titles
        ndviTrend: 'CROP HEALTH TREND', sixMonth: '3-MONTH',
        soilMoistureChart: 'SOIL MOISTURE', sarSmap: 'SURFACE/ROOT',
        threeDayForecast: '7-DAY FORECAST', openMeteo: 'REAL-TIME',
        organicScore: 'ORGANIC SCORE',
        // Chart labels
        surface05: 'Surface\n0–5cm', rootZone0100: 'Root Zone\n0–100cm',
        rainMm: 'Rain mm', maxC: 'Max °C', minC: 'Min °C', score: 'Score',
        moistureLabel: 'Moisture %',
        // Drawn area panel
        drawnArea: '📐 Drawn Area', previewArea: '⬜ Preview Area',
        unitSqm: 'Sqm',
        // Crop identification
        autoDetected: 'AUTO DETECTED', userConfirmed: 'USER CONFIRMED',
        engineDetected: 'Engine detected',
        alternatives: 'Alternatives',
        // Crop calendar
        seasonLabel: 'Season', weeksTotal: 'weeks total',
        currentStageLabel: 'Current Stage',
        rangeLabel: 'Range', nowLabel: 'Now',
        // Status labels
        healthyCrop: '● HEALTHY CROP', moderateStress: '◐ MODERATE STRESS',
        cropStress: '○ CROP STRESS', waterBare: '≈ WATER / BARE SOIL',
        healthy: 'HEALTHY', moderate: 'MODERATE', stressed: 'STRESSED', waterBareS: 'WATER/BARE',
        // Organic cards
        pass: 'PASS', fail: 'FAIL', fair: 'FAIR', noData: 'NO DATA',
        allPassed: '✓ All criteria passed', criteriaFailed: 'criteria failed',
        runScan: 'Run scan to load', runScanGoal: 'Run scan to evaluate this goal',
        runScanView: 'Run a Smart Farm Scan to view your field report',
        projectedOutput: 'Projected Output',
        // Organic goal labels
        vegHealth: 'Vegetation Health', vegDesc: 'Crop Health, EVI, canopy density',
        waterMgmt: 'Water Management', waterDesc: 'Soil moisture, irrigation adequacy',
        soilHealth: 'Soil Health', soilDesc: 'pH, organic carbon, nitrogen',
        // Soil Health Card
        soilHealthCard: 'Soil Health Card', soilSource: 'SoilGrids ISRIC 250m',
        soilNitrogen: 'Nitrogen', soilSOC: 'Organic Carbon', soilCEC: 'CEC',
        soilClay: 'Clay', soilSand: 'Sand', soilBD: 'Bulk Density',
        soilFertility: 'Fertility Score', soilType: 'Soil Type',
        soilSurface: 'Surface 0-5cm', soilRoot: 'Root Zone 5-15cm',
        soilGovtTest: '🏛 Govt Lab Test ₹5', soilGovtDesc: 'Free test for NPK + micronutrients',
        climateRes: 'Climate Resilience', climateDesc: 'Temperature, humidity, rainfall',
        terrainSuit: 'Terrain Suitability', terrainDesc: 'Elevation, slope, drainage',
        trendStab: 'Trend Stability', trendDesc: 'Crop health trend, seasonal consistency',
        // Tooltip weather
        tooltipRain: 'Rain', tooltipMax: 'Max', tooltipMin: 'Min',
        // Misc
        remove: 'Remove', grade: 'Grade',
        searchPlaceholder: '🔍 Search village, district, location…',
        // Form labels
        latitude: 'Latitude', longitude: 'Longitude',
        startDate: 'Start Date', endDate: 'End Date',
        farmSize: 'Farm Size',
        unitAcres: 'Acres', unitGuntha: 'Guntha', unitMeter: 'Meter²',
        // Map legend
        legendTitle: 'CROP HEALTH',
        legendWaterBare: 'Water / Bare', legendVeryStressed: 'Very Stressed',
        legendStressed: 'Stressed', legendModerate: 'Moderate',
        legendGood: 'Good', legendHealthy: 'Healthy', legendVeryHealthy: 'Very Healthy',
        legendScore: 'Score', legendShowBtn: 'HEALTH',
        // MBox labels
        mboxCropHealth: 'CROP HEALTH', mboxCropGrowth: 'CROP GROWTH',
        mboxMoisture: 'CROP MOISTURE (%)', mboxCropStress: 'CROP STRESS',
        mboxSurface: 'Surface 0-5cm', mboxRootZone: 'Root Zone',
        mboxNitrogen: 'Nitrogen', mboxSoilPh: 'Soil pH',
        mboxElevation: 'Elevation', mboxHumidity: 'Humidity',
        mboxRain30d: 'Rain 30d', mboxAirTemp: 'Air Temp',
        // History card
        histHealth: 'HEALTH',
        // Soil card
        soilGrade: 'Grade',
        soilAlkaline: 'Alkaline', soilNeutral: 'Neutral', soilAcidic: 'Acidic',
        // Dismiss / settings
        openSettings: 'Open Settings', dismiss: 'Dismiss',
        closeBtn: '✕ Close',
        // Form extra
        cropName: 'Crop Name', cropOptional: '(optional)',
        cropPlaceholder: 'Wheat, Maize, Sugarcane…',
        autoFilled: '✅ Area auto-filled in Farm Size — ready to scan',
        // History panel
        lastScans: 'Last', scans: 'scans',
        clearAll: 'Clear All',
        unknown: 'Unknown',
        orgLabel: 'ORG',
        // Yield card
        rainFactor: 'Rain Factor', tempFactor: 'Temp Factor',
        indicative: 'Indicative only. Actual yield depends on variety, inputs & local conditions.',
        totalApprox: 'Total ≈', kgUnit: 'kg', kgHaUnit: 'kg/ha', acresUnit: 'acres',
        custNotice: 'Notice: Crop health and farm information are based on satellite data and are for reference only. We are not responsible for any crop loss, damage, or other issues arising from its use.',
        NDVI: 'Crop health',
        Rice: 'Rice',
        Wheat: 'Wheat',
        Cotton: 'Cotton',
        Soyabean: 'Soyabean',
        Maize: 'Maize/Corn',
        Sugarcane: 'Sugarcane',
        Gram: 'Gram/Chickpea',
        Onion: 'Onion',
        Tomato: 'Tomato',
        Turmeric: 'Turmeric',
        Groundnut: 'Groundnut',
        Banana: 'Banana',
        Sunflower: 'Sunflower',
        Mango: 'Mango',
        Jowar: 'Jowar/Sorghum',
        Bajra: 'Bajra/Millet',
        Tur: 'Tur',
        Grape: 'Grape',
        Pomegranate: 'Pomegranate',
        Papaya: 'Papaya',
        Methi: 'Fenugreek',
        Spinach: 'Spinach',
        Coriander: 'Coriander',
        Chilli: 'Chilli',
        Brinjal: 'Brinjal',
        Garlic: 'Garlic',
        Cauliflower: 'Cauliflower',
        Bhendi: 'Okra',
        Carrot: 'Carrot',
        Radish: 'Radish',
        Ginger: 'Ginger',
        Lauki: 'Bottle Gourd',
        Paddy: 'Paddy',

    },
    mr: {
        tagline: 'उपग्रह बुद्धिमत्ता टर्मिनल',
        live: 'थेट',
        crop: 'पीक', ndvi: 'पीक आरोग्य', moisture: 'पीक ओलावा', organic: 'सेंद्रिय', highAlerts: 'उच्च सूचना',
        rootZone: 'मूळ क्षेत्र', soilPh: 'माती pH', elevation: 'उंची',
        airTemp: 'हवा तापमान', humidity: 'आर्द्रता', rain30d: 'पाऊस 30दि',
        yieldEst: 'उत्पन्न अंदाज', alerts: 'सूचना',
        geoLocation: 'भौगोलिक स्थान', cropIdentification: 'पीक ओळख',
        spectralIndices: 'स्पेक्ट्रल निर्देशांक', soilMoisture: 'माती ओलावा',
        soilField: 'माती व शेत', alertsWarnings: 'सूचना व इशारे',
        organicGoals: 'सेंद्रिय उद्दिष्टे', yieldEstimate: 'उत्पन्न अंदाज',
        cropCalendar: 'पीक दिनदर्शिका', currentStage: 'सध्याची अवस्था',
        yieldDeepTitle: 'उत्पन्न विश्लेषण', yieldRange: 'उत्पन्न श्रेणी',
        yieldConfidence: 'विश्वासार्हता', yieldMethods: 'वापरलेल्या पद्धती',
        recentScans: 'अलीकडील स्कॅन',
        smartFarmScan: 'स्मार्ट शेत स्कॅन', scanning: 'स्कॅनिंग…',
        ndviTrend: 'पीक आरोग्य ट्रेंड', sixMonth: '3-महिना',
        soilMoistureChart: 'माती ओलावा', sarSmap: 'पृष्ठभाग/मूळ',
        threeDayForecast: '७-दिवस अंदाज', openMeteo: 'रिअल-टाइम',
        organicScore: 'सेंद्रिय गुण',
        surface05: 'पृष्ठभाग\n०–५ सेमी', rootZone0100: 'मूळ क्षेत्र\n०–१०० सेमी',
        rainMm: 'पाऊस मिमी', maxC: 'कमाल °से.', minC: 'किमान °से.', score: 'गुण',
        moistureLabel: 'ओलावा %',
        drawnArea: '📐 आखलेले क्षेत्र', previewArea: '⬜ पूर्वावलोकन क्षेत्र',
        unitSqm: 'चौ.मी.',
        autoDetected: 'स्वयं ओळखले', userConfirmed: 'शेतकरी पुष्टी',
        engineDetected: 'यंत्राने ओळखले',
        alternatives: 'पर्यायी पिके',
        seasonLabel: 'हंगाम', weeksTotal: 'एकूण आठवडे',
        currentStageLabel: 'सध्याची अवस्था',
        rangeLabel: 'श्रेणी', nowLabel: 'आता',
        healthyCrop: '● निरोगी पीक', moderateStress: '◐ मध्यम ताण',
        cropStress: '○ पीक ताण', waterBare: '≈ पाणी / उघडी माती',
        healthy: 'निरोगी', moderate: 'मध्यम', stressed: 'तणावग्रस्त', waterBareS: 'पाणी/उघडे',
        pass: 'उत्तीर्ण', fail: 'अनुत्तीर्ण', fair: 'ठीक', noData: 'डेटा नाही',
        allPassed: '✓ सर्व निकष उत्तीर्ण', criteriaFailed: 'निकष अनुत्तीर्ण',
        runScan: 'स्कॅन करा', runScanGoal: 'उद्दिष्ट तपासण्यासाठी स्कॅन करा',
        runScanView: 'शेत माहिती पाहण्यासाठी स्कॅन करा',
        projectedOutput: 'अपेक्षित उत्पन्न',
        vegHealth: 'वनस्पती आरोग्य', vegDesc: 'पीक आरोग्य, EVI, छत घनता',
        waterMgmt: 'जल व्यवस्थापन', waterDesc: 'माती ओलावा, सिंचन',
        soilHealth: 'माती आरोग्य', soilDesc: 'pH, सेंद्रिय कार्बन, नत्र',
        // Soil Health Card
        soilHealthCard: '🌱 माती आरोग्य कार्ड', soilSource: 'सॉइलग्रिड्स ISRIC २५० मी.',
        soilNitrogen: 'नत्र (Nitrogen)', soilSOC: 'सेंद्रिय कार्बन', soilCEC: 'CEC',
        soilClay: 'चिकणमाती %', soilSand: 'वाळू %', soilBD: 'माती घनता',
        soilFertility: 'सुपीकता गुण', soilType: 'माती प्रकार',
        soilSurface: 'पृष्ठभाग ०–५ सेमी', soilRoot: 'मूळ क्षेत्र ५–१५ सेमी',
        soilGovtTest: '🏛 सरकारी माती परीक्षण ₹५', soilGovtDesc: 'NPK + सूक्ष्म अन्नद्रव्ये मोफत',
        climateRes: 'हवामान सहनशीलता', climateDesc: 'तापमान, आर्द्रता, पाऊस',
        terrainSuit: 'भूप्रदेश उपयुक्तता', terrainDesc: 'उंची, उतार, निचरा',
        trendStab: 'ट्रेंड स्थिरता', trendDesc: 'पीक आरोग्य ट्रेंड, हंगामी सातत्य',
        tooltipRain: 'पाऊस', tooltipMax: 'कमाल', tooltipMin: 'किमान',
        remove: 'काढा', grade: 'श्रेणी',
        searchPlaceholder: '🔍 गाव, जिल्हा, ठिकाण शोधा…',
        // Form labels
        latitude: 'अक्षांश', longitude: 'रेखांश',
        startDate: 'सुरुवात तारीख', endDate: 'शेवट तारीख',
        farmSize: 'शेत क्षेत्र',
        unitAcres: 'एकर', unitGuntha: 'गुंठा', unitMeter: 'चौ.मी.',
        // Map legend
        legendTitle: 'पीक आरोग्य',
        legendWaterBare: 'पाणी / उघडी माती', legendVeryStressed: 'अत्यंत ताण',
        legendStressed: 'ताण', legendModerate: 'मध्यम',
        legendGood: 'चांगले', legendHealthy: 'निरोगी', legendVeryHealthy: 'अत्यंत निरोगी',
        legendScore: 'गुण', legendShowBtn: 'आरोग्य',
        // MBox labels
        mboxCropHealth: 'पीक आरोग्य', mboxCropGrowth: 'पीक वाढ',
        mboxMoisture: 'पीक ओलावा', mboxCropStress: 'पीक ताण',
        mboxSurface: 'पृष्ठभाग ०–५ सेमी', mboxRootZone: 'मूळ क्षेत्र',
        mboxNitrogen: 'नाइट्रोजन', mboxSoilPh: 'माती pH',
        mboxElevation: 'उंची', mboxHumidity: 'आर्द्रता',
        mboxRain30d: 'पाऊस 30दि', mboxAirTemp: 'हवा तापमान',
        // History card
        histHealth: 'आरोग्य',
        // Soil card
        soilGrade: 'श्रेणी',
        soilAlkaline: 'क्षारीय', soilNeutral: 'तटस्थ', soilAcidic: 'आम्लीय',
        // Dismiss / settings
        openSettings: 'सेटिंग्ज उघडा', dismiss: 'बंद करा',
        closeBtn: '✕ बंद करा',
        // Form extra
        cropName: 'पीक नाव', cropOptional: '(पर्यायी)',
        cropPlaceholder: 'गहू, मक्का, ऊस…',
        autoFilled: '✅ क्षेत्र शेत आकारात भरले — स्कॅनसाठी तयार',
        // History panel
        lastScans: 'शेवटचे', scans: 'स्कॅन',
        clearAll: 'सर्व हटवा',
        unknown: 'अज्ञात',
        orgLabel: 'सेंद्रिय',
        rainFactor: 'पाऊस घटक', tempFactor: 'तापमान घटक',
        indicative: 'अंदाजे माहिती. प्रत्यक्ष उत्पन्न वाण, निविष्ठा आणि स्थानिक परिस्थितीवर अवलंबून असते.',
        totalApprox: 'एकूण ≈', kgUnit: 'किग्रॅ', kgHaUnit: 'किग्रॅ/हे', acresUnit: 'एकर',
        custNotice: 'सूचना: पिकांचे आरोग्य आणि शेताची माहिती उपग्रह(Satellite) डेटावर आधारित असून ती केवळ माहितीसाठी आहे.या माहितीच्या वापरामुळे होणाऱ्या कोणत्याही पीक नुकसानीसाठी, हानीसाठी किंवा इतर समस्यांसाठी आम्ही जबाबदार राहणार नाही.',
        NDVI: 'पीक आरोग्य',
        Rice: 'तांदूळ',
        Wheat: 'गहू',
        Cotton: 'कापूस',
        Soyabean: 'सोयाबीन',
        Maize: 'मका',
        Sugarcane: 'ऊस',
        Gram: 'हरभरा',
        Onion: 'कांदा',
        Tomato: 'टोमॅटो',
        Turmeric: 'हळद',
        Groundnut: 'भुईमूग',
        Banana: 'केळी',
        Sunflower: 'सूर्यफूल',
        Mango: 'आंबा',
        Jowar: 'ज्वारी',
        Bajra: 'बाजरी',
        Tur: 'तूर',
        Grape: 'द्राक्ष',
        Pomegranate: 'डाळिंब',
        Papaya: 'पपई',
        Methi: 'मेथी',
        Spinach: 'पालक',
        Coriander: 'कोथिंबीर',
        Chilli: 'मिरची',
        Brinjal: 'वांगी',
        Garlic: 'लसूण',
        Cauliflower: 'फुलकोबी',
        Bhendi: 'भेंडी',
        Carrot: 'गाजर',
        Radish: 'मुळा',
        Ginger: 'आले',
        Lauki: 'दुधी भोपळा',
        Paddy: 'धान',

    },
    hi: {
        tagline: 'सैटेलाइट इंटेलिजेंस टर्मिनल',
        live: 'लाइव',
        crop: 'फसल', ndvi: 'फसल स्वास्थ्य', moisture: 'नमी', organic: 'जैविक', highAlerts: 'उच्च चेतावनी',
        rootZone: 'जड़ क्षेत्र', soilPh: 'मिट्टी pH', elevation: 'ऊंचाई',
        airTemp: 'हवा तापमान', humidity: 'आर्द्रता', rain30d: 'बारिश 30दि',
        yieldEst: 'उत्पादन अनुमान', alerts: 'चेतावनी',
        geoLocation: 'भौगोलिक स्थान', cropIdentification: 'फसल पहचान',
        spectralIndices: 'स्पेक्ट्रल सूचकांक', soilMoisture: 'मिट्टी नमी',
        soilField: 'मिट्टी व खेत', alertsWarnings: 'चेतावनी व सूचनाएं',
        organicGoals: 'जैविक लक्ष्य', yieldEstimate: 'उत्पादन अनुमान',
        recentScans: 'हालिया स्कैन',
        smartFarmScan: 'स्मार्ट खेत स्कैन', scanning: 'स्कैनिंग…',
        ndviTrend: 'फसल स्वास्थ्य ट्रेंड', sixMonth: '3-माह',
        soilMoistureChart: 'मिट्टी नमी', sarSmap: 'सतह/जड़',
        threeDayForecast: '७-दिन पूर्वानुमान', openMeteo: 'रियल-टाइम',
        organicScore: 'जैविक स्कोर',
        surface05: 'सतह\n०–५ सेमी', rootZone0100: 'जड़ क्षेत्र\n०–१०० सेमी',
        rainMm: 'बारिश mm', maxC: 'अधिकतम °से.', minC: 'न्यूनतम °से.', score: 'स्कोर',
        moistureLabel: 'नमी %',
        drawnArea: '📐 खींचा क्षेत्र', previewArea: '⬜ पूर्वावलोकन क्षेत्र',
        unitSqm: 'वर्ग मी.',
        autoDetected: 'स्वतः पहचाना', userConfirmed: 'किसान पुष्टि',
        engineDetected: 'इंजन द्वारा पहचाना',
        alternatives: 'वैकल्पिक फसलें',
        seasonLabel: 'मौसम', weeksTotal: 'कुल सप्ताह',
        currentStageLabel: 'वर्तमान चरण',
        rangeLabel: 'सीमा', nowLabel: 'अभी',
        healthyCrop: '● स्वस्थ फसल', moderateStress: '◐ मध्यम तनाव',
        cropStress: '○ फसल तनाव', waterBare: '≈ पानी / खाली मिट्टी',
        healthy: 'स्वस्थ', moderate: 'मध्यम', stressed: 'तनावग्रस्त', waterBareS: 'पानी/खाली',
        pass: 'उत्तीर्ण', fail: 'अनुत्तीर्ण', fair: 'ठीक', noData: 'डेटा नहीं',
        allPassed: '✓ सभी मानदंड पास', criteriaFailed: 'मानदंड विफल',
        runScan: 'स्कैन चलाएं', runScanGoal: 'लक्ष्य जांचने के लिए स्कैन करें',
        runScanView: 'खेत जानकारी देखने के लिए स्कैन करें',
        projectedOutput: 'अनुमानित उत्पादन',
        vegHealth: 'वनस्पति स्वास्थ्य', vegDesc: 'फसल स्वास्थ्य, EVI, छत्र घनत्व',
        waterMgmt: 'जल प्रबंधन', waterDesc: 'मिट्टी नमी, सिंचाई',
        soilHealth: 'मिट्टी स्वास्थ्य', soilDesc: 'pH, जैव कार्बन, नाइट्रोजन',
        // Soil Health Card
        soilHealthCard: '🌱 मिट्टी स्वास्थ्य कार्ड', soilSource: 'सॉइलग्रिड्स (ISRIC) – २५० मीटर',
        soilNitrogen: 'नाइट्रोजन', soilSOC: 'जैव कार्बन', soilCEC: 'CEC',
        soilClay: 'मिट्टी %', soilSand: 'रेत %', soilBD: 'मिट्टी घनत्व',
        soilFertility: 'उर्वरता स्कोर', soilType: 'मिट्टी प्रकार',
        soilSurface: 'सतह ०–५ सेमी', soilRoot: 'जड़ क्षेत्र ५–१५ सेमी',
        soilGovtTest: '🏛 सरकारी मिट्टी परीक्षण ₹5', soilGovtDesc: 'NPK + सूक्ष्म पोषक तत्व मुफ्त',
        // Missing Hindi keys
        cropCalendar: 'फसल कैलेंडर', currentStage: 'वर्तमान चरण',
        yieldDeepTitle: 'उत्पादन विश्लेषण', yieldRange: 'उत्पादन सीमा',
        yieldConfidence: 'विश्वसनीयता', yieldMethods: 'उपयोग की गई विधियां',
        climateRes: 'जलवायु लचीलापन', climateDesc: 'तापमान, आर्द्रता, वर्षा',
        terrainSuit: 'भूभाग उपयुक्तता', terrainDesc: 'ऊंचाई, ढलान, जल निकासी',
        trendStab: 'ट्रेंड स्थिरता', trendDesc: 'फसल स्वास्थ्य ट्रेंड, मौसमी निरंतरता',
        tooltipRain: 'बारिश', tooltipMax: 'अधिकतम', tooltipMin: 'न्यूनतम',
        remove: 'हटाएं', grade: 'ग्रेड',
        searchPlaceholder: '🔍 गाँव, जिला, स्थान खोजें…',
        // Form labels
        latitude: 'अक्षांश', longitude: 'देशांतर',
        startDate: 'शुरू तारीख', endDate: 'अंत तारीख',
        farmSize: 'खेत क्षेत्र',
        unitAcres: 'एकड़', unitGuntha: 'गुंठा', unitMeter: 'वर्ग मी.',
        // Map legend
        legendTitle: 'फसल स्वास्थ्य',
        legendWaterBare: 'पानी / खाली मिट्टी', legendVeryStressed: 'बहुत तनाव',
        legendStressed: 'तनाव', legendModerate: 'मध्यम',
        legendGood: 'अच्छा', legendHealthy: 'स्वस्थ', legendVeryHealthy: 'बहुत स्वस्थ',
        legendScore: 'स्कोर', legendShowBtn: 'स्वास्थ्य',
        // MBox labels
        mboxCropHealth: 'फसल स्वास्थ्य', mboxCropGrowth: 'फसल वृद्धि',
        mboxMoisture: 'नमी', mboxCropStress: 'फसल तनाव',
        mboxSurface: 'सतह ०–५ सेमी', mboxRootZone: 'जड़ क्षेत्र',
        mboxNitrogen: 'नाइट्रोजन', mboxSoilPh: 'मिट्टी pH',
        mboxElevation: 'ऊंचाई', mboxHumidity: 'आर्द्रता',
        mboxRain30d: 'बारिश 30दि', mboxAirTemp: 'हवा तापमान',
        // History card
        histHealth: 'स्वास्थ्य',
        // Soil card
        soilGrade: 'ग्रेड',
        soilAlkaline: 'क्षारीय', soilNeutral: 'उदासीन', soilAcidic: 'अम्लीय',
        // Dismiss / settings
        openSettings: 'सेटिंग खोलें', dismiss: 'बंद करें',
        closeBtn: '✕ बंद करें',
        // Form extra
        cropName: 'फसल का नाम', cropOptional: '(वैकल्पिक)',
        cropPlaceholder: 'गेहूं, मक्का, गन्ना…',
        autoFilled: '✅ क्षेत्र खेत आकार में भरा गया — स्कैन के लिए तैयार',
        // History panel
        lastScans: 'अंतिम', scans: 'स्कैन',
        clearAll: 'सब हटाएं',
        unknown: 'अज्ञात',
        orgLabel: 'जैविक',
        rainFactor: 'वर्षा कारक', tempFactor: 'तापमान कारक',
        indicative: 'केवल अनुमानित। वास्तविक उत्पादन किस्म, आदानों और स्थानीय परिस्थितियों पर निर्भर करता है।',
        totalApprox: 'कुल ≈', kgUnit: 'किग्रा', kgHaUnit: 'किग्रा/हे', acresUnit: 'एकड़',
        custNotice: 'सूचना: फसल की स्थिति और खेत की जानकारी उपग्रह (Satellite) डेटा पर आधारित है और केवल जानकारी के लिए है। इस जानकारी के उपयोग से होने वाले किसी भी फसल नुकसान, हानि या अन्य समस्याओं के लिए हम जिम्मेदार नहीं होंगे।',
        NDVI: 'फसल स्वास्थ्य',
        Rice: 'चावल',
        Wheat: 'गेहूं',
        Cotton: 'कपास',
        Soyabean: 'सोयाबीन',
        Maize: 'मक्का',
        Sugarcane: 'गन्ना',
        Gram: 'चना',
        Onion: 'प्याज',
        Tomato: 'टमाटर',
        Turmeric: 'हल्दी',
        Groundnut: 'मूंगफली',
        Banana: 'केला',
        Sunflower: 'सूरजमुखी',
        Mango: 'आम',
        Jowar: 'ज्वार',
        Bajra: 'बाजरा',
        Tur: 'अरहर',
        Grape: 'अंगूर',
        Pomegranate: 'अनार',
        Papaya: 'पपीता',
        Methi: 'मेथी',
        Spinach: 'पालक',
        Coriander: 'धनिया',
        Chilli: 'मिर्च',
        Brinjal: 'बैंगन',
        Garlic: 'लहसुन',
        Cauliflower: 'फूलगोभी',
        Bhendi: 'भिंडी',
        Carrot: 'गाजर',
        Radish: 'मूली',
        Ginger: 'अदरक',
        Lauki: 'लौकी',
        Paddy: 'धान',

    },
    te: {
        tagline: 'శాటిలైట్ ఇంటెలిజెన్స్ టెర్మినల్',
        live: 'లైవ్',
        crop: 'పంట', ndvi: 'పంట ఆరోగ్యం', moisture: 'తేమ', organic: 'సేంద్రీయ', highAlerts: 'అధిక హెచ్చరికలు',
        rootZone: 'వేర్ల మండలం', soilPh: 'నేల pH', elevation: 'ఎత్తు',
        airTemp: 'గాలి ఉష్ణోగ్రత', humidity: 'తేమశాతం', rain30d: 'వర్షం 30రో',
        yieldEst: 'దిగుబడి అంచనా', alerts: 'హెచ్చరికలు',
        geoLocation: 'భౌగోళిక స్థానం', cropIdentification: 'పంట గుర్తింపు',
        spectralIndices: 'స్పెక్ట్రల్ సూచీలు', soilMoisture: 'నేల తేమ',
        soilField: 'నేల & పొలం', alertsWarnings: 'హెచ్చరికలు',
        organicGoals: 'సేంద్రీయ లక్ష్యాలు', yieldEstimate: 'దిగుబడి అంచనా',
        recentScans: 'ఇటీవలి స్కాన్లు',
        smartFarmScan: 'స్మార్ట్ పొలం స్కాన్', scanning: 'స్కానింగ్…',
        ndviTrend: 'పంట ఆరోగ్య ట్రెండ్', sixMonth: '6-నెలలు',
        soilMoistureChart: 'నేల తేమ', sarSmap: 'ఉపరితలం/వేరు',
        threeDayForecast: '7-రోజుల అంచనా', openMeteo: 'రియల్-టైమ్',
        organicScore: 'సేంద్రీయ స్కోర్',
        surface05: 'ఉపరితలం\n0–5cm', rootZone0100: 'వేర్ల మండలం\n0–100cm',
        rainMm: 'వర్షం mm', maxC: 'గరిష్ఠ °C', minC: 'కనిష్ఠ °C', score: 'స్కోర్',
        moistureLabel: 'తేమ %',
        drawnArea: '📐 గీసిన విస్తీర్ణం', previewArea: '⬜ పూర్వావలోకన విస్తీర్ణం',
        unitSqm: 'చ.మీ.',
        autoDetected: 'స్వయంచాలక గుర్తింపు', userConfirmed: 'రైతు ధృవీకరణ',
        engineDetected: 'ఇంజన్ గుర్తించింది',
        alternatives: 'ప్రత్యామ్నాయ పంటలు',
        seasonLabel: 'సీజన్', weeksTotal: 'మొత్తం వారాలు',
        currentStageLabel: 'ప్రస్తుత దశ',
        rangeLabel: 'పరిధి', nowLabel: 'ఇప్పుడు',
        cropCalendar: 'పంట క్యాలెండర్', currentStage: 'ప్రస్తుత దశ',
        yieldDeepTitle: 'దిగుబడి విశ్లేషణ', yieldRange: 'దిగుబడి పరిధి',
        healthyCrop: '● ఆరోగ్యకరమైన పంట', moderateStress: '◐ మధ్యస్థ ఒత్తిడి',
        cropStress: '○ పంట ఒత్తిడి', waterBare: '≈ నీరు / బీడు నేల',
        healthy: 'ఆరోగ్యకరం', moderate: 'మధ్యస్థం', stressed: 'ఒత్తిడిలో', waterBareS: 'నీరు/బీడు',
        pass: 'ఉత్తీర్ణం', fail: 'అనుత్తీర్ణం', fair: 'సరే', noData: 'డేటా లేదు',
        allPassed: '✓ అన్ని నిబంధనలు ఉత్తీర్ణం', criteriaFailed: 'నిబంధనలు విఫలమయ్యాయి',
        runScan: 'స్కాన్ చేయండి', runScanGoal: 'లక్ష్యం తనిఖీ చేయడానికి స్కాన్ చేయండి',
        runScanView: 'పొలం సమాచారం చూడడానికి స్కాన్ చేయండి',
        projectedOutput: 'అంచనా దిగుబడి',
        vegHealth: 'వృక్షసంపద ఆరోగ్యం', vegDesc: 'పంట ఆరోగ్యం, EVI, పైకప్పు సాంద్రత',
        waterMgmt: 'నీటి నిర్వహణ', waterDesc: 'నేల తేమ, నీటిపారుదల',
        soilHealth: 'నేల ఆరోగ్యం', soilDesc: 'pH, సేంద్రీయ కార్బన్, నత్రజని',
        climateRes: 'వాతావరణ స్థితిస్థాపకత', climateDesc: 'ఉష్ణోగ్రత, తేమ, వర్షపాతం',
        terrainSuit: 'భూభాగ అనుకూలత', terrainDesc: 'ఎత్తు, వాలు, నీటి పారుదల',
        trendStab: 'ట్రెండ్ స్థిరత్వం', trendDesc: 'పంట ఆరోగ్య ట్రెండ్, కాలానుగుణ స్థిరత్వం',
        tooltipRain: 'వర్షం', tooltipMax: 'గరిష్ఠ', tooltipMin: 'కనిష్ఠ',
        remove: 'తొలగించు', grade: 'గ్రేడ్',
        searchPlaceholder: '🔍 గ్రామం, జిల్లా, స్థలం వెతకండి…',
        // Form labels
        latitude: 'అక్షాంశం', longitude: 'రేఖాంశం',
        startDate: 'ప్రారంభ తేదీ', endDate: 'ముగింపు తేదీ',
        farmSize: 'పొలం విస్తీర్ణం',
        unitAcres: 'ఎకరాలు', unitGuntha: 'గుంట', unitMeter: 'చ.మీ.',
        // Map legend
        legendTitle: 'పంట ఆరోగ్యం',
        legendWaterBare: 'నీరు / బీడు నేల', legendVeryStressed: 'తీవ్ర ఒత్తిడి',
        legendStressed: 'ఒత్తిడి', legendModerate: 'మధ్యస్థం',
        legendGood: 'బాగుంది', legendHealthy: 'ఆరోగ్యకరం', legendVeryHealthy: 'చాలా ఆరోగ్యకరం',
        legendScore: 'స్కోర్', legendShowBtn: 'ఆరోగ్యం',
        // MBox labels
        mboxCropHealth: 'పంట ఆరోగ్యం', mboxCropGrowth: 'పంట వృద్ధి',
        mboxMoisture: 'తేమ', mboxCropStress: 'పంట ఒత్తిడి',
        mboxSurface: 'ఉపరితలం 0-5cm', mboxRootZone: 'వేర్ల మండలం',
        mboxNitrogen: 'నత్రజని', mboxSoilPh: 'నేల pH',
        mboxElevation: 'ఎత్తు', mboxHumidity: 'తేమశాతం',
        mboxRain30d: 'వర్షం 30రో', mboxAirTemp: 'గాలి ఉష్ణోగ్రత',
        // History card
        histHealth: 'ఆరోగ్యం',
        // Soil card
        soilGrade: 'గ్రేడ్',
        soilAlkaline: 'క్షారీయ', soilNeutral: 'తటస్థ', soilAcidic: 'ఆమ్లీయ',
        // Dismiss / settings
        openSettings: 'సెట్టింగ్స్ తెరవండి', dismiss: 'మూసివేయి',
        closeBtn: '✕ మూసివేయి',
        // Form extra
        cropName: 'పంట పేరు', cropOptional: '(ఐచ్ఛికం)',
        cropPlaceholder: 'గోధుమ, మొక్కజొన్న, చెరకు…',
        autoFilled: '✅ పొలం పరిమాణం నింపబడింది — స్కాన్‌కు సిద్ధం',
        // History panel
        lastScans: 'చివరి', scans: 'స్కాన్‌లు',
        clearAll: 'అన్నీ తొలగించు',
        unknown: 'తెలియదు',
        orgLabel: 'సేంద్రీయ',
        rainFactor: 'వర్షపు కారకం', tempFactor: 'ఉష్ణోగ్రత కారకం',
        indicative: 'అంచనా మాత్రమే. వాస్తవ దిగుబడి రకం, ఇన్‌పుట్లు & స్థానిక పరిస్థితులపై ఆధారపడుతుంది.',
        totalApprox: 'మొత్తం ≈', kgUnit: 'కి.గ్రా', kgHaUnit: 'కి.గ్రా/హె', acresUnit: 'ఎకరాలు',
        custNotice: 'గమనిక: పంట ఆరోగ్యం మరియు వ్యవసాయ భూమి సమాచారం ఉపగ్రహ (Satellite) డేటా ఆధారంగా ఇవ్వబడింది మరియు ఇది సమాచారం కోసం మాత్రమే. ఈ సమాచారాన్ని ఉపయోగించడం వల్ల కలిగే పంట నష్టం, హాని లేదా ఇతర సమస్యలకు మేము బాధ్యత వహించము.',
        NDVI: 'పంట ఆరోగ్యం',
        Rice: 'బియ్యం',
        Wheat: 'గోధుమ',
        Cotton: 'పత్తి',
        Soyabean: 'సోయాబీన్',
        Maize: 'మొక్కజొన్న',
        Sugarcane: 'చెరకు',
        Gram: 'సెనగ',
        Onion: 'ఉల్లిపాయ',
        Tomato: 'టమాటా',
        Turmeric: 'పసుపు',
        Groundnut: 'వేరుశెనగ',
        Banana: 'అరటి',
        Sunflower: 'పొద్దుతిరుగుడు',
        Mango: 'మామిడి',
        Jowar: 'జొన్న',
        Bajra: 'సజ్జ',
        Tur: 'కంది',
        Grape: 'ద్రాక్ష',
        Pomegranate: 'దానిమ్మ',
        Papaya: 'బొప్పాయి',
        Methi: 'మెంతి',
        Spinach: 'పాలకూర',
        Coriander: 'కొత్తిమీర',
        Chilli: 'మిరపకాయ',
        Brinjal: 'వంకాయ',
        Garlic: 'వెల్లుల్లి',
        Cauliflower: 'కాలీఫ్లవర్',
        Bhendi: 'బెండకాయ',
        Carrot: 'క్యారెట్',
        Radish: 'ముల్లంగి',
        Ginger: 'అల్లం',
        Lauki: 'సొరకాయ',
        Paddy: 'వరి',
    },
};


// ── Chart.js registration ────────────────────────────────────────
ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, BarController, Title, Tooltip, Legend, Filler,
    ChartDataLabels
);

// Global chart style
ChartJS.defaults.color = '#7090a8';
ChartJS.defaults.font.family = "'JetBrains Mono', monospace";
ChartJS.defaults.font.size = 9;
// Disable datalabels globally — only enable per-dataset where explicitly set
ChartJS.defaults.set('plugins.datalabels', { display: false });

// ════════════════════════════════════════════════════════════════
// Shared chart options builder
// ════════════════════════════════════════════════════════════════
const mkChart = (overrides = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 700, easing: 'easeOutQuart' },
    plugins: {
        legend: {
            labels: {
                color: '#b8d0e8',
                font: { size: 14, family: "'JetBrains Mono', monospace" },
                boxWidth: 8, padding: 10,
            }
        },
        tooltip: {
            backgroundColor: '#0A0F16',
            borderColor: 'rgba(255,255,255,0.08)', borderWidth: 1,
            titleColor: '#b8d0e8', bodyColor: '#E8F0FA',
            titleFont: { size: 12 }, bodyFont: { size: 13 },
            padding: 9, cornerRadius: 5,
        }
    },
    scales: {
        x: {
            ticks: { color: '#b8d0e8', font: { size: 13 }, maxRotation: 40 },
            grid: { color: 'rgba(255,255,255,0.04)' },
        },
        y: {
            ticks: { color: '#b8d0e8', font: { size: 13 } },
            grid: { color: 'rgba(255,255,255,0.04)' },
        },
    },
    ...overrides,
});

// ════════════════════════════════════════════════════════════════
// Multilingual Crop Name Lookup
// Used as fallback when API doesn't return Hindi/Telugu crop names
// ════════════════════════════════════════════════════════════════
const CROP_NAMES_ML = {
    // English key → { mr, hi, te }
    'Wheat': { mr: 'गहू', hi: 'गेहूँ', te: 'గోధుమ' },
    'Rice': { mr: 'तांदूळ', hi: 'धान/चावल', te: 'వరి' },
    'Paddy': { mr: 'भात', hi: 'धान', te: 'వరి' },
    'Cotton': { mr: 'कापूस', hi: 'कपास', te: 'పత్తి' },
    'Sugarcane': { mr: 'ऊस', hi: 'गन्ना', te: 'చెరకు' },
    'Soybean': { mr: 'सोयाबीन', hi: 'सोयाबीन', te: 'సోయాబీన్' },
    'Soyabean': { mr: 'सोयाबीन', hi: 'सोयाबीन', te: 'సోయాబీన్' },
    'Maize': { mr: 'मका', hi: 'मक्का', te: 'మొక్కజొన్న' },
    'Corn': { mr: 'मका', hi: 'मक्का', te: 'మొక్కజొన్న' },
    'Chickpea': { mr: 'हरभरा', hi: 'चना', te: 'శనగ' },
    'Gram': { mr: 'हरभरा', hi: 'चना', te: 'శనగ' },
    'Onion': { mr: 'कांदा', hi: 'प्याज', te: 'ఉల్లిపాయ' },
    'Tomato': { mr: 'टोमॅटो', hi: 'टमाटर', te: 'టమాటా' },
    'Potato': { mr: 'बटाटा', hi: 'आलू', te: 'బంగాళాదుంప' },
    'Banana': { mr: 'केळी', hi: 'केला', te: 'అరటి' },
    'Turmeric': { mr: 'हळद', hi: 'हल्दी', te: 'పసుపు' },
    'Groundnut': { mr: 'भुईमूग', hi: 'मूंगफली', te: 'వేరుశెనగ' },
    'Sunflower': { mr: 'सूर्यफूल', hi: 'सूरजमुखी', te: 'పొద్దుతిరుగుడు' },
    'Jowar': { mr: 'ज्वारी', hi: 'ज्वार', te: 'జొన్న' },
    'Sorghum': { mr: 'ज्वारी', hi: 'ज्वार', te: 'జొన్న' },
    'Bajra': { mr: 'बाजरी', hi: 'बाजरा', te: 'సజ్జ' },
    'Millet': { mr: 'बाजरी', hi: 'बाजरा', te: 'సజ్జ' },
    'Lentil': { mr: 'मसूर', hi: 'मसूर', te: 'మసూర్' },
    'Pigeon Pea': { mr: 'तूर', hi: 'अरहर', te: 'కంది' },
    'Tur': { mr: 'तूर', hi: 'अरहर', te: 'కంది' },
    'Arhar': { mr: 'तूर', hi: 'अरहर', te: 'కంది' },
    'Grape': { mr: 'द्राक्ष', hi: 'अंगूर', te: 'ద్రాక్ష' },
    'Pomegranate': { mr: 'डाळिंब', hi: 'अनार', te: 'దానిమ్మ' },
    'Mango': { mr: 'आंबा', hi: 'आम', te: 'మామిడి' },
    'Orange': { mr: 'संत्री', hi: 'संतरा', te: 'నారంగి' },
    'Coconut': { mr: 'नारळ', hi: 'नारियल', te: 'కొబ్బరి' },
    'Palm': { mr: 'ताड', hi: 'ताड़', te: 'తాటి' },
    'Ginger': { mr: 'आले', hi: 'अदरक', te: 'అల్లం' },
    'Garlic': { mr: 'लसूण', hi: 'लहसुन', te: 'వెల్లుల్లి' },
    'Fallow': { mr: 'पडीक जमीन', hi: 'परती भूमि', te: 'బీడు భూమి' },
    'Unknown': { mr: 'अज्ञात', hi: 'अज्ञात', te: 'తెలియదు' },
};

// [FIX — Crop Calendar showing empty for real crops like Cotton] Reverse
// lookup: localized crop name (whatever the farmer actually typed, often
// in Marathi/Hindi/Telugu, e.g. "कापूस") -> the English CROP_CALENDAR key
// ("Cotton"). The existing crop-matching logic only ever compared the
// typed text against English key text/substrings — a Devanagari or
// Telugu string can never match an English key that way, so real,
// existing CROP_CALENDAR data (Cotton has full stage data!) was silently
// unreachable whenever the farmer typed the name in their own language,
// falling through to 'Unknown' every time.
function resolveCropKeyFromAnyLanguage(typedName) {
    if (!typedName) return null;
    const needle = typedName.trim().toLowerCase();
    if (!needle) return null;
    for (const [engKey, translations] of Object.entries(CROP_NAMES_ML)) {
        if (engKey.toLowerCase() === needle) return engKey;
        for (const localName of Object.values(translations)) {
            if (localName && localName.toLowerCase() === needle) return engKey;
        }
    }
    return null;
}

// Resolve crop name from API + fallback to CROP_NAMES_ML lookup
function resolveCropName(cropP, apiCropData, lang) {
    if (!cropP) return null;
    if (lang === 'mr') return apiCropData?.crop_Marathi || CROP_NAMES_ML[cropP]?.mr || cropP;
    if (lang === 'hi') return apiCropData?.crop_Hindi || CROP_NAMES_ML[cropP]?.hi || cropP;
    if (lang === 'te') return apiCropData?.crop_Telugu || CROP_NAMES_ML[cropP]?.te || cropP;

    return cropP;

}

// [FIX — Sugarcane/long-duration-crop trend window] Derives the crop's
// real growing-cycle length from CROP_CALENDAR.stages (already-active,
// ICAR/MAHA/NDDB-sourced data) — the LAST stage's end-week × 7 gives total
// duration in days. E.g. Sugarcane's last stage is [44,52] weeks -> 364
// days (~12 months), matching its "Long duration 10-18 months" note.
// Falls back to 90 days (the previous fixed default, unchanged for every
// other crop) if the crop isn't found or has no stages defined.
function getCropDurationDays(cropKey) {
    // [FIX — same root cause as the Crop Calendar bug] Resolve a
    // localized name (e.g. "ऊस") to its English CROP_CALENDAR key first.
    const resolvedKey = resolveCropKeyFromAnyLanguage(cropKey) || cropKey;
    const cal = CROP_CALENDAR?.[resolvedKey];
    if (!cal || !Array.isArray(cal.stages) || cal.stages.length === 0) return 90;
    const maxWeek = Math.max(...cal.stages.map(s => Array.isArray(s.weeks) ? s.weeks[1] : 0));
    return maxWeek > 0 ? Math.round(maxWeek * 7) : 90;
}

// Actual satellite-history FETCH window is capped — a longer window means
// more Sentinel-2/HLS revisit dates to pull (each a cheap single-band NDVI
// fetch, but still more requests), so we don't blindly fetch a full
// 12-18 month history just because the crop's total lifecycle is that
// long. 240 days (~8 months) comfortably covers a full short/medium-cycle
// crop and a very substantial portion of a long-duration crop's growth,
// without the cost of fetching the entire life cycle every scan.
const CROP_HISTORY_WINDOW_CAP_DAYS = 240;
function getCropHistoryWindowDays(cropKey) {
    return Math.min(CROP_HISTORY_WINDOW_CAP_DAYS, Math.max(90, getCropDurationDays(cropKey)));
}


const A = {
    flood: { c: '#00D4FF', bg: 'rgba(0,212,255,0.09)', ib: 'rgba(0,212,255,0.18)', icon: <Droplets size={11} /> },
    drought: { c: '#FFB800', bg: 'rgba(255,184,0,0.09)', ib: 'rgba(255,184,0,0.18)', icon: <Thermometer size={11} /> },
    health: { c: '#FF4A6E', bg: 'rgba(255,74,110,0.08)', ib: 'rgba(255,74,110,0.18)', icon: <Activity size={11} /> },
    organic: { c: '#39FF14', bg: 'rgba(57,255,20,0.08)', ib: 'rgba(57,255,20,0.18)', icon: <Leaf size={11} /> },
    disease: { c: '#A78BFA', bg: 'rgba(167,139,250,0.08)', ib: 'rgba(167,139,250,0.18)', icon: <Bug size={11} /> },
    default: { c: '#b8d0e8', bg: 'rgba(139,160,184,0.07)', ib: 'rgba(139,160,184,0.15)', icon: <AlertTriangle size={11} /> },
};
const acfg = t => A[t] || A.default;

// ════════════════════════════════════════════════════════════════
// MapController
// ════════════════════════════════════════════════════════════════
// ── Auto-fly map when lat/lon changes (from search select) ─────
function FlyToLocation({ lat, lon }) {
    const map = useMap();
    const prevRef = useRef({ lat, lon });
    useEffect(() => {
        const prev = prevRef.current;
        if (parseFloat(lat) !== parseFloat(prev.lat) || parseFloat(lon) !== parseFloat(prev.lon)) {
            map.flyTo([parseFloat(lat), parseFloat(lon)], 15, { duration: 1.2 });
            prevRef.current = { lat, lon };
        }
    }, [lat, lon, map]);
    return null;
}

function MapController({ onSelect, boundary, onDrawn }) {
    const map = useMap();

    // ── STALE CLOSURE FIX ────────────────────────────────────────
    // useMapEvents registers handlers ONCE at mount, capturing props by
    // value. When acres/unit change in parent, the old onDrawn closure
    // still holds the original values. Using a ref solves this —
    // the ref is always updated on every render, so the handler
    // always calls the LATEST version of onDrawn.
    const onDrawnRef = useRef(onDrawn);
    const onSelectRef = useRef(onSelect);
    useEffect(() => { onDrawnRef.current = onDrawn; });  // no deps → runs every render
    useEffect(() => { onSelectRef.current = onSelect; });

    useEffect(() => {
        if (map.pm) {
            map.pm.addControls({
                position: 'topleft',
                drawMarker: false, drawPolyline: false, drawRectangle: false,
                editMode: true, dragMode: true, removalMode: true,
            });
        }
        return () => { };
    }, [map]);

    useMapEvents({
        click(e) {
            // Use ref so we always call the latest onSelect
            onSelectRef.current(+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6));
            map.flyTo(e.latlng, map.getZoom());
        },
        // Capture farmer-drawn polygon from Geoman
        'pm:create'(e) {
            if (!e.layer) return;
            const lls = e.layer.getLatLngs?.()[0] || [];
            if (lls.length >= 3) {
                const coords = lls.map(ll => [ll.lat, ll.lng]);
                // Use ref — always has the CURRENT acres/unit values from parent
                const accepted = onDrawnRef.current?.(coords);
                if (accepted === false) {
                    // Rejected (too large) — flash red 2s so farmer sees boundary
                    e.layer.setStyle({ color: '#FF4A6E', fillColor: '#FF4A6E', fillOpacity: 0.22, weight: 3 });
                    setTimeout(() => { try { e.layer.remove(); } catch (_) { } }, 2500);
                } else {
                    e.layer.remove();  // accepted — our Polygon component renders it
                }
            }
        },
        'pm:edit'(e) {
            const lls = e.layer?.getLatLngs?.()[0] || [];
            // Use ref for latest callback
            if (lls.length >= 3) onDrawnRef.current?.(lls.map(ll => [ll.lat, ll.lng]));
        },
    });

    useEffect(() => {
        if (boundary?.length > 0) map.fitBounds(boundary);
    }, [boundary, map]);

    return null;
}

// ════════════════════════════════════════════════════════════════
// ConfRing
// ════════════════════════════════════════════════════════════════
function ConfRing({ val, lang = 'en' }) {
    const r = 20, circ = 2 * Math.PI * r, p = Math.max(0, Math.min(100, val || 0));
    const col = p >= 70 ? '#39FF14' : p >= 45 ? '#FFB800' : '#FF4A6E';
    const displayVal = lang !== 'en' ? localNum(String(Math.round(p)), lang) : Math.round(p);
    return (
        <div className="conf-ring">
            <svg width="50" height="50" viewBox="0 0 50 50">
                <circle cx="25" cy="25" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="2.5" />
                <circle cx="25" cy="25" r={r} fill="none" stroke={col} strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray={`${(p / 100) * circ} ${circ}`}
                    style={{ filter: `drop-shadow(0 0 4px ${col})` }} />
            </svg>
            <div className="conf-val" style={{ color: col }}>{displayVal}</div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
// MetricBox
// ════════════════════════════════════════════════════════════════
function MBox({ label, value, unit, color, bar }) {
    const d = value == null ? '—' : typeof value === 'number' ? value.toFixed(2) : String(value);
    const showUnit = d !== '—' && unit;
    return (
        <div className="mbox">
            <div className="mbox-label">{label}</div>
            <div className={`mbox-val ${d.length > 8 ? 'sm' : ''}`} style={{ color }}>
                {d}{showUnit ? unit : ''}
            </div>
            {bar !== undefined && (
                <div className="prog">
                    <div className="prog-fill" style={{ width: `${Math.max(0, Math.min(100, bar || 0))}%`, background: color }} />
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
// AlertRow — FIX: consistent, clean design
// ════════════════════════════════════════════════════════════════
// Alert type → translated label map
const ALERT_TYPE_LABELS = {
    health: { en: 'HEALTH', mr: 'आरोग्य', hi: 'स्वास्थ्य', te: 'ఆరోగ్యం' },
    flood: { en: 'FLOOD', mr: 'पूर', hi: 'बाढ़', te: 'వరద' },
    drought: { en: 'DROUGHT', mr: 'दुष्काळ', hi: 'सूखा', te: 'కరువు' },
    organic: { en: 'ORGANIC', mr: 'सेंद्रिय', hi: 'जैविक', te: 'సేంద్రీయ' },
    disease: { en: 'DISEASE', mr: 'रोग', hi: 'रोग', te: 'వ్యాధి' },
    advisory: { en: 'ADVISORY', mr: 'सल्ला', hi: 'सलाह', te: 'సలహా' },
};
// ── Number localisation ─────────────────────────────────────────────────────
const DEVA = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
const TELU = ['౦', '౧', '౨', '౩', '౪', '౫', '౬', '౭', '౮', '౯'];
function localNum(str, lang) {
    if (!str && str !== 0) return str;
    const s = String(str);
    if (lang === 'mr' || lang === 'hi') return s.replace(/\d/g, d => DEVA[d]);
    if (lang === 'te') return s.replace(/\d/g, d => TELU[d]);
    return s;
}
function localMsg(msg, lang) {
    if (lang === 'en' || !msg) return msg;
    return msg.replace(/[\d.]+/g, m => localNum(m, lang));
}

// ── Stress message translations ──────────────────────────────────────────────
const MSG_TRANS = {
    'Mild Water Stress':
        { mr: 'सौम्य जल ताण', hi: 'हल्का जल तनाव', te: 'తేలికపాటి నీటి ఒత్తిడి' },
    'Severe Water Stress':
        { mr: 'तीव्र जल ताण', hi: 'गंभीर जल तनाव', te: 'తీవ్రమైన నీటి ఒత్తిడి' },
    'Moderate Water Stress':
        { mr: 'मध्यम जल ताण', hi: 'मध्यम जल तनाव', te: 'మధ్యస్థ నీటి ఒత్తిడి' },
    'Mild Nitrogen Deficiency':
        { mr: 'सौम्य नाइट्रोजन कमतरता', hi: 'हल्की नाइट्रोजन कमी', te: 'తేలికపాటి నత్రజని లోపం' },
    'Moderate Nitrogen Deficiency — Apply Manure':
        { mr: 'मध्यम नाइट्रोजन कमतरता — खत द्या', hi: 'मध्यम नाइट्रोजन कमी — खाद दें', te: 'మధ్యస్థ నత్రజని లోపం — ఎరువు వేయండి' },
    'Severe Nitrogen Deficiency — Emergency Top Dressing':
        { mr: 'तीव्र नाइट्रोजन कमतरता — तातडीने खत द्या', hi: 'गंभीर नाइट्रोजन कमी — तुरंत खाद दें', te: 'తీవ్రమైన నత్రజని లోపం — అత్యవసరంగా ఎరువు' },
    'Excess Nitrogen — Reduce Manure':
        { mr: 'नत्र जास्त — खत कमी करा', hi: 'नाइट्रोजन अधिक — खाद कम करें', te: 'నత్రజని అధికం — ఎరువు తగ్గించండి' },
    'Heat Stress — Temperature Exceeds 38°C':
        { mr: 'उष्णता ताण — तापमान ३८°C पेक्षा जास्त', hi: 'गर्मी तनाव — तापमान 38°C से अधिक', te: 'వేడి ఒత్తిడి — 38°C కంటే ఎక్కువ' },
    // Python engine pillar-4 messages (exact match required)
    'Critical NDVI Drop — Possible Disease or Pest Attack':
        { mr: 'गंभीर NDVI घट — कीड/रोग शक्य', hi: 'गंभीर NDVI गिरावट — कीट/रोग संभव', te: 'తీవ్రమైన NDVI తగ్గుదల — తెగులు/వ్యాధి వీలు' },
    'Sudden NDVI Decline — Inspect Farm Immediately':
        { mr: 'अचानक NDVI घट — शेत ताबडतोब तपासा', hi: 'अचानक NDVI गिरावट — खेत तुरंत जांचें', te: 'అకస్మాత్తు NDVI తగ్గుదల — పొలం వెంటనే తనిఖీ' },
    'Moderate NDVI Decline — Monitor Closely':
        { mr: 'मध्यम NDVI घट — जवळून लक्ष ठेवा', hi: 'मध्यम NDVI गिरावट — ध्यान से देखें', te: 'మధ్యస్థ NDVI తగ్గుదల — నిశితంగా గమనించండి' },
    // Legacy labels (dashboard may also generate these)
    'Critical Crop Health Drop — Possible Disease or Pest Attack':
        { mr: 'पीक आरोग्य अचानक घट — कीड/रोग शक्य', hi: 'फसल स्वास्थ्य में तेज गिरावट — कीट/रोग संभव', te: 'పంట ఆరోగ్యం పడిపోవడం — తెగులు/రోగం వీలు' },
    'Sudden Crop Health Drop — Inspect Farm Immediately':
        { mr: 'पीक आरोग्य अचानक घट — शेत तपासा', hi: 'फसल स्वास्थ्य अचानक कम — खेत जांचें', te: 'పంట ఆరోగ్యం అకస్మాత్తుగా తగ్గింది — పొలం తనిఖీ' },
    'Moderate Crop Health Drop — Monitor Closely':
        { mr: 'पीक आरोग्य मध्यम घट — जवळून पहा', hi: 'फसल स्वास्थ्य में मध्यम गिरावट — ध्यान दें', te: 'పంట ఆరోగ్యం మధ్యస్థ తగ్గుదల — గమనించండి' },
    // Chlorophyll / nutrient messages from pillar-3
    'Low Chlorophyll — Check Nutrition':
        { mr: 'Chlorophyll कमी — पोषण तपासा', hi: 'क्लोरोफिल कम — पोषण जांचें', te: 'క్లోరోఫిల్ తక్కువ — పోషణ తనిఖీ' },
    'Possible Chlorophyll Deficiency':
        { mr: 'Chlorophyll कमतरता शक्य', hi: 'क्लोरोफिल की संभावित कमी', te: 'క్లోరోఫిల్ లోపం వీలు' },
    // Irrigation messages from pillar-2
    'Moderate Water Stress — Irrigate Today':
        { mr: 'मध्यम जल ताण — आज सिंचन करा', hi: 'मध्यम जल तनाव — आज सिंचाई करें', te: 'మధ్యస్థ నీటి ఒత్తిడి — ఈ రోజు నీరు పెట్టండి' },
    'Severe Water Stress — Irrigate Immediately':
        { mr: 'तीव्र जल ताण — ताबडतोब सिंचन करा', hi: 'गंभीर जल तनाव — तुरंत सिंचाई करें', te: 'తీవ్రమైన నీటి ఒత్తిడి — వెంటనే నీటిపారుదల' },
};
function translateMsg(msg, lang) {
    if (!msg || lang === 'en') return msg;
    const exact = MSG_TRANS[msg];
    if (exact?.[lang]) return localMsg(exact[lang], lang);
    for (const [en, tr] of Object.entries(MSG_TRANS)) {
        if (msg.startsWith(en) && tr[lang]) return localMsg(tr[lang] + msg.slice(en.length), lang);
    }
    return localMsg(msg, lang);
}
function buildAlertMsg(key, lang, vars = {}) {
    const { sarDb, vci, rootZ, moisture, orgScore, fc, dp, ndvi, evi, crop } = vars;
    // Translate crop name to local language using CROP_NAMES_ML lookup
    const cropLocal = lang === 'mr' ? (CROP_NAMES_ML[crop]?.mr || crop)
        : lang === 'hi' ? (CROP_NAMES_ML[crop]?.hi || crop)
            : lang === 'te' ? (CROP_NAMES_ML[crop]?.te || crop)
                : (crop || '');
    if (lang === 'mr') {
        if (key === 'flood') return `शेतात पूर आढळला — SAR VV: ${localNum(sarDb, 'mr')} dB`;
        if (key === 'drought') return `दुष्काळ ताण — VCI: ${localNum(vci, 'mr')}`;
        if (key === 'irr') return `सिंचन आवश्यक — मूळ क्षेत्र ${localNum(rootZ?.toFixed(1), 'mr')}% ओलावा`;
        if (key === 'irr_crit') return `सिंचन अत्यावश्यक: ${localNum(rootZ?.toFixed(1), 'mr')}% — २४-४८ तासांत करा`;
        if (key === 'organic') return `सेंद्रिय गुण ${localNum(orgScore?.toFixed(1), 'mr')} — ${localNum(fc, 'mr')} निकष अयशस्वी`;
        if (key === 'disease') return `रोग/कीड: ${dp?.disease_Pest_Risk || ''} — पीक आरोग्य बदल ${localNum(dp?.ndvi_Anomaly?.toFixed(3), 'mr')}`;
        if (key === 'sowing') return `पेरणीची वेळ: पीक आरोग्य ${localNum(ndvi?.toFixed(2), 'mr')} — शेत तयार. ${cropLocal} पेरा.`;
        if (key === 'spray') return `सेंद्रिय फवारणी: आर्द्रता ${localNum(moisture, 'mr')}% — नीम तेल वापरा.`;
        if (key === 'weed') return `तण काढा: पीक आरोग्य ${localNum(ndvi?.toFixed(2), 'mr')}, canopy ${localNum(evi?.toFixed(2), 'mr')}.`;
    } else if (lang === 'hi') {
        if (key === 'flood') return `खेत में बाढ़ मिली — SAR VV: ${localNum(sarDb, 'hi')} dB`;
        if (key === 'drought') return `सूखा तनाव — VCI: ${localNum(vci, 'hi')}`;
        if (key === 'irr') return `सिंचाई जरूरी — जड़ क्षेत्र ${localNum(rootZ?.toFixed(1), 'hi')}% नमी`;
        if (key === 'irr_crit') return `सिंचाई अत्यावश्यक: ${localNum(rootZ?.toFixed(1), 'hi')}% — 24-48 घंटे में करें`;
        if (key === 'organic') return `जैविक स्कोर ${localNum(orgScore?.toFixed(1), 'hi')} — ${localNum(fc, 'hi')} मानदंड विफल`;
        if (key === 'disease') return `रोग/कीट: ${dp?.disease_Pest_Risk || ''} — फसल स्वास्थ्य बदलाव ${localNum(dp?.ndvi_Anomaly?.toFixed(3), 'hi')}`;
        if (key === 'sowing') return `बुआई का समय: फसल स्वास्थ्य ${localNum(ndvi?.toFixed(2), 'hi')} — ${cropLocal} बोएं.`;
        if (key === 'spray') return `जैविक छिड़काव: नमी ${localNum(moisture, 'hi')}% — नीम तेल.`;
        if (key === 'weed') return `खरपतवार हटाएं: फसल स्वास्थ्य ${localNum(ndvi?.toFixed(2), 'hi')}, canopy ${localNum(evi?.toFixed(2), 'hi')}.`;
    } else if (lang === 'te') {
        if (key === 'flood') return `పొలంలో వరద — SAR VV: ${localNum(sarDb, 'te')} dB`;
        if (key === 'drought') return `కరువు ఒత్తిడి — VCI: ${localNum(vci, 'te')}`;
        if (key === 'irr') return `నీటిపారుదల అవసరం — వేర్లలో తేమ ${localNum(rootZ?.toFixed(1), 'te')}%`;
        if (key === 'irr_crit') return `నీటిపారుదల తక్షణమే: ${localNum(rootZ?.toFixed(1), 'te')}% — ${localNum('24', 'te')}-${localNum('48', 'te')} గంటల్లో`;
        if (key === 'organic') return `సేంద్రీయ స్కోర్ ${localNum(orgScore?.toFixed(1), 'te')} — ${localNum(fc, 'te')} ప్రమాణాలు విఫలం`;
        if (key === 'disease') return `వ్యాధి/తెగులు: ${dp?.disease_Pest_Risk || ''} — పంట ఆరోగ్య మార్పు ${localNum(dp?.ndvi_Anomaly?.toFixed(3), 'te')}`;
        if (key === 'sowing') return `విత్తన సమయం: పంట ఆరోగ్యం ${localNum(ndvi?.toFixed(2), 'te')} — ${cropLocal} విత్తండి.`;
        if (key === 'spray') return `సేంద్రీయ పిచికారీ: తేమ ${localNum(moisture, 'te')}%.`;
        if (key === 'weed') return `కలుపు తీయండి: పంట ఆరోగ్యం ${localNum(ndvi?.toFixed(2), 'te')}.`;
    }
    return null;
}

function AlertRow({ a, lang }) {
    const cfg = acfg(a.type);
    const typeLabels = ALERT_TYPE_LABELS[a.type] || { en: (a.type || '').toUpperCase() };
    const typeLabel = typeLabels[lang] || typeLabels.en;
    const displayMsg = a._transKey
        ? (buildAlertMsg(a._transKey, lang, a._vars) || a.message)
        : translateMsg(a.message, lang);
    return (
        <div className="alert-row">
            <div className="alert-stripe" style={{ background: cfg.c }} />
            <div className="alert-icon-cell">
                <div className="alert-icon-wrap" style={{ background: cfg.ib, color: cfg.c }}>
                    {cfg.icon}
                </div>
            </div>
            <div className="alert-content">
                <span className="alert-type-lbl" style={{ color: cfg.c }}>{typeLabel}</span>
                <span className="alert-text">{displayMsg}</span>
            </div>
            {a.severity === 'high' && (
                <div className="alert-sev">
                    <span className="sev-high" style={{ background: `${cfg.c}18`, color: cfg.c }}>
                        {lang === 'mr' ? 'उच्च' : lang === 'hi' ? 'उच्च' : lang === 'te' ? 'అధిక' : 'HI'}
                    </span>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
// SbHead — sidebar section divider
// ════════════════════════════════════════════════════════════════
function SbHead({ label, icon, voiceBtn }) {
    return (
        <div className="sb-head">
            <span className="sb-head-accent">{icon || '◈'}</span>
            <span className="sb-head-label">{label}</span>
            <span className="sb-head-line" />
            {voiceBtn && <span style={{ marginLeft: 6, flexShrink: 0 }}>{voiceBtn}</span>}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
// NoData
// ════════════════════════════════════════════════════════════════
function NoData({ text = 'Run scan to load' }) {
    return (
        <div className="no-data">
            <div className="no-data-line" />
            <span className="no-data-txt">{text}</span>
            <div className="no-data-line" />
        </div>
    );
}
// ── VoiceBtn: section-level 🔊 play/stop button ─────────────────────
function VoiceBtn({ playing, onPlay, lang }) {
    const stopLbl = lang === 'mr' ? 'थांबा' : lang === 'hi' ? 'रोकें' : lang === 'te' ? 'ఆపండి' : 'Stop';
    return (
        <button onClick={onPlay}
            title={playing ? stopLbl : 'Listen'}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '4px 9px', borderRadius: 5, cursor: 'pointer',
                fontSize: 19, fontWeight: 700, letterSpacing: 0.3,
                border: `1px solid ${playing ? '#39FF14' : 'rgba(57,255,20,0.22)'}`,
                background: playing ? 'rgba(57,255,20,0.14)' : 'rgba(57,255,20,0.05)',
                color: playing ? '#39FF14' : 'white', transition: 'all 0.2s',
                boxShadow: playing ? '0 0 8px rgba(57,255,20,0.25)' : 'none',
            }}>
            {playing
                ? <>
                    <Radio size={9} style={{ animation: 'vRadio 0.6s ease-in-out infinite alternate', color: '#39FF14' }} />
                    <span>{stopLbl}</span>
                </>
                : <Radio size={9} />}
        </button>
    );
}

// ════════════════════════════════════════════════════════════════
// ORGANIC GOALS COMPONENT
// ════════════════════════════════════════════════════════════════
function OrganicGoals({ bd, score, grade, topIssues, failCount, t, lang }) {
    const scoreColor = score >= 80 ? '#39FF14' : score >= 60 ? '#FFB800' : '#FF4A6E';

    // Multilingual action text helpers
    const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;

    const goals = [
        {
            key: 'A', label: t.vegHealth, icon: '🌿', desc: t.vegDesc,
            action: s => s >= 80
                ? L('छत्र आवरण उत्तम — सध्याचे व्यवस्थापन ठेवा', 'छत्र आच्छादन उत्तम — प्रबंधन जारी रखें', 'పైకప్పు కవరేజ్ అద్భుతం — నిర్వహణ కొనసాగించండి', 'Maintain current crop management — excellent canopy coverage')
                : s >= 60
                    ? L('पर्णीय फवारणी करा; खत व सूक्ष्म अन्नद्रव्ये तपासा', 'फोलियर स्प्रे करें; खाद और सूक्ष्म पोषक तत्व जांचें', 'ఆకు పిచికారీ చేయండి; ఎరువు మరియు సూక్ష్మపోషకాలు తనిఖీ', 'Apply foliar spray; check manure and micronutrient levels')
                    : L('कंपोस्ट द्या; रोप अंतर व निविष्ठा वेळापत्रक सुधारा', 'कम्पोस्ट दें; पौध अंतर और इनपुट शेड्यूल सुधारें', 'కంపోస్ట్ వేయండి; మొక్కల అంతరం మరియు ఇన్‌పుట్ వేళాపట్టి మెరుగుపరచండి', 'Apply compost; improve plant spacing and input schedule')
        },
        {
            key: 'B', label: t.waterMgmt, icon: '🚿', desc: t.waterDesc,
            action: s => s >= 80
                ? L('जल व्यवस्थापन उत्तम — हस्तक्षेप आवश्यक नाही', 'जल प्रबंधन उत्तम — हस्तक्षेप जरूरी नहीं', 'నీటి నిర్వహణ అద్భుతం — జోక్యం అవసరం లేదు', 'Water management optimal — no intervention needed')
                : s >= 60
                    ? L('ठिबक सिंचन वेळापत्रक करा; बांध व जलवाहिन्या तपासा', 'ड्रिप सिंचाई शेड्यूल करें; बांध और नहरें जांचें', 'డ్రిప్ నీటిపారుదల షెడ్యూల్ చేయండి; గట్లు తనిఖీ', 'Schedule drip irrigation; inspect bunding and water channels')
                    : L('तातडीने: मल्चिंग व ठिबक सिंचन बसवा', 'अत्यावश्यक: मल्चिंग और ड्रिप सिंचाई तुरंत लगाएं', 'అత్యవసరం: మల్చింగ్ మరియు డ్రిప్ వ్యవస్థ ఏర్పాటు', 'Critical: install mulching and drip system urgently')
        },
        {
            key: 'C', label: t.soilHealth, icon: '🐍', desc: t.soilDesc,
            action: s => s >= 80
                ? L('माती आरोग्य उत्तम — सेंद्रिय निविष्ठा ठेवा', 'मिट्टी स्वास्थ्य उत्तम — जैविक इनपुट बनाए रखें', 'నేల ఆరోగ్యం అద్భుతం — సేంద్రీయ ఇన్‌పుట్లు కొనసాగించండి', 'Soil health excellent — maintain organic inputs')
                : s >= 60
                    ? L('गांडूळखत घाला; pH तपासा व चुना वापरा', 'वर्मीकम्पोस्ट डालें; pH जांचें और चूना लगाएं', 'వర్మీకంపోస్ట్ వేయండి; pH తనిఖీ చేసి సున్నం వేయండి', 'Add vermicompost; check pH and apply lime if needed')
                    : L('तातडीने: माती सुधारणा आवश्यक — pH व कार्बन अत्यंत कमी', 'अत्यावश्यक: मिट्टी सुधार जरूरी — pH और कार्बन बहुत कम', 'అత్యవసరం: నేల సంస్కరణ అవసరం — pH మరియు కార్బన్ చాలా తక్కువ', 'Urgent: soil amendment required — pH and carbon critically low')
        },
        {
            key: 'D', label: t.climateRes, icon: '🌡️', desc: t.climateDesc,
            action: s => s >= 80
                ? L('हवामान सेंद्रिय शेतीस अनुकूल', 'जलवायु जैविक खेती के लिए अनुकूल', 'వాతావరణం సేంద్రీయ వ్యవసాయానికి అనుకూలం', 'Climate conditions favorable for organic farming')
                : s >= 60
                    ? L('उष्णता ताण लक्षात ठेवा; सावली जाळी वापरा', 'गर्मी तनाव देखें; शेड नेट या विंडब्रेक लगाएं', 'వేడి ఒత్తిడి గమనించండి; నీడ వల లేదా గాలి అడ్డుగోడ పెట్టండి', 'Monitor heat stress; consider shade netting or windbreaks')
                    : L('उच्च हवामान धोका — जैव-उत्तेजक वापरा', 'उच्च जलवायु जोखिम — बायो-स्टिमुलेंट लगाएं', 'అధిక వాతావరణ ప్రమాదం — బయో-స్టిమ్యులెంట్లు వేయండి', 'High climate risk — apply bio-stimulants for stress tolerance')
        },
        {
            key: 'E', label: t.terrainSuit, icon: '🗻', desc: t.terrainDesc,
            action: s => s >= 80
                ? L('भूप्रदेश सेंद्रिय शेतीस उपयुक्त', 'भूभाग जैविक खेती के लिए उपयुक्त', 'భూభాగం సేంద్రీయ వ్యవసాయానికి అనుకూలం', 'Terrain well-suited for organic farming')
                : s >= 60
                    ? L('निचरा वाहिन्या तपासा — पाणी साठणे टाळा', 'जल निकासी नहरें जांचें — जलभराव रोकें', 'నీటి పారుదల కాలువలు తనిఖీ చేయండి', 'Check drainage channels to prevent waterlogging')
                    : L('उतार किंवा निचरा धोका — कंटूर बंडिंग करा', 'ढलान या जल निकासी जोखिम — कंटूर बंडिंग करें', 'వాలు లేదా నీటి పారుదల ప్రమాదం — కంటూర్ బండింగ్ చేయండి', 'Slope or drainage risk — contour bunding recommended')
        },
        {
            key: 'F', label: t.trendStab, icon: '📊', desc: t.trendDesc,
            action: s => s >= 80
                ? L('स्थिर वाढ ट्रेंड — सातत्यपूर्ण हंगामी कामगिरी', 'स्थिर वृद्धि ट्रेंड — सुसंगत मौसमी प्रदर्शन', 'స్థిరమైన వృద్ధి ట్రెండ్ — నిరంతర కాలానుగుణ పనితీరు', 'Stable growth trend — consistent seasonal performance')
                : s >= 60
                    ? L('मध्यम चढउतार — सिंचन व निविष्ठा वेळापत्रक तपासा', 'मध्यम उतार-चढ़ाव — सिंचाई और इनपुट कैलेंडर जांचें', 'మధ్యస్థ హెచ్చుతగ్గులు — నీటిపారుదల వేళాపట్టి తనిఖీ', 'Moderate fluctuation — review irrigation and input calendar')
                    : L('अस्थिर ट्रेंड — कीड चक्र किंवा अनियमित निविष्ठा तपासा', 'अस्थिर ट्रेंड — कीट चक्र या अनियमित इनपुट जांचें', 'అస్థిర ట్రెండ్ — తెగులు చక్రం లేదా అనిర్దిష్ట ఇన్‌పుట్లు తనిఖీ', 'Unstable trend — check for pest cycle or irregular inputs')
        },
    ];

    const cleanIssues = (topIssues || [])
        .filter(i => typeof i === 'string' && i.length < 80
            && !i.includes('=') && !i.includes('>') && !i.includes('flood_detected'));

    return (
        <div style={{ margin: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* ── Score Header ── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '12px 14px',
                background: 'var(--surface2)',
                border: '1px solid var(--b1)',
                borderLeft: `3px solid ${scoreColor}`,
                borderRadius: '0 8px 8px 0',
            }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexShrink: 0 }}>
                    <span style={{ fontFamily: "var(--ff-d)", fontSize: '40px', letterSpacing: '-1px', color: scoreColor, lineHeight: 1 }}>
                        {score != null ? localNum(score.toFixed(1), lang) : '—'}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontFamily: "var(--ff-m)", fontSize: '13px', fontWeight: 700, color: scoreColor, letterSpacing: '0.8px' }}>
                            {t.grade} {grade || '—'}
                        </span>
                        <span style={{ fontFamily: "var(--ff-m)", fontSize: '15px', color: 'var(--tx2)' }}></span>
                    </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                    {failCount === 0
                        ? <span style={{ fontFamily: "var(--ff-m)", fontSize: '10px', color: '#39FF14', letterSpacing: '0.5px' }}>{t.allPassed}</span>
                        : <span style={{ fontFamily: "var(--ff-m)", fontSize: '10px', color: '#FF4A6E', letterSpacing: '0.5px' }}>✗ {localNum(String(failCount), lang)} {t.criteriaFailed}</span>
                    }
                    {cleanIssues.slice(0, 1).map((issue, i) => (
                        <span key={i} style={{ fontFamily: "var(--ff-u)", fontSize: '10px', color: 'var(--tx2)', lineHeight: 1.4 }}>{issue}</span>
                    ))}
                </div>
            </div>

            {/* ── 6 Goal Cards ── */}
            {goals.map(({ key, label, icon, desc, action }) => {
                const raw = bd[key];
                const s = raw == null ? null : typeof raw === 'object' ? (raw.score ?? null) : raw;
                const sc = s == null ? '#7090a8' : s >= 80 ? '#39FF14' : s >= 60 ? '#FFB800' : '#FF4A6E';
                const lbl = s == null ? t.noData : s >= 80 ? t.pass : s >= 60 ? t.fair : t.fail;
                return (
                    <div key={key} style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--b1)',
                        borderLeft: `3px solid ${sc}`,
                        borderRadius: '0 7px 7px 0',
                        padding: '9px 12px 9px 12px',
                        transition: 'background 0.15s',
                    }}>
                        {/* Top row */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>{icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: "var(--ff-u)", fontSize: '11px', fontWeight: 700, color: 'var(--tx1)', marginBottom: '2px' }}>
                                    {label}
                                </div>
                                <div style={{
                                    fontFamily: "var(--ff-m)", fontSize: '9px', color: '#f3f4f5', letterSpacing: '0.5px'
                                }}>
                                    {desc}
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                                <span style={{ fontFamily: "var(--ff-d)", fontSize: '22px', color: sc, lineHeight: 1, letterSpacing: '1px' }}>
                                    {s != null ? localNum(s.toFixed(0), lang) : '—'}
                                </span>
                                <span style={{
                                    fontFamily: "var(--ff-m)", fontSize: '11px', fontWeight: 700,
                                    letterSpacing: '1.2px', padding: '2px 7px', borderRadius: '20px',
                                    color: sc, background: `${sc}18`, border: `1px solid ${sc}40`
                                }}>{lbl}</span>
                            </div>
                        </div>

                        {/* Progress bar */}
                        {s != null && (
                            <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden', marginBottom: '7px' }}>
                                <div style={{
                                    height: '100%', width: `${s}%`, borderRadius: '2px',
                                    background: `linear-gradient(90deg, ${sc}88, ${sc})`,
                                    transition: 'width 1.4s cubic-bezier(0.16,1,0.3,1)'
                                }} />
                            </div>
                        )}

                        {/* Action */}
                        <div style={{
                            fontFamily: "var(--ff-m)", fontSize: '10.5px', color: '#f3f4f5',
                            lineHeight: 1.5, padding: '5px 8px',
                            background: 'rgba(255,255,255,0.025)',
                            borderRadius: '4px', borderLeft: '2px solid rgba(255,255,255,0.07)'
                        }}>
                            {s != null ? action(s) : t.runScanGoal}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
// LazyChart — renders children only when scrolled into view
// ════════════════════════════════════════════════════════════════
function LazyChart({ children, height = 120 }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { rootMargin: '100px' }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return (
        <div ref={ref} style={{ minHeight: visible ? undefined : height }}>
            {visible ? children : null}
        </div>
    );
}

// [PERF] In-memory scan cache: re-scanning the same farm/dates within 10 min
// returns instantly instead of waiting for the server again.
const _scanCache = new Map();
const SCAN_CACHE_TTL_MS = 10 * 60 * 1000;

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export function AgriDashboard() {
    const srPlantID = sessionStorage.getItem("user_plantid");
    const srUserID = sessionStorage.getItem("user_id");
    const srRoleID = sessionStorage.getItem("role_id");
    const navigate = useNavigate();

    // ── Read farm data stored by SearchPlant.scanFarm() ─────────
    const _ssLat = sessionStorage.getItem('scanfarmlat');
    const _ssLon = sessionStorage.getItem('scanfarmlon');
    const _ssArea = sessionStorage.getItem('scanfarmarea');
    const _ssBndry = sessionStorage.getItem('scanfarmboundry');
    const _ssCrop = sessionStorage.getItem('scanfarmcrop');
    const [lat, setLat] = useState(_ssLat ? String(parseFloat(_ssLat).toFixed(6)) : '20.593700');
    const [lon, setLon] = useState(_ssLon ? String(parseFloat(_ssLon).toFixed(6)) : '78.962900');
    const [startDate, setStartDate] = useState(new Date(Date.now() - 90 * 864e5).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [boundary, setBoundary] = useState(null);
    const [error, setError] = useState(null);
    const [acres, setAcres] = useState(_ssArea ? parseFloat(_ssArea) : 1.0);
    const [unit, setUnit] = useState('acres');
    const [lang, setLang] = useState('mr');          // i18n language state — default Marathi
    const t = T[lang] || T.en;                        // active translation dict
    const ssCrop = () => resolveCropName(_ssCrop, data?.crop_Identification, 'en');
    const [cropsName, setCropName] = useState(ssCrop);
    const userEditedDatesRef = useRef(false);  // becomes true once farmer manually touches Start/End Date
    // [FIX — Sugarcane/long-duration-crop trend window] Previously Start
    // Date always defaulted to "90 days ago" for every crop, so a
    // Sugarcane farmer (10-18 month growing cycle) saw only a small recent
    // slice of the crop's life labeled "3-MONTH" regardless. Now the
    // default Start Date auto-extends based on the selected crop's real
    // growing-cycle length (from CROP_CALENDAR), unless the farmer has
    // already manually edited the date fields — their choice is respected.
    useEffect(() => {
        if (userEditedDatesRef.current) return;
        if (!cropsName) return;
        const windowDays = getCropHistoryWindowDays(cropsName);
        setStartDate(new Date(Date.now() - windowDays * 864e5).toISOString().split('T')[0]);
    }, [cropsName]);
    const polyRef = useRef(null);
    const [drawnAreaSqm, setDrawnAreaSqm] = useState(null);  // area of farmer-drawn polygon
    const [isUserDrawn, setIsUserDrawn] = useState(false);  // true = farmer drew it, false = auto
    const [drawError, setDrawError] = useState('');     // polygon-too-large error message
    const drawErrorTimer = useRef(null);                     // auto-clear timer
    const [scanHistory, setScanHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [legendVisible, setLegendVisible] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);



    // ── Voice / TTS state ─────────────────────────────────────────
    const [voicePlaying, setVoicePlaying] = useState(false);
    const [voiceSection, setVoiceSection] = useState(null);
    const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);
    const voicesRef = useRef([]);   // cached voice list — populated by onvoiceschanged
    const VOICE_LANG = { en: 'en-IN', mr: 'mr-IN', hi: 'hi-IN', te: 'te-IN' };

    // ═══════════════════════════════════════════════════════════════
    //  VOICE ENGINE  — farmer-friendly, section-specific, 4 languages
    //  BUG FIX 1: section buttons now speak ONLY their section data
    //  BUG FIX 2: pickVoice() forces correct Indic voice on browser
    // ═══════════════════════════════════════════════════════════════

    // ── Voice state ──────────────────────────────────────────────────
    const [voiceReady, setVoiceReady] = useState(false);
    const [noIndic, setNoIndic] = useState(false);

    // Browser detection — needed because Chrome/Safari need different handling
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isChrome = /Chrome/.test(ua) && !/Edg/.test(ua) && !/OPR/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    const isEdge = /Edg/.test(ua);
    const isFirefox = /Firefox/.test(ua);

    // ── Load voices — handles all browsers ───────────────────────────
    //
    // Browser behaviour:
    //   Edge    — voices populated synchronously on first getVoices() call
    //   Firefox — voices populated synchronously or within 1 onvoiceschanged
    //   Chrome  — returns [] immediately, fires onvoiceschanged 1–3× async
    //   Safari  — like Chrome but sometimes fires only once
    //   Android Chrome — voices may need up to 2s to fully load
    //
    const plantRobotOperation = () => {
        /*if (srRoleID != null && (srRoleID !== "6")) {*/
        navigate(`/robot-operation?pid=${srPlantID}`);
        // }
        // window.location.href = "/#/RobotsOperation?pid=" + srPlantID + "";
    }
    //
    useEffect(() => {
        const synth = synthRef.current;
        if (!synth) return;
        let t1, t2, t3;

        const load = () => {
            const vv = synth.getVoices();
            if (vv.length > 0) {
                voicesRef.current = vv;
                setVoiceReady(true);
            }
        };

        load();                          // Edge / Firefox: works immediately
        synth.onvoiceschanged = load;    // Chrome / Safari: async callback
        t1 = setTimeout(load, 100);     // Chrome: 100ms nudge
        t2 = setTimeout(load, 500);     // Safari / slow Android: 500ms
        t3 = setTimeout(load, 2000);    // Android Chrome: up to 2s

        return () => {
            synth.onvoiceschanged = null;
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
        };
    }, []);

    // ── hasVoiceForLang: check if browser has a matching voice ────────
    const hasVoiceForLang = (code) => {
        const pre = code.split('-')[0];
        return voicesRef.current.some(v =>
            v.lang === code ||
            v.lang.toLowerCase().startsWith(pre + '-') ||
            v.lang.toLowerCase().startsWith(pre)
        );
    };

    // ── pickVoice ────────────────────────────────────────────────────
    //
    // Cross-browser voice selection strategy:
    //
    //   Edge:    Microsoft Heera (mr-IN), Kalpana (te-IN), Hemant (hi-IN) — offline
    //   Firefox: OS-level voices via Web Speech API (Windows has Indic if installed)
    //   Chrome:  Google हिन्दी (hi-IN) built-in; mr/te need online TTS (no offline)
    //   Safari:  macOS/iOS voices — Lekha (hi-IN) on Indian iOS; no mr/te offline
    //   Android: Google TTS engine — supports mr/te if language pack installed
    //
    // CRITICAL RULE: Never assign an English voice to a non-English utterance.
    //   If we set utt.voice = (English voice), Chrome ignores utt.lang and
    //   speaks in English regardless. Returning null lets the browser use its
    //   own TTS routing based on utt.lang alone.
    //
    const pickVoice = (code) => {
        if (synthRef.current) {
            const fresh = synthRef.current.getVoices();
            if (fresh.length > 0) voicesRef.current = fresh;
        }
        const vv = voicesRef.current;
        if (!vv.length) return null;
        const pre = code.split('-')[0];  // 'mr' | 'hi' | 'te' | 'en'

        // 1. Exact BCP-47 — best quality (Edge offline, Chrome hi-IN)
        const exact = vv.find(v => v.lang === code);
        if (exact) return exact;

        // 2. Same language, any region  ('mr-IN' → 'mr-IN' if stored differently)
        const regional = vv.find(v => v.lang.toLowerCase().startsWith(pre + '-'));
        if (regional) return regional;

        // 3. Loose language prefix match
        const loose = vv.find(v => v.lang.toLowerCase().startsWith(pre));
        if (loose) return loose;

        // 4. English only: fall back gracefully
        if (pre === 'en') {
            return vv.find(v => v.lang === 'en-IN')
                || vv.find(v => v.lang.toLowerCase().startsWith('en'))
                || null;
        }

        // 5. Non-English, no matching voice found.
        //    Return null — doSpeak will set only utt.lang, allowing the
        //    browser to route to its online TTS (Google Cloud / Apple TTS)
        return null;
    };

    // ── Helper: season name per language ──────────────────────────
    const seasonName = (ssn, L) => ({
        mr: { Kharif: 'खरीप', Rabi: 'रब्बी', Zaid: 'उन्हाळी' }[ssn] || ssn,
        hi: { Kharif: 'खरीफ', Rabi: 'रबी', Zaid: 'जायद' }[ssn] || ssn,
        te: { Kharif: 'ఖరీఫ్', Rabi: 'రబీ', Zaid: 'జాయద్' }[ssn] || ssn,
    }[L] || ssn);

    // ── Helper: health status in local language ────────────────────
    const healthName = (h, L) => ({
        mr: { Excellent: 'उत्कृष्ट', Good: 'चांगले', Fair: 'ठीक आहे', Stressed: 'ताण आहे', Critical: 'गंभीर स्थिती' }[h] || h,
        hi: { Excellent: 'बहुत अच्छा', Good: 'अच्छा', Fair: 'ठीक', Stressed: 'तनाव है', Critical: 'गंभीर स्थिति' }[h] || h,
        te: { Excellent: 'అద్భుతంగా', Good: 'బాగుంది', Fair: 'సరే', Stressed: 'ఒత్తిడిలో', Critical: 'తీవ్రమైన స్థితి' }[h] || h,
    }[L] || h);

    // ── Helper: stress type in local language ──────────────────────
    const stressName = (s, L) => {
        const MAP = {
            mr: {
                'Mild Water Stress': 'थोडा पाण्याचा ताण',
                'Moderate Water Stress': 'मध्यम पाण्याचा ताण',
                'Severe Water Stress': 'तीव्र पाण्याचा ताण',
                'Mild Nitrogen Deficiency': 'थोडी नत्राची कमतरता',
                'Moderate Nitrogen Deficiency — Apply Manure': 'मध्यम नत्राची कमतरता — खत द्या',
                'Severe Nitrogen Deficiency — Emergency Top Dressing': 'तीव्र नत्राची कमतरता',
                'Excess Nitrogen — Reduce Manure': 'नत्र जास्त झाले आहे',
                'Heat Stress — Temperature Exceeds 38°C': 'उष्णतेचा ताण',
                'Critical Crop Health Drop — Possible Disease or Pest Attack': 'पीक आरोग्य गंभीर घट — कीड/रोग शक्य',
                'Sudden Crop Health Drop — Inspect Farm Immediately': 'पीक आरोग्य अचानक कमी — शेत ताबडतोब तपासा',
            },
            hi: {
                'Mild Water Stress': 'हल्का पानी का तनाव',
                'Moderate Water Stress': 'मध्यम पानी का तनाव',
                'Severe Water Stress': 'गंभीर पानी की कमी',
                'Mild Nitrogen Deficiency': 'हल्की नाइट्रोजन की कमी',
                'Moderate Nitrogen Deficiency — Apply Manure': 'मध्यम नाइट्रोजन कमी — खाद दें',
                'Severe Nitrogen Deficiency — Emergency Top Dressing': 'गंभीर नाइट्रोजन की कमी',
                'Excess Nitrogen — Reduce Manure': 'नाइट्रोजन बहुत ज्यादा है',
                'Heat Stress — Temperature Exceeds 38°C': 'गर्मी का तनाव',
                'Critical Crop Health Drop — Possible Disease or Pest Attack': 'फसल स्वास्थ्य गंभीर गिरावट — कीट/बीमारी संभव',
                'Sudden Crop Health Drop — Inspect Farm Immediately': 'फसल स्वास्थ्य अचानक कम — खेत तुरंत जांचें',
            },
            te: {
                'Mild Water Stress': 'తేలికపాటి నీటి ఒత్తిడి',
                'Severe Water Stress': 'తీవ్రమైన నీటి కొరత',
                'Mild Nitrogen Deficiency': 'తేలికపాటి నత్రజని లోపం',
                'Heat Stress — Temperature Exceeds 38°C': 'వేడి ఒత్తిడి',
                'Critical Crop Health Drop — Possible Disease or Pest Attack': 'పంట ఆరోగ్యం తీవ్ర తగ్గుదల — తెగులు/వ్యాధి అవకాశం',
            },
        };
        return (MAP[L] && MAP[L][s]) || s;
    };

    // ═══════════════════════════════════════════════════════════════
    //  SECTION VOICE BUILDERS — each section has rich farmer text
    // ═══════════════════════════════════════════════════════════════
    // standalone=true → called from section button (speak greeting + section data)
    // standalone=false → called from buildFullReport (no greeting, just section label + data)
    const buildSectionText = (sec, L, standalone = true) => {
        if (!data) return '';
        const st = data.stats || {};
        const cr = data.crop_Identification || {};
        const hlth = data.health || {};
        const yld = data.yield_Estimation || {};
        const org = data.organic_Alert || {};
        const dp = data.disease_Pest_Alert || {};
        const fl = data.flood || {};
        const dr = data.drought || {};
        const ssn = data.meta?.season || '';

        // shared values
        const _safeGetCropName = (crop, lang) => {
            try { return typeof getCropName === 'function' ? getCropName(crop, lang) : null; }
            catch (_) { return null; }
        };
        const cropN = L === 'mr' ? (cr.crop_Marathi || _safeGetCropName(cr.crop_Primary, 'mr') || cr.crop_Primary || 'Unknown')
            : L === 'hi' ? (_safeGetCropName(cr.crop_Primary, 'hi') || cr.crop_Primary || 'Unknown')
                : L === 'te' ? (_safeGetCropName(cr.crop_Primary, 'te') || cr.crop_Primary || 'Unknown')
                    : (cr.crop_Primary || data.Detected_Crop || 'Unknown');
        const hScore = +(data.health_Score || hlth.health_Score || 0).toFixed(0);
        const hStat = healthName(hlth.health_Status || '', L);
        const ndvi = st.health_Metric;
        const evi = st.evi;
        const ndre = st.ndre;
        const nitIdx = st.nitrogen_Index;
        const surf = st.soil_Moisture_Percent ?? st.surface_Moisture_Pct_SAR;
        const rootZ = st.root_Zone_Moisture;
        const irr = hlth.irrigation_Needed;
        const fert = hlth.fertilizer_Needed ?? hlth.manure_Needed;  // engine field
        const ph = st.soil_Ph;
        const elev = st.elevation_M;
        const slope = st.slope_Deg;
        const temp = st.temperature_C;
        const hum = st.humidity;
        const rain = st.chirps_Rainfall_mm;
        const yKgHa = yld.yield_Estimate_kg_per_ha;
        const yTot = yld.total_Estimated_Yield_kg;
        const yConf = yld.confidence_Level || '';
        const yMeth = (yld.methods_Used || []).length;
        const os = org.organic_Score;
        const og = (org.organic_Grade || '').split('—')[0].trim();
        const oPass = org.pass_Count || 0;
        const oFail = org.fail_Count || 0;
        const oElig = org.organic_Eligible;
        const stypes = (hlth.stress_Types || []).map(s => stressName(s, L));
        const pillar = data.pillar_Scores || {};
        const crStg = hlth.health_Status || '';  // proxy for growth stage context
        const p7rain = st.forecast_7day_rain_mm || data.forecast_7day_rain_mm || [];
        const p7tmax = st.forecast_7day_temp_max || data.forecast_7day_temp_max || [];
        const totRain7 = p7rain.reduce((a, v) => a + (+v || 0), 0);
        const maxTemp7 = p7tmax.length ? Math.max(...p7tmax.filter(Boolean)) : null;
        const algAlerts = data.alerts || [];
        const highAlerts = algAlerts.filter(a => a.severity === 'high');
        const areaAc = data.meta?.area_Acres || 0;

        // ── NDVI → photosynthesis description ────────────────────
        const ndviPhoto = (v, L) => {
            if (v == null) return '';
            if (L === 'mr') return v >= 0.7 ? 'प्रकाशसंश्लेषण खूप चांगले आहे.' : v >= 0.5 ? 'प्रकाशसंश्लेषण ठीक आहे.' : v >= 0.3 ? 'प्रकाशसंश्लेषण कमी आहे.' : 'प्रकाशसंश्लेषण खूप कमी आहे.';
            if (L === 'hi') return v >= 0.7 ? 'प्रकाश संश्लेषण बहुत अच्छा है.' : v >= 0.5 ? 'प्रकाश संश्लेषण ठीक है.' : v >= 0.3 ? 'प्रकाश संश्लेषण कम है.' : 'प्रकाश संश्लेषण बहुत कम है.';
            if (L === 'te') return v >= 0.7 ? 'కిరణజన్య సంయోగక్రియ చాలా బాగుంది.' : v >= 0.5 ? 'కిరణజన్య సంయోగక్రియ సరిగా ఉంది.' : 'కిరణజన్య సంయోగక్రియ తక్కువగా ఉంది.';
            return v >= 0.7 ? 'Photosynthesis is very good.' : v >= 0.5 ? 'Photosynthesis is normal.' : v >= 0.3 ? 'Photosynthesis is low. Crop may need care.' : 'Photosynthesis is very low. Crop needs urgent attention.';
        };

        // ── CROP HEALTH section ───────────────────────────────────
        if (sec === 'crop' || sec === 'health') {
            // 3-month NDVI trend proxy from pillar scores
            // const hPillar removed — unused
            // ── Crop-specific alert thresholds from CropData.js ────────
            const _calEntry = CROP_CALENDAR[cr.crop_Primary] || CROP_CALENDAR['Unknown'];
            const _thresholds = _calEntry?.alertThresholds || { ndvi_stress: 0.20, moisture_low: 25, moisture_high: 75 };
            const _ndviStress = _thresholds.ndvi_stress ?? 0.20;
            const _moistLow = _thresholds.moisture_low ?? 25;
            const _moistHigh = _thresholds.moisture_high ?? 75;
            // Override stress detection with crop-specific thresholds
            // _isMoistStress/_isNdviStress removed — thresholds used via _calEntry.alertThresholds

            const nitTxt = nitIdx != null ? (
                L === 'mr' ? (nitIdx < 0.35 ? 'नाइट्रोजनची कमतरता आहे. खत द्या.' : nitIdx > 0.65 ? 'नाइट्रोजन जास्त झाले आहे.' : 'नत्र प्रमाण ठीक आहे.')
                    : L === 'hi' ? (nitIdx < 0.35 ? 'नाइट्रोजन कम है. खाद दें.' : nitIdx > 0.65 ? 'नाइट्रोजन ज्यादा है.' : 'नाइट्रोजन ठीक है.')
                        : L === 'te' ? (nitIdx < 0.35 ? 'నత్రజని తక్కువ. ఎరువు వేయండి.' : nitIdx > 0.65 ? 'నత్రజని ఎక్కువ.' : 'నత్రజని సరిగా ఉంది.')
                            : (nitIdx < 0.35 ? 'Nitrogen is low. Apply organic manure.' : nitIdx > 0.65 ? 'Nitrogen is excess.' : 'Nitrogen level is good.')
            ) : '';
            const heatTxt = (temp != null && temp > 38)
                ? (L === 'mr' ? 'तापमान ३८ अंशापेक्षा जास्त आहे. उष्णतेचा ताण आहे.' : L === 'hi' ? 'तापमान 38 डिग्री से ज्यादा है. गर्मी का तनाव है.' : L === 'te' ? 'ఉష్ణోగ్రత 38 కంటే ఎక్కువ. వేడి ఒత్తిడి ఉంది.' : 'Temperature above 38°C. Heat stress detected.')
                : '';
            const irrTxt = irr
                ? (L === 'mr' ? 'पाण्याची कमतरता आहे. आज सिंचन करा.' : L === 'hi' ? 'पानी की कमी है. आज सिंचाई करें.' : L === 'te' ? 'నీటి కొరత ఉంది. ఈ రోజు నీరు పెట్టండి.' : 'Water stress detected. Irrigate today.')
                : (L === 'mr' ? 'पाण्याचे प्रमाण ठीक आहे.' : L === 'hi' ? 'पानी की मात्रा ठीक है.' : L === 'te' ? 'నీటి మోతాదు సరిగా ఉంది.' : 'Water level is adequate.');

            if (L === 'mr') return [
                standalone ? `नमस्कार शेतकरी! हे भूहित उपग्रह शेत आरोग्य अहवाल.` : `पीक आरोग्य:`,
                `तुमचे ${seasonName(ssn, L)} हंगाम आहे. पीक आरोग्य स्थिती ${hStat}.`,
                `आरोग्य गुण १०० पैकी ${hScore}. ${hScore >= 80 ? 'शेत खूप चांगले आहे.' : hScore >= 60 ? 'शेत ठीक आहे.' : 'शेताला काळजीची गरज आहे.'}`,
                ndvi != null ? `मागील तीन महिन्यांत उपग्रहाने पीक आरोग्य गुण ${ndvi.toFixed(2)} मोजला. ${ndvi >= 0.6 ? 'हिरवळ चांगली आहे.' : ndvi >= 0.4 ? 'हिरवळ मध्यम आहे.' : 'हिरवळ कमी आहे.'}` : '',
                ndviPhoto(ndvi, L),
                stypes.length ? `पिकावर ताण: ${stypes.join(', ')}.` : '',
                nitTxt,
                heatTxt,
                irrTxt,
                fert ? (L === 'mr' ? 'खत देणे आवश्यक आहे.' : '') : '',
            ].filter(Boolean).join(' ');

            if (L === 'hi') return [
                standalone ? `नमस्ते किसान! यह भूहित उपग्रह खेत स्वास्थ्य रिपोर्ट है.` : `फसल स्वास्थ्य:`,
                `आपकी ${seasonName(ssn, L)} मौसम है. फसल स्वास्थ्य स्थिति ${hStat}.`,
                `स्वास्थ्य स्कोर 100 में से ${hScore}. ${hScore >= 80 ? 'खेत बहुत अच्छा है.' : hScore >= 60 ? 'खेत ठीक है.' : 'खेत पर ध्यान देने की जरूरत है.'}`,
                ndvi != null ? `पिछले तीन महीनों में फसल स्वास्थ्य स्कोर ${ndvi.toFixed(2)} दर्ज हुई. ${ndvi >= 0.6 ? 'हरियाली अच्छी है.' : ndvi >= 0.4 ? 'हरियाली ठीक है.' : 'हरियाली कम है.'}` : '',
                ndviPhoto(ndvi, L),
                stypes.length ? `फसल पर तनाव: ${stypes.join(', ')}.` : '',
                nitTxt,
                heatTxt,
                irrTxt,
                fert ? 'खाद देना जरूरी है.' : '',
            ].filter(Boolean).join(' ');

            if (L === 'te') return [
                standalone ? `నమస్కారం రైతు! ఇది భూహిత్ ఉపగ్రహ పొలం ఆరోగ్య నివేదిక.` : `పంట ఆరోగ్యం:`,
                `మీ ${seasonName(ssn, L)} సీజన్. ఆరోగ్య స్థితి ${hStat}.`,
                `ఆరోగ్య స్కోర్ 100 లో ${hScore}. ${hScore >= 80 ? 'పొలం చాలా బాగుంది.' : hScore >= 60 ? 'పొలం సరిగా ఉంది.' : 'పొలానికి శ్రద్ధ అవసరం.'}`,
                ndvi != null ? `పంట ఆరోగ్య స్కోర్ ${ndvi.toFixed(2)}. ${ndvi >= 0.6 ? 'పచ్చదనం బాగుంది.' : ndvi >= 0.4 ? 'పచ్చదనం సరిగా ఉంది.' : 'పచ్చదనం తక్కువ.'}` : '',
                ndviPhoto(ndvi, L),
                stypes.length ? `పంటపై ఒత్తిడి: ${stypes.join(', ')}.` : '',
                nitTxt, heatTxt, irrTxt,
            ].filter(Boolean).join(' ');

            return [
                standalone ? `Hello farmer! This is your Bhuhit satellite crop health report.` : `Crop health:`,
                `Your crop season is ${ssn} season. Crop health status is ${hlth.health_Status || 'unknown'}.`,
                `Health score is ${hScore} out of 100. ${hScore >= 80 ? 'Your farm is doing very well.' : hScore >= 60 ? 'Your farm is doing okay.' : 'Your farm needs attention.'}`,
                ndvi != null ? `Over the last three months, satellite measured Crop Health score of ${ndvi.toFixed(2)}. ${ndvi >= 0.6 ? 'Crop canopy is very green and healthy.' : ndvi >= 0.4 ? 'Crop canopy is moderate.' : 'Crop canopy is weak.'}` : '',
                ndviPhoto(ndvi, L),
                crStg ? `Current crop stage: ${crStg}.` : '',
                stypes.length ? `Stress types detected: ${stypes.join(', ')}.` : '',
                nitTxt,
                heatTxt,
                irrTxt,
                fert ? 'Manure application is recommended.' : '',
            ].filter(Boolean).join(' ');
        }

        // ── SOIL HEALTH section ───────────────────────────────────
        if (sec === 'soil') {
            const phTxt = ph != null ? (
                L === 'mr' ? (ph < 6 ? `मातीचा pH ${ph.toFixed(1)} आहे. माती आम्लीय आहे. चुना टाका.` : ph > 7.5 ? `pH ${ph.toFixed(1)} आहे. माती क्षारीय आहे.` : `pH ${ph.toFixed(1)} आहे. माती चांगली आहे.`)
                    : L === 'hi' ? (ph < 6 ? `मिट्टी का pH ${ph.toFixed(1)} है. मिट्टी अम्लीय है. चूना डालें.` : ph > 7.5 ? `pH ${ph.toFixed(1)} है. मिट्टी क्षारीय है.` : `pH ${ph.toFixed(1)} है. मिट्टी अच्छी है.`)
                        : L === 'te' ? (ph < 6 ? `నేల pH ${ph.toFixed(1)}. ఆమ్లీయంగా ఉంది. సున్నం వేయండి.` : ph > 7.5 ? `pH ${ph.toFixed(1)}. క్షారీయంగా ఉంది.` : `pH ${ph.toFixed(1)}. నేల మంచిగా ఉంది.`)
                            : (ph < 6 ? `Soil pH is ${ph.toFixed(1)}. Soil is acidic. Apply lime.` : ph > 7.5 ? `Soil pH is ${ph.toFixed(1)}. Soil is alkaline.` : `Soil pH is ${ph.toFixed(1)}. Soil pH is good.`)
            ) : '';
            const elevTxt = elev != null ? (
                L === 'mr' ? `शेताची उंची समुद्रसपाटीपासून ${Math.round(elev)} मीटर आहे.`
                    : L === 'hi' ? `खेत की ऊंचाई समुद्र तल से ${Math.round(elev)} मीटर है.`
                        : L === 'te' ? `పొలం ఎత్తు సముద్ర మట్టానికి ${Math.round(elev)} మీటర్లు.`
                            : `Farm elevation is ${Math.round(elev)} metres above sea level.`
            ) : '';
            const slopeTxt = slope != null && slope > 5 ? (
                L === 'mr' ? `उताराचा कोन ${slope.toFixed(0)} अंश आहे. पावसाचे पाणी वाहून जाते.`
                    : L === 'hi' ? `जमीन की ढलान ${slope.toFixed(0)} डिग्री है. वर्षा जल बह जाता है.`
                        : L === 'te' ? `వాలు ${slope.toFixed(0)} డిగ్రీలు. వర్షపు నీరు కొట్టుకుపోతుంది.`
                            : `Slope is ${slope.toFixed(0)} degrees. Rainwater may run off quickly.`
            ) : '';
            const surfTxt = surf != null ? (
                L === 'mr' ? `पृष्ठभागाचा ओलावा ${surf.toFixed(0)} टक्के आहे.`
                    : L === 'hi' ? `मिट्टी की ऊपरी नमी ${surf.toFixed(0)} प्रतिशत है.`
                        : L === 'te' ? `ఉపరితల తేమ ${surf.toFixed(0)} శాతం.`
                            : `Surface soil moisture is ${surf.toFixed(0)} percent.`
            ) : '';
            const rootTxt = rootZ != null ? (
                L === 'mr' ? `मूळ क्षेत्राचा ओलावा ${rootZ.toFixed(0)} टक्के आहे. ${rootZ < 20 ? 'सिंचन तातडीने करा.' : rootZ < 35 ? 'लवकर सिंचन करा.' : 'ओलावा ठीक आहे.'}`
                    : L === 'hi' ? `जड़ क्षेत्र की नमी ${rootZ.toFixed(0)} प्रतिशत है. ${rootZ < 20 ? 'तुरंत सिंचाई करें.' : rootZ < 35 ? 'जल्द सिंचाई करें.' : 'नमी ठीक है.'}`
                        : L === 'te' ? `వేర్ల మండలం తేమ ${rootZ.toFixed(0)} శాతం. ${rootZ < 20 ? 'వెంటనే నీరు పెట్టండి.' : rootZ < 35 ? 'త్వరలో నీరు పెట్టండి.' : 'తేమ సరిగా ఉంది.'}`
                            : `Root zone moisture is ${rootZ.toFixed(0)} percent. ${rootZ < 20 ? 'Irrigate immediately.' : rootZ < 35 ? 'Irrigate soon.' : 'Moisture level is good.'}`
            ) : '';
            const nitSoilTxt = nitIdx != null ? (
                L === 'mr' ? (nitIdx < 0.35 ? 'जमिनीत नत्र कमी आहे. युरिया खत द्या.' : nitIdx > 0.65 ? 'नत्र जास्त आहे. खत थांबवा.' : 'नत्र प्रमाण चांगले आहे.')
                    : L === 'hi' ? (nitIdx < 0.35 ? 'मिट्टी में नाइट्रोजन कम है. यूरिया खाद दें.' : nitIdx > 0.65 ? 'नाइट्रोजन ज्यादा है. खाद रोकें.' : 'नाइट्रोजन स्तर अच्छा है.')
                        : L === 'te' ? (nitIdx < 0.35 ? 'నేలలో నత్రజని తక్కువ. యూరియా ఎరువు వేయండి.' : nitIdx > 0.65 ? 'నత్రజని ఎక్కువ. ఎరువు ఆపండి.' : 'నత్రజని స్థాయి బాగుంది.')
                            : (nitIdx < 0.35 ? 'Soil nitrogen is low. Apply manure.' : nitIdx > 0.65 ? 'Nitrogen is excess. Stop applying manure.' : 'Soil nitrogen level is good.')
            ) : '';

            if (L === 'mr') return [standalone ? `माती आरोग्य.` : `माती:`, phTxt, elevTxt, slopeTxt, surfTxt, rootTxt, nitSoilTxt].filter(Boolean).join(' ');
            if (L === 'hi') return [standalone ? `मिट्टी स्वास्थ्य.` : `मिट्टी:`, phTxt, elevTxt, slopeTxt, surfTxt, rootTxt, nitSoilTxt].filter(Boolean).join(' ');
            if (L === 'te') return [standalone ? `నేల ఆరోగ్యం.` : `నేల:`, phTxt, elevTxt, slopeTxt, surfTxt, rootTxt, nitSoilTxt].filter(Boolean).join(' ');
            return [standalone ? `Soil health.` : `Soil:`, phTxt, elevTxt, slopeTxt, surfTxt, rootTxt, nitSoilTxt].filter(Boolean).join(' ');
        }

        // ── MOISTURE section ──────────────────────────────────────
        if (sec === 'moisture') {
            const rootTxt2 = rootZ != null ? (
                L === 'mr' ? `मूळ क्षेत्राचा ओलावा ${rootZ.toFixed(0)} टक्के. ${rootZ < 20 ? 'पाणी खूप कमी आहे. ताबडतोब सिंचन करा.' : rootZ < 35 ? 'पाणी कमी आहे. लवकर सिंचन करा.' : 'पाण्याचे प्रमाण ठीक आहे.'}`
                    : L === 'hi' ? `जड़ क्षेत्र की नमी ${rootZ.toFixed(0)} प्रतिशत. ${rootZ < 20 ? 'पानी बहुत कम है. तुरंत सिंचाई करें.' : rootZ < 35 ? 'पानी कम है. जल्द सिंचाई करें.' : 'पानी की मात्रा ठीक है.'}`
                        : L === 'te' ? `వేర్ల మండలం తేమ ${rootZ.toFixed(0)} శాతం. ${rootZ < 20 ? 'నీరు చాలా తక్కువ. వెంటనే నీటిపారుదల చేయండి.' : rootZ < 35 ? 'నీరు తక్కువ. త్వరలో నీరు పెట్టండి.' : 'నీరు సరిపోతుంది.'}`
                            : `Root zone moisture is ${rootZ.toFixed(0)}%. ${rootZ < 20 ? 'Water is critically low. Irrigate immediately.' : rootZ < 35 ? 'Water is low. Irrigate soon.' : 'Moisture is adequate.'}`
            ) : '';
            const irrAdv = irr
                ? (L === 'mr' ? 'सिंचन सल्ला: आज किंवा उद्या सिंचन करा.' : L === 'hi' ? 'सिंचाई सलाह: आज या कल सिंचाई करें.' : L === 'te' ? 'నీటిపారుదల సలహా: ఈ రోజు లేదా రేపు నీరు పెట్టండి.' : 'Irrigation advice: water your farm today or tomorrow.')
                : (L === 'mr' ? 'सध्या सिंचनाची गरज नाही.' : L === 'hi' ? 'अभी सिंचाई की जरूरत नहीं.' : L === 'te' ? 'ప్రస్తుతం నీటిపారుదల అవసరం లేదు.' : 'No irrigation needed right now.');
            if (L === 'mr') return [standalone ? `माती ओलावा माहिती.` : `ओलावा:`, surf != null ? `जमिनीचा वरचा ओलावा ${surf.toFixed(0)} टक्के आहे.` : '', rootTxt2, irrAdv].filter(Boolean).join(' ');
            if (L === 'hi') return [standalone ? `मिट्टी नमी जानकारी.` : `नमी:`, surf != null ? `मिट्टी की ऊपरी नमी ${surf.toFixed(0)} प्रतिशत है.` : '', rootTxt2, irrAdv].filter(Boolean).join(' ');
            if (L === 'te') return [standalone ? `నేల తేమ సమాచారం.` : `తేమ:`, surf != null ? `ఉపరితల తేమ ${surf.toFixed(0)} శాతం.` : '', rootTxt2, irrAdv].filter(Boolean).join(' ');
            return [standalone ? `Soil moisture details.` : `Moisture:`, surf != null ? `Surface soil moisture is ${surf.toFixed(0)} percent.` : '', rootTxt2, irrAdv].filter(Boolean).join(' ');
        }

        // ── WEATHER section ───────────────────────────────────────
        if (sec === 'weather') {
            const tempTxt = temp != null ? (
                L === 'mr' ? `आजचे तापमान ${temp.toFixed(0)} अंश आहे. ${temp > 38 ? 'खूप जास्त उष्णता आहे. झाडांना सकाळी पाणी द्या.' : temp > 32 ? 'जास्त उष्णता आहे.' : 'तापमान ठीक आहे.'}`
                    : L === 'hi' ? `आज का तापमान ${temp.toFixed(0)} डिग्री है. ${temp > 38 ? 'बहुत गर्मी है. सुबह पानी दें.' : temp > 32 ? 'गर्मी ज्यादा है.' : 'तापमान ठीक है.'}`
                        : L === 'te' ? `నేటి ఉష్ణోగ్రత ${temp.toFixed(0)} డిగ్రీలు. ${temp > 38 ? 'చాలా వేడిగా ఉంది. ఉదయం నీరు పెట్టండి.' : temp > 32 ? 'వేడి ఎక్కువగా ఉంది.' : 'ఉష్ణోగ్రత సరిగా ఉంది.'}`
                            : `Today's temperature is ${temp.toFixed(0)} degrees Celsius. ${temp > 38 ? 'Very hot. Water crops in the morning.' : temp > 32 ? 'It is quite warm.' : 'Temperature is normal.'}`
            ) : '';
            const humTxt = hum != null ? (
                L === 'mr' ? `हवेतील आर्द्रता ${Math.round(hum)} टक्के आहे. ${hum > 80 ? 'आर्द्रता जास्त आहे. बुरशीजन्य रोगाची काळजी घ्या.' : hum < 30 ? 'हवा कोरडी आहे.' : 'आर्द्रता ठीक आहे.'}`
                    : L === 'hi' ? `हवा में नमी ${Math.round(hum)} प्रतिशत है. ${hum > 80 ? 'नमी बहुत ज्यादा है. फंगस रोग का ध्यान रखें.' : hum < 30 ? 'हवा सूखी है.' : 'नमी ठीक है.'}`
                        : L === 'te' ? `గాలిలో తేమ ${Math.round(hum)} శాతం. ${hum > 80 ? 'తేమ చాలా ఎక్కువ. శిలీంద్ర వ్యాధి జాగ్రత్త.' : hum < 30 ? 'గాలి పొడిగా ఉంది.' : 'తేమ సరిగా ఉంది.'}`
                            : `Humidity is ${Math.round(hum)} percent. ${hum > 80 ? 'Very humid. Watch for fungal disease.' : hum < 30 ? 'Air is dry.' : 'Humidity is normal.'}`
            ) : '';
            const rainTxt = rain != null ? (
                L === 'mr' ? `मागील ३० दिवसांत ${Math.round(rain)} मिलीमीटर पाऊस झाला.`
                    : L === 'hi' ? `पिछले 30 दिनों में ${Math.round(rain)} मिलीमीटर बारिश हुई.`
                        : L === 'te' ? `గత 30 రోజుల్లో ${Math.round(rain)} మిల్లీమీటర్ల వర్షపాతం.`
                            : `Over the last 30 days, ${Math.round(rain)} millimetres of rain fell.`
            ) : '';
            const fcTxt = totRain7 > 0 || maxTemp7 != null ? (
                L === 'mr' ? `पुढील ७ दिवसांचा अंदाज: ${totRain7 > 20 ? `${Math.round(totRain7)} मिमी पाऊस अपेक्षित आहे.` : totRain7 > 0 ? `थोडा पाऊस होईल.` : 'पाऊस नाही.'} ${maxTemp7 != null ? `जास्तीत जास्त तापमान ${Math.round(maxTemp7)} अंश राहील.` : ''}`
                    : L === 'hi' ? `अगले 7 दिनों का अनुमान: ${totRain7 > 20 ? `${Math.round(totRain7)} मिमी बारिश की उम्मीद है.` : totRain7 > 0 ? `थोड़ी बारिश होगी.` : 'बारिश नहीं.'} ${maxTemp7 != null ? `अधिकतम तापमान ${Math.round(maxTemp7)} डिग्री रहेगा.` : ''}`
                        : L === 'te' ? `రాబోయే 7 రోజుల అంచనా: ${totRain7 > 20 ? `${Math.round(totRain7)} మిమీ వర్షం అంచనా.` : totRain7 > 0 ? `కొంచెం వర్షం.` : 'వర్షం లేదు.'} ${maxTemp7 != null ? `గరిష్ఠ ఉష్ణోగ్రత ${Math.round(maxTemp7)} డిగ్రీలు.` : ''}`
                            : `7-day forecast: ${totRain7 > 20 ? `${Math.round(totRain7)} mm of rain expected.` : totRain7 > 0 ? `Light rain expected.` : 'No rain expected.'} ${maxTemp7 != null ? `Maximum temperature will be around ${Math.round(maxTemp7)} degrees.` : ''}`
            ) : '';
            if (L === 'mr') return [standalone ? `हवामान माहिती.` : `हवामान:`, tempTxt, humTxt, rainTxt, fcTxt].filter(Boolean).join(' ');
            if (L === 'hi') return [standalone ? `मौसम जानकारी.` : `मौसम:`, tempTxt, humTxt, rainTxt, fcTxt].filter(Boolean).join(' ');
            if (L === 'te') return [standalone ? `వాతావరణ సమాచారం.` : `వాతావరణం:`, tempTxt, humTxt, rainTxt, fcTxt].filter(Boolean).join(' ');
            return [standalone ? `Weather details.` : `Weather:`, tempTxt, humTxt, rainTxt, fcTxt].filter(Boolean).join(' ');
        }

        // ── ALERTS section ────────────────────────────────────────
        if (sec === 'alerts') {
            const hi = highAlerts.length;
            const tot = algAlerts.length;
            const alertSummary = highAlerts.slice(0, 3).map(a => stressName(a.message || a.type, L)).join('. ');
            if (L === 'mr') return hi > 0
                ? `${standalone ? '' : 'सूचना: '}${hi} गंभीर सूचना आहेत. ${alertSummary}. कृपया ताबडतोब कार्यवाही करा.`
                : `${standalone ? '' : 'सूचना: '}सध्या कोणतीही गंभीर सूचना नाही. एकूण ${tot} सूचना आहेत.`;
            if (L === 'hi') return hi > 0
                ? `${standalone ? '' : 'चेतावनी: '}${hi} गंभीर चेतावनियां हैं. ${alertSummary}. कृपया तुरंत कार्रवाई करें.`
                : `${standalone ? '' : 'चेतावनी: '}अभी कोई गंभीर चेतावनी नहीं है. कुल ${tot} सूचनाएं हैं.`;
            if (L === 'te') return hi > 0
                ? `${standalone ? '' : 'హెచ్చరికలు: '}${hi} తీవ్రమైన హెచ్చరికలు ఉన్నాయి. ${alertSummary}. దయచేసి వెంటనే చర్య తీసుకోండి.`
                : `${standalone ? '' : 'హెచ్చరికలు: '}ఇప్పుడు తీవ్రమైన హెచ్చరికలు లేవు. మొత్తం ${tot} హెచ్చరికలు.`;
            return hi > 0
                ? `${standalone ? '' : 'Alerts: '}${hi} critical alert${hi > 1 ? 's' : ''}. ${alertSummary}. Please take action immediately.`
                : `${standalone ? '' : 'Alerts: '}No critical alerts at this time. Total ${tot} notification${tot !== 1 ? 's' : ''}.`;
        }

        // ── ORGANIC section ───────────────────────────────────────
        if (sec === 'organic') {
            const bd = data.organic_Alert?.category_Breakdown || {};
            // score + grade + eligibility
            const scoreTxt = os != null ? (
                L === 'mr' ? `तुमच्या शेताचा सेंद्रिय गुण १०० पैकी ${os.toFixed(0)} आहे. दर्जा ${og}. ${os >= 80 ? 'शेत सेंद्रिय प्रमाणपत्रासाठी योग्य आहे.' : os >= 60 ? 'थोडी सुधारणा केल्यास प्रमाणपत्र मिळेल.' : 'अजून सुधारणा करणे आवश्यक आहे.'}`
                    : L === 'hi' ? `आपके खेत का जैविक स्कोर 100 में से ${os.toFixed(0)} है. ग्रेड ${og}. ${os >= 80 ? 'खेत जैविक प्रमाणन के लिए उपयुक्त है.' : os >= 60 ? 'थोड़े सुधार से प्रमाणन मिलेगा.' : 'और सुधार जरूरी है.'}`
                        : L === 'te' ? `మీ పొలం సేంద్రీయ స్కోర్ 100 లో ${os.toFixed(0)}. గ్రేడ్ ${og}. ${os >= 80 ? 'పొలం సేంద్రీయ ధృవీకరణకు అనుకూలం.' : os >= 60 ? 'కొంచెం మెరుగుదలతో ధృవీకరణ వస్తుంది.' : 'ఇంకా మెరుగుదల అవసరం.'}`
                            : `Your farm's organic score is ${os.toFixed(0)} out of 100. Grade ${og}. ${os >= 80 ? 'Farm qualifies for organic certification.' : os >= 60 ? 'Small improvements will get you certified.' : 'More improvements are needed.'}`
            ) : '';
            // pass/fail counts
            const passTxt = oPass > 0 || oFail > 0 ? (
                L === 'mr' ? `${oPass} निकष उत्तीर्ण आणि ${oFail} निकष अयशस्वी आहेत.`
                    : L === 'hi' ? `${oPass} मानदंड पास और ${oFail} मानदंड विफल हैं.`
                        : L === 'te' ? `${oPass} ప్రమాణాలు ఉత్తీర్ణం, ${oFail} విఫలమయ్యాయి.`
                            : `${oPass} criteria passed and ${oFail} criteria failed.`
            ) : '';
            // 6 pillar goal scores — read from category_Breakdown (keys A-F)
            const pillarNames = {
                A: { mr: 'वनस्पती आरोग्य', hi: 'वनस्पति स्वास्थ्य', te: 'వృక్షసంపద ఆరోగ్యం', en: 'Vegetation Health' },
                B: { mr: 'जल व्यवस्थापन', hi: 'जल प्रबंधन', te: 'నీటి నిర్వహణ', en: 'Water Management' },
                C: { mr: 'माती आरोग्य', hi: 'मिट्टी स्वास्थ्य', te: 'నేల ఆరోగ్యం', en: 'Soil Health' },
                D: { mr: 'हवामान सहनशीलता', hi: 'जलवायु लचीलापन', te: 'వాతావరణ స్థితిస్థాపకత', en: 'Climate Resilience' },
                E: { mr: 'भूप्रदेश उपयुक्तता', hi: 'भूभाग उपयुक्तता', te: 'భూభాగ అనుకూలత', en: 'Terrain Suitability' },
                F: { mr: 'ट्रेंड स्थिरता', hi: 'ट्रेंड स्थिरता', te: 'ట్రెండ్ స్థిరత్వం', en: 'Trend Stability' },
            };
            const grade = s => s >= 80 ? { mr: 'उत्तम', hi: 'बहुत अच्छा', te: 'చాలా బాగు', en: 'Excellent' }
                : s >= 60 ? { mr: 'ठीक', hi: 'ठीक', te: 'సరే', en: 'Fair' }
                    : { mr: 'कमकुवत', hi: 'कमजोर', te: 'బలహీనంగా', en: 'Weak' };
            const advice = {
                A: s => s >= 80
                    ? { mr: 'सध्याची पिकाची काळजी चांगली आहे. असेच ठेवा.', hi: 'फसल प्रबंधन अच्छा है. ऐसे ही रखें.', te: 'పంట నిర్వహణ మంచిగా ఉంది.', en: 'Crop management is excellent. Keep it up.' }
                    : s >= 60
                        ? { mr: 'पानी शिंपडणे करा. पोषण तत्त्वे तपासा.', hi: 'पत्तेदार छिड़काव करें. पोषण जांचें.', te: 'ఆకు పిచికారీ చేయండి. పోషకాలు తనిఖీ.', en: 'Apply foliar spray. Check nutrient levels.' }
                        : { mr: 'कंपोस्ट खत द्या. झाडांमधील अंतर सुधारा.', hi: 'कंपोस्ट खाद डालें. पौधों की दूरी ठीक करें.', te: 'కంపోస్ట్ ఎరువు వేయండి. మొక్కల అంతరం సరి చేయండి.', en: 'Apply compost. Improve plant spacing.' },
                B: s => s >= 80
                    ? { mr: 'पाणी व्यवस्थापन उत्तम आहे.', hi: 'जल प्रबंधन उत्तम है.', te: 'నీటి నిర్వహణ చాలా బాగుంది.', en: 'Water management is optimal.' }
                    : s >= 60
                        ? { mr: 'ठिबक सिंचन आखा. पाणी वाहिन्या तपासा.', hi: 'ड्रिप सिंचाई की व्यवस्था करें. नहर देखें.', te: 'బిందు సేద్యం అమర్చండి. కాలువలు తనిఖీ.', en: 'Schedule drip irrigation. Inspect water channels.' }
                        : { mr: 'तातडीने मल्चिंग आणि ठिबक सिंचन बसवा.', hi: 'तुरंत मल्चिंग और ड्रिप सिंचाई लगाएं.', te: 'తక్షణమే మల్చింగ్, బిందు సేద్యం అమర్చండి.', en: 'Install mulching and drip system urgently.' },
                C: s => s >= 80
                    ? { mr: 'माती उत्तम आहे. सेंद्रिय खते द्या.', hi: 'मिट्टी उत्तम है. जैव खाद बनाए रखें.', te: 'నేల చాలా మంచిగా ఉంది.', en: 'Soil health is excellent.' }
                    : s >= 60
                        ? { mr: 'गांडूळ खत घाला. pH तपासून चुना टाका.', hi: 'वर्मीकंपोस्ट डालें. pH देखकर चूना लगाएं.', te: 'వెర్మీకంపోస్ట్ వేయండి. pH తనిఖీ.', en: 'Add vermicompost. Check pH and apply lime.' }
                        : { mr: 'माती सुधारणा तातडीने करा. pH आणि कार्बन खूप कमी आहे.', hi: 'तुरंत मिट्टी सुधारें. pH और कार्बन बहुत कम है.', te: 'నేల దిద్దుబాటు తక్షణమే అవసరం.', en: 'Urgent soil amendment needed. pH and carbon critically low.' },
                D: s => s >= 80
                    ? { mr: 'हवामान सेंद्रिय शेतीसाठी अनुकूल आहे.', hi: 'मौसम जैविक खेती के लिए अनुकूल है.', te: 'వాతావరణం సేంద్రీయ వ్యవసాయానికి అనుకూలంగా ఉంది.', en: 'Climate is favorable for organic farming.' }
                    : s >= 60
                        ? { mr: 'उष्णतेचा ताण लक्षात घ्या. शेड नेट वापरा.', hi: 'गर्मी का तनाव देखें. शेड नेट लगाएं.', te: 'వేడి ఒత్తిడి గమనించండి. షేడ్ నెట్ వాడండి.', en: 'Monitor heat stress. Consider shade netting.' }
                        : { mr: 'हवामान धोका जास्त आहे. बायो स्टिमुलंट वापरा.', hi: 'मौसम जोखिम ज्यादा है. बायो स्टिमुलेंट लगाएं.', te: 'వాతావరణ ప్రమాదం ఎక్కువ. బయో స్టిమ్యులెంట్ వాడండి.', en: 'High climate risk. Apply bio-stimulants.' },
                E: s => s >= 80
                    ? { mr: 'जमीन सेंद्रिय शेतीसाठी उत्तम आहे.', hi: 'जमीन जैविक खेती के लिए उपयुक्त है.', te: 'భూమి సేంద్రీయ వ్యవసాయానికి అనుకూలంగా ఉంది.', en: 'Terrain is well-suited for organic farming.' }
                    : s >= 60
                        ? { mr: 'पाणी साठण्याची समस्या तपासा.', hi: 'जलभराव की समस्या जांचें.', te: 'నీటి నిల్వ సమస్య తనిఖీ.', en: 'Check drainage to prevent waterlogging.' }
                        : { mr: 'उतार धोका आहे. मातीच्या बांधाची व्यवस्था करा.', hi: 'ढलान जोखिम है. मेड बनाएं.', te: 'వాలు ప్రమాదం. వరుస గట్లు వేయండి.', en: 'Slope risk. Contour bunding recommended.' },
                F: s => s >= 80
                    ? { mr: 'पीक वाढ स्थिर आणि चांगली आहे.', hi: 'फसल विकास स्थिर और अच्छा है.', te: 'పంట వృద్ధి స్థిరంగా మంచిగా ఉంది.', en: 'Stable crop growth trend. Consistent performance.' }
                    : s >= 60
                        ? { mr: 'थोडे चढउतार आहेत. सिंचन व खताचे वेळापत्रक तपासा.', hi: 'उतार-चढ़ाव है. सिंचाई और खाद का समय-सारणी जांचें.', te: 'కొంచెం హెచ్చుతగ్గులు. నీటిపారుదల వేళాపట్టి తనిఖీ.', en: 'Some fluctuation. Review irrigation and input calendar.' }
                        : { mr: 'अस्थिर ट्रेंड. कीड चक्र किंवा अनियमित खत तपासा.', hi: 'अस्थिर ट्रेंड. कीट चक्र या अनियमित खाद जांचें.', te: 'అస్థిర ట్రెండ్. తెగులు చక్రం లేదా అనిర్దిష్ట ఎరువు తనిఖీ.', en: 'Unstable trend. Check for pest cycle or irregular inputs.' },
            };
            // Build pillar lines
            const pillarLines = Object.entries(pillarNames).map(([key, names]) => {
                const raw = bd[key];
                const s = raw == null ? null : typeof raw === 'object' ? (raw.score ?? null) : raw;
                if (s == null) return '';
                const grd = grade(s)[L] || grade(s)['en'];
                const adv = (advice[key](s)[L] || advice[key](s)['en']);
                const nm = names[L] || names['en'];
                return L === 'mr' ? `${nm}: ${s.toFixed(0)} गुण, ${grd}. ${adv}`
                    : L === 'hi' ? `${nm}: ${s.toFixed(0)} स्कोर, ${grd}. ${adv}`
                        : L === 'te' ? `${nm}: ${s.toFixed(0)} స్కోర్, ${grd}. ${adv}`
                            : `${nm}: ${s.toFixed(0)} score, ${grd}. ${adv}`;
            }).filter(Boolean);

            const label = { mr: standalone ? 'सेंद्रिय आरोग्य माहिती.' : 'सेंद्रिय:', hi: standalone ? 'जैविक स्वास्थ्य जानकारी.' : 'जैविक:', te: standalone ? 'సేంద్రీయ ఆరోగ్య సమాచారం.' : 'సేంద్రీయ:', en: standalone ? 'Organic health details.' : 'Organic:' }[L] || 'Organic:';
            return [label, scoreTxt, passTxt, ...pillarLines].filter(Boolean).join(' ');
        }

        // ── YIELD section ─────────────────────────────────────────
        if (sec === 'yield') {
            const yTxt = yKgHa != null ? (
                L === 'mr' ? `तुमच्या शेताचे अपेक्षित उत्पन्न प्रति हेक्टर ${yKgHa.toLocaleString()} किलो आहे. एकूण अपेक्षित उत्पन्न ${yTot != null ? yTot.toLocaleString() + '  किलो.' : 'मोजता येत नाही.'} ${yKgHa >= 3000 ? 'उत्पन्न खूप चांगले आहे!' : yKgHa >= 1500 ? 'उत्पन्न ठीक आहे.' : 'उत्पन्न सुधारण्यासाठी खत व पाणी द्या.'}`
                    : L === 'hi' ? `आपके खेत का अनुमानित उत्पादन प्रति हेक्टर ${yKgHa.toLocaleString()} किलोग्राम है. कुल ${yTot != null ? yTot.toLocaleString() + ' किलोग्राम.' : 'गणना नहीं हो सकी.'} ${yKgHa >= 3000 ? 'उत्पादन बहुत अच्छा है!' : yKgHa >= 1500 ? 'उत्पादन ठीक है.' : 'उत्पादन बढ़ाने के लिए खाद और पानी दें.'}`
                        : L === 'te' ? `మీ పొలం అంచనా దిగుబడి హెక్టారుకు ${yKgHa.toLocaleString()} కిలోగ్రాములు. మొత్తం ${yTot != null ? yTot.toLocaleString() + ' కిలోలు.' : 'లెక్కించడం సాధ్యం కాలేదు.'} ${yKgHa >= 3000 ? 'దిగుబడి చాలా బాగుంది!' : yKgHa >= 1500 ? 'దిగుబడి సరిగా ఉంది.' : 'దిగుబడి పెంచడానికి ఎరువు మరియు నీరు ఇవ్వండి.'}`
                            : `Your estimated yield is ${yKgHa.toLocaleString()} kilograms per hectare. Total estimated ${yTot != null ? yTot.toLocaleString() + ' kilograms.' : 'could not be calculated.'} ${yKgHa >= 3000 ? 'Yield is very good!' : yKgHa >= 1500 ? 'Yield is moderate.' : 'To improve yield, apply manure and ensure good irrigation.'}`
            ) : (L === 'mr' ? 'उत्पन्नाचा अंदाज उपलब्ध नाही.' : L === 'hi' ? 'उत्पादन अनुमान उपलब्ध नहीं.' : L === 'te' ? 'దిగుబడి అంచనా అందుబాటులో లేదు.' : 'Yield estimate is not available.');
            const confTxt = yConf ? (
                L === 'mr' ? `विश्वासार्हता: ${yConf}.` : L === 'hi' ? `विश्वसनीयता: ${yConf}.` : L === 'te' ? `నమ్మకత: ${yConf}.` : `Confidence level: ${yConf}.`
            ) : '';
            if (L === 'mr') return [standalone ? `उत्पन्न अंदाज माहिती.` : `उत्पन्न:`, yTxt, confTxt, `हा अंदाज उपग्रह माहितीवर आधारित आहे. प्रत्यक्ष उत्पन्न स्थानिक परिस्थितीवर अवलंबून असते.`].filter(Boolean).join(' ');
            if (L === 'hi') return [standalone ? `उत्पादन अनुमान जानकारी.` : `उत्पादन:`, yTxt, confTxt, `यह अनुमान उपग्रह डेटा पर आधारित है. वास्तविक उत्पादन स्थानीय परिस्थितियों पर निर्भर करता है.`].filter(Boolean).join(' ');
            if (L === 'te') return [standalone ? `దిగుబడి అంచనా సమాచారం.` : `దిగుబడి:`, yTxt, confTxt, `ఈ అంచనా ఉపగ్రహ డేటా ఆధారంగా ఉంది. వాస్తవ దిగుబడి స్థానిక పరిస్థితులపై ఆధారపడుతుంది.`].filter(Boolean).join(' ');
            return [standalone ? `Yield estimate details.` : `Yield:`, yTxt, confTxt, `This estimate is based on satellite data. Actual yield depends on local conditions.`].filter(Boolean).join(' ');
        }

        // ── CROP CALENDAR section ──────────────────────────────────────
        if (sec === 'calendar') {
            const cropKey = data.crop_Identification?.crop_Primary || data.Detected_Crop || '';
            // calData_v removed — engine does not provide _calData
            // Reconstruct stage data from the engine crop
            const STAGE_NAMES = {
                mr: {
                    'Land Prep': 'जमीन तयारी', 'Sowing': 'पेरणी', 'Germination': 'उगवण', 'Transplanting': 'रोप लावणी',
                    'Seedling': 'रोपण', 'Emergence': 'उगवण', 'Vegetative': 'वनस्पतीजन्य', 'Tillering': 'फुटवे अवस्था',
                    'Squaring': 'कळी येणे', 'Flowering': 'फुलोरा', 'Jointing': 'जोडणी', 'Heading': 'शीर्ष',
                    'Panicle Init.': 'लोंबी निर्मिती', 'Boll Dev.': 'बोंड विकास', 'Pod Fill': 'शेंगा भरणे',
                    'Boll Opening': 'बोंड उघडणे', 'Grain Fill': 'धान्य भरणे', 'Maturity': 'परिपक्वता',
                    'Harvest': 'कापणी', 'Picking': 'वेचणी'
                },
                hi: {
                    'Land Prep': 'भूमि तैयारी', 'Sowing': 'बुआई', 'Germination': 'अंकुरण', 'Transplanting': 'रोपाई',
                    'Seedling': 'पौध', 'Emergence': 'उगाव', 'Vegetative': 'वानस्पतिक', 'Tillering': 'कल्ले निकलना',
                    'Squaring': 'कली बनना', 'Flowering': 'फूल आना', 'Jointing': 'जोड़', 'Heading': 'शीर्ष',
                    'Panicle Init.': 'बाली निकलना', 'Boll Dev.': 'टिण्डा विकास', 'Pod Fill': 'फलियां भरना',
                    'Boll Opening': 'टिण्डा खुलना', 'Grain Fill': 'दाना भरना', 'Maturity': 'परिपक्वता',
                    'Harvest': 'कटाई', 'Picking': 'चुनाई'
                },
                te: {
                    'Land Prep': 'నేల తయారీ', 'Sowing': 'విత్తనం వేయడం', 'Germination': 'అంకురోద్గమం',
                    'Transplanting': 'నాట్లు వేయడం', 'Vegetative': 'వృక్ష దశ', 'Tillering': 'తనులు వేయడం',
                    'Flowering': 'పుష్పించడం', 'Grain Fill': 'గింజ నిండటం', 'Maturity': 'పరిపక్వత',
                    'Harvest': 'కోత'
                },
            };
            // Get current stage from health_Status proxy or NDVI range match
            const ndviv = (data.stats?.health_Metric || 0);
            const hstv = data.health?.health_Status || '';
            const ssn = data.meta?.season || '';
            const ssnL = seasonName(ssn, L);
            // Best-guess current stage name from engine
            const stgEn = hstv || 'Growing';
            const stgL = (STAGE_NAMES[L] && STAGE_NAMES[L][stgEn]) || stgEn;
            // Total weeks from NDVI — rough estimate
            const ndviPct = Math.round(ndviv * 100);
            const advice_cal = ndviv >= 0.65
                ? {
                    mr: 'पीक चांगले वाढत आहे. नियमित काळजी ठेवा.',
                    hi: 'फसल अच्छी तरह बढ़ रही है. नियमित देखभाल रखें.',
                    te: 'పంట బాగా పెరుగుతోంది. నిరంతర సంరక్షణ చేయండి.',
                    en: 'Crop is growing well. Maintain regular care.'
                }
                : ndviv >= 0.4
                    ? {
                        mr: 'पीक मध्यम अवस्थेत आहे. खत व पाणी व्यवस्थित द्या.',
                        hi: 'फसल मध्यम चरण में है. जैव खाद और पानी ठीक से दें.',
                        te: 'పంట మధ్య దశలో ఉంది. ఎరువు మరియు నీరు సరిగా ఇవ్వండి.',
                        en: 'Crop is in mid-stage. Ensure proper manure and water.'
                    }
                    : {
                        mr: 'पीक सुरुवातीच्या अवस्थेत आहे. जमीन ओलसर ठेवा.',
                        hi: 'फसल शुरुआती चरण में है. मिट्टी नम रखें.',
                        te: 'పంట ప్రారంభ దశలో ఉంది. నేలను తేమగా ఉంచండి.',
                        en: 'Crop is in early stage. Keep soil moist.'
                    };
            const adv = advice_cal[L] || advice_cal['en'];
            if (L === 'mr') return [
                standalone ? `पीक दिनदर्शिका माहिती.` : `पीक अवस्था:`,
                `तुमचे पीक हंगाम ${ssnL} आहे.`,
                `सध्याची अवस्था: ${stgL}.`,
                `पीक आरोग्य गुण ${ndviv.toFixed(2)} आहे, म्हणजे ${ndviPct} टक्के हिरवळ.`,
                adv,
            ].filter(Boolean).join(' ');
            if (L === 'hi') return [
                standalone ? `फसल कैलेंडर जानकारी.` : `फसल चरण:`,
                `आपकी फसल मौसम ${ssnL} है.`,
                `वर्तमान चरण: ${stgL}.`,
                `फसल स्वास्थ्य स्कोर ${ndviv.toFixed(2)} है, यानी ${ndviPct} प्रतिशत हरियाली.`,
                adv,
            ].filter(Boolean).join(' ');
            if (L === 'te') return [
                standalone ? `పంట క్యాలెండర్ సమాచారం.` : `పంట దశ:`,
                `మీ పంట సీజన్ ${ssnL}.`,
                `ప్రస్తుత దశ: ${stgL}.`,
                `పంట ఆరోగ్య స్కోర్ ${ndviv.toFixed(2)}, అంటే ${ndviPct} శాతం పచ్చదనం.`,
                adv,
            ].filter(Boolean).join(' ');
            return [
                standalone ? `Crop calendar details.` : `Crop stage:`,
                `Your crop season is ${ssn}.`,
                `Current stage: ${stgEn}.`,
                `Crop health score is ${ndviv.toFixed(2)}, meaning ${ndviPct} percent green cover.`,
                adv,
            ].filter(Boolean).join(' ');
        }

        return '';
    };

    // ── Full report — all sections combined ───────────────────────
    const buildFullReport = (L) => {
        if (!data) return L === 'mr' ? 'स्कॅन केल्यानंतर माहिती मिळेल.' : L === 'hi' ? 'स्कैन के बाद जानकारी उपलब्ध होगी.' : L === 'te' ? 'స్కాన్ తర్వాత సమాచారం అందుబాటులో ఉంటుంది.' : 'Please run a scan first.';
        // ONE greeting at the start, then each section data without repeated greetings
        const greeting = {
            mr: 'नमस्कार शेतकरी! हे भूहित उपग्रह संपूर्ण शेत अहवाल आहे.',
            hi: 'नमस्ते किसान भाई! यह भूहित उपग्रह का पूरा खेत रिपोर्ट है.',
            te: 'నమస్కారం రైతు! ఇది భూహిత్ ఉపగ్రహ సమగ్ర పొలం నివేదిక.',
            en: 'Hello farmer! This is your complete Bhuhit satellite farm report.',
        }[L] || 'Hello farmer! This is your complete Bhuhit satellite farm report.';
        // Each section called with standalone=false (no individual greetings)
        const sections = ['crop', 'soil', 'moisture', 'weather', 'organic', 'yield', 'alerts', 'calendar'];
        const parts = sections
            .map(sec => buildSectionText(sec, L, false))
            .filter(Boolean);
        return [greeting, ...parts].join(' ');
    };

    // ── Main text getter ─────────────────────────────────────────
    //     const getVoiceText = (section = null) => {
    //         const voice = data?.farm_Voice;
    //         if (voice) {
    //             if (section && voice.sections?.[section]) return voice.sections[section][lang] || voice.sections[section]['en'] || '';
    //             if (!section) return voice[lang] || voice['en'] || buildFullReport(lang);
    //         }
    //         if (!data) return lang==='mr'?'स्कॅन केल्यानंतर माहिती उपलब्ध होईल.':lang==='hi'?'स्कैन के बाद जानकारी उपलब्ध होगी.':lang==='te'?'స్కాన్ తర్వాత సమాచారం అందుబాటులో ఉంటుంది.':'Please run a scan first.';
    //         if (section) return buildSectionText(section, lang) || buildFullReport(lang);
    //         return buildFullReport(lang);
    //     };

    // ═══════════════════════════════════════════════════════════════════
    //  VOICE ENGINE V2 — Industry Standard TTS for 4 Languages
    //  Fixes applied:
    //  1. cleanTextForTTS()  — remove symbols/acronyms that cause English switch
    //  2. splitIntoChunks()  — Chrome 200-char limit → sentence-boundary splits
    //  3. speakChunks()      — sequential chunk playback with 150ms natural pause
    //  4. Per-language rate  — MR:0.72 TE:0.75 HI:0.80 EN:0.88 (tested on Android)
    //  5. MR pitch 0.92      — lower pitch = more natural Marathi voice
    //  6. Never assigns English voice to non-English text
    // ═══════════════════════════════════════════════════════════════════

    // ── cleanTextForTTS: remove/replace symbols that break Indic TTS ──
    // Problem: numbers+symbols in Marathi text cause Chrome to switch to
    // English mid-sentence. We replace common ones with native words.
    const cleanTextForTTS = (raw, langCode) => {
        if (!raw) return '';
        let t = String(raw);
        if (langCode === 'mr-IN') {
            t = t.replace(/(\d+\.?\d*)\s*%/g, (_, n) => `${n} टक्के`);
            t = t.replace(/(\d+\.?\d*)\s*°C/g, (_, n) => `${n} अंश`);
            t = t.replace(/(\d+\.?\d*)\s*°/g, (_, n) => `${n} अंश`);
            t = t.replace(/(\d+\.?\d*)\s*mm/g, (_, n) => `${n} मिलीमीटर`);
            t = t.replace(/(\d+\.?\d*)\s*kg\/ha/g, (_, n) => `${n} किलो प्रति हेक्टर`);
            t = t.replace(/(\d+\.?\d*)\s*kg/g, (_, n) => `${n} किलोग्राम`);
            t = t.replace(/(\d+\.?\d*)\s*ha\b/g, (_, n) => `${n} हेक्टर`);
            t = t.replace(/(\d+\.?\d*)\s*m\b/g, (_, n) => `${n} मीटर`);
            t = t.replace(/(\d+\.?\d*)\s*ac\b/g, (_, n) => `${n} एकर`);
            // Remove all-caps acronyms (NDVI, EVI, SAR etc) — cause English TTS
            t = t.replace(/\b[A-Z]{2,6}\b/g, '');
            // Remove special chars that TTS reads aloud
            t = t.replace(/[★☆✔✘⚠📐✅❌🌱🌿🌸🌻🚜🧅🥔🍅🍌🥭]/g, '');
            t = t.replace(/[-–—]/g, ' ');
        } else if (langCode === 'hi-IN') {
            t = t.replace(/(\d+\.?\d*)\s*%/g, (_, n) => `${n} प्रतिशत`);
            t = t.replace(/(\d+\.?\d*)\s*°C/g, (_, n) => `${n} डिग्री`);
            t = t.replace(/(\d+\.?\d*)\s*°/g, (_, n) => `${n} डिग्री`);
            t = t.replace(/(\d+\.?\d*)\s*mm/g, (_, n) => `${n} मिलीमीटर`);
            t = t.replace(/(\d+\.?\d*)\s*kg/g, (_, n) => `${n} किलोग्राम`);
            t = t.replace(/\b[A-Z]{2,6}\b/g, '');
            t = t.replace(/[★☆✔✘⚠]/g, '');
        } else if (langCode === 'te-IN') {
            t = t.replace(/(\d+\.?\d*)\s*%/g, (_, n) => `${n} శాతం`);
            t = t.replace(/(\d+\.?\d*)\s*°C/g, (_, n) => `${n} డిగ్రీలు`);
            t = t.replace(/(\d+\.?\d*)\s*°/g, (_, n) => `${n} డిగ్రీలు`);
            t = t.replace(/(\d+\.?\d*)\s*mm/g, (_, n) => `${n} మిల్లీమీటర్లు`);
            t = t.replace(/(\d+\.?\d*)\s*kg/g, (_, n) => `${n} కిలోగ్రాములు`);
            t = t.replace(/\b[A-Z]{2,6}\b/g, '');
            t = t.replace(/[★☆✔✘⚠]/g, '');
        }
        // Final: collapse whitespace
        return t.replace(/\s{2,}/g, ' ').trim();
    };

    // ── splitIntoChunks: Chrome online TTS has ~200-char limit ────────
    // Without splitting, Chrome cuts off long Marathi/Telugu text mid-sentence.
    // Bug fix: replaced lookbehind regex (?<=[।.!?]) with forward split — Android
    // Chrome/WebView does NOT support lookbehind assertions and throws silently,
    // causing the entire text to be sent as one chunk which Chrome online TTS drops.
    const splitIntoChunks = (text, maxLen = 190) => {
        if (!text) return [];
        if (text.length <= maxLen) return [text];
        const chunks = [];
        // Split on sentence-ending punctuation — forward split, Android-safe
        const parts = text.split(/((?:[।.!?])\s*)/);
        let cur = '';
        for (let i = 0; i < parts.length; i++) {
            const p = parts[i];
            if (!p) continue;
            const candidate = cur + p;
            if (candidate.length > maxLen && cur.length > 0) {
                chunks.push(cur.trim());
                cur = p;
            } else {
                cur = candidate;
            }
        }
        if (cur.trim()) chunks.push(cur.trim());
        // Fallback: if no sentence boundaries found, split by character length
        if (chunks.length === 0) {
            for (let i = 0; i < text.length; i += maxLen) chunks.push(text.slice(i, i + maxLen));
        }
        return chunks.filter(c => c.trim().length > 0);
    };

    // ── speakChunks: play array of text chunks sequentially ───────────
    // Each chunk is a separate SpeechSynthesisUtterance.
    // 150ms gap between chunks = natural sentence pause.
    const speakChunks = (chunks, sectionKey, code, idx = 0) => {
        const synth = synthRef.current;
        if (!synth || idx >= chunks.length) {
            if (idx >= chunks.length) { setVoicePlaying(false); setVoiceSection(null); }
            return;
        }
        // Bug fix: chunks are already cleaned by doSpeak — do NOT clean again
        const chunk = chunks[idx];
        if (!chunk.trim()) { speakChunks(chunks, sectionKey, code, idx + 1); return; }

        const utt = new SpeechSynthesisUtterance(chunk);
        utt.lang = code;
        utt.rate = code === 'mr-IN' ? 0.72
            : code === 'te-IN' ? 0.75
                : code === 'hi-IN' ? 0.80 : 0.88;
        utt.pitch = code === 'mr-IN' ? 0.92
            : code === 'te-IN' ? 0.95 : 1.0;
        utt.volume = 1.0;

        // Assign voice only if language matches — NEVER assign English to Indic
        const v = pickVoice(code);
        if (v) utt.voice = v;

        // Bug fix: set playing state immediately before speak (not in onstart)
        // Chrome sometimes never fires onstart for online TTS on Indic languages
        if (idx === 0) {
            setVoicePlaying(true);
            setVoiceSection(sectionKey);
        }
        utt.onend = () => {
            if (idx < chunks.length - 1) {
                setTimeout(() => speakChunks(chunks, sectionKey, code, idx + 1), 150);
            } else {
                stopChromePauseFix(); setVoicePlaying(false); setVoiceSection(null);
            }
        };
        utt.onerror = (e) => {
            if (e.error !== 'interrupted') { stopChromePauseFix(); setVoicePlaying(false); setVoiceSection(null); }
        };
        synth.speak(utt);
    };

    // ── doSpeak: entry point — clean → split → speak chunks ───────────
    const doSpeak = (text, sectionKey, code) => {
        const synth = synthRef.current;
        if (!synth || !text) return;
        synth.cancel();
        // Clean text first, then split into browser-safe chunks
        const cleaned = cleanTextForTTS(text, code);
        const chunks = splitIntoChunks(cleaned, 190);
        if (chunks.length === 0) return;
        speakChunks(chunks, sectionKey, code, 0);
    };

    // ── speakText: cross-browser voice orchestration ─────────────────
    //
    // Browser    | Voices ready?  | Strategy
    // -----------|----------------|----------------------------------
    // Edge       | Immediately    | doSpeak at once — has offline Indic
    // Firefox    | Immediately    | doSpeak at once
    // Chrome     | Async (~300ms) | MUST wait for onvoiceschanged event
    //            |   mr/te/hi:    | Google online TTS via utt.lang routing
    //            |   no offline   | Need 300-500ms after voices event fires
    // Safari     | Async (~500ms) | Same as Chrome; has hi-IN on iOS
    // Android    | Async (1–2s)   | Google TTS engine via onvoiceschanged
    //
    // ROOT CAUSE of Chrome Indic failure:
    //   1. synth.getVoices() returns [] on first call in Chrome.
    //   2. Chrome fires onvoiceschanged asynchronously.
    //   3. Chrome has a known bug: speechSynthesis PAUSES after ~15s of audio.
    //      Fix: a setInterval pings synth.resume() every 10s while speaking.
    //   4. Race condition: setting onvoiceschanged in speakText overwrote the
    //      useEffect handler — fixed by using voicesRef directly.
    //
    const chromePauseFixRef = useRef(null);  // interval handle for Chrome pause workaround

    // Start Chrome pause-prevention timer
    const startChromePauseFix = () => {
        if (!isChrome) return;
        stopChromePauseFix();
        chromePauseFixRef.current = setInterval(() => {
            const synth = synthRef.current;
            if (!synth) return;
            if (synth.speaking && synth.paused) {
                synth.resume();
            }
        }, 10000);
    };
    const stopChromePauseFix = () => {
        if (chromePauseFixRef.current) {
            clearInterval(chromePauseFixRef.current);
            chromePauseFixRef.current = null;
        }
    };

    const speakText = (text, sectionKey = null) => {
        const synth = synthRef.current;
        if (!synth || !text) return;

        // Toggle off if same section button pressed again
        if (voicePlaying && voiceSection === sectionKey) {
            synth.cancel();
            stopChromePauseFix();
            setVoicePlaying(false); setVoiceSection(null); return;
        }

        synth.cancel();
        stopChromePauseFix();

        const code = VOICE_LANG[lang] || 'en-IN';
        const isIndic = (lang === 'mr' || lang === 'te' || lang === 'hi');

        const trySpeak = (voices) => {
            if (voices.length > 0) voicesRef.current = voices;
            const vv = voicesRef.current;
            const hasLang = vv.some(v =>
                v.lang === code || v.lang.startsWith(code.split('-')[0])
            );

            if (!hasLang && (lang === 'mr' || lang === 'te')) {
                // No offline mr/te voice — show install hint once
                if ((isChrome || isSafari) && !noIndic) setNoIndic(true);
                // Still attempt via browser online TTS routing
                const delayMs = isChrome ? 300 : (lang === 'te' ? 200 : 50);
                setTimeout(() => { doSpeak(text, sectionKey, code); startChromePauseFix(); }, delayMs);
            } else {
                doSpeak(text, sectionKey, code);
                startChromePauseFix();
            }
        };

        // Chrome fix: always call getVoices() fresh first
        const freshVoices = synth.getVoices();
        if (freshVoices.length > 0) {
            trySpeak(freshVoices);
            return;
        }

        // Chrome/Safari: voices not ready — wait for onvoiceschanged
        // NOTE: do NOT overwrite synth.onvoiceschanged here (race condition with useEffect).
        // Instead, use a one-shot polling approach.
        let voicesLoaded = false;
        const pollAndSpeak = () => {
            if (voicesLoaded) return;
            const v = synth.getVoices();
            if (v.length > 0) {
                voicesLoaded = true;
                // Extra 100ms for Chrome to fully initialize online TTS
                setTimeout(() => trySpeak(v), isChrome && isIndic ? 100 : 0);
                return;
            }
        };

        // Poll via existing onvoiceschanged (set by useEffect) — wrap it
        const prevHandler = synth.onvoiceschanged;
        synth.onvoiceschanged = () => {
            if (prevHandler) prevHandler();
            pollAndSpeak();
        };

        // Hard fallback: up to 2s if event never fires
        const timeoutMs = (isChrome || isSafari) ? 2000 : 600;
        setTimeout(() => {
            if (!voicesLoaded) {
                voicesLoaded = true;
                // Restore original handler
                synth.onvoiceschanged = prevHandler || null;
                const v = synth.getVoices();
                trySpeak(v.length > 0 ? v : voicesRef.current);
            }
        }, timeoutMs);
    };
    const stopVoice = () => { synthRef.current?.cancel(); stopChromePauseFix(); setVoicePlaying(false); setVoiceSection(null); };
    const handleVoiceFull = () => speakText(buildFullReport(lang), 'full');
    const handleVoiceSection = sec => speakText(buildSectionText(sec, lang), sec);


    // ── Nominatim village/location search ─────────────────────
    const handleSearch = async (q) => {
        const query = q || searchQuery;
        if (!query.trim()) return;
        setSearchLoading(true); setSearchResults([]);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=in`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await res.json();
            setSearchResults(data || []);
        } catch (e) {
            console.warn('Search failed:', e);
        } finally {
            setSearchLoading(false);
        }
    };

    const selectSearchResult = (result) => {
        const la = parseFloat(result.lat).toFixed(6);
        const lo = parseFloat(result.lon).toFixed(6);
        setLat(la); setLon(lo);
        setSearchResults([]); setSearchQuery('');
        // Map will auto-fly via useEffect on lat/lon change
    };

    // ── Polygon area calculation (Shoelace + spherical correction) ──────
    // Input: [[lat,lon], [lat,lon], ...] — same format as boundary state
    const calcPolygonAreaSqm = (coords) => {
        if (!coords || coords.length < 3) return 0;
        // Spherical Shoelace formula — accurate enough for farm-scale polygons
        const toRad = d => d * Math.PI / 180;
        const R = 6371000; // Earth radius metres
        let area = 0;
        const n = coords.length;
        for (let i = 0; i < n; i++) {
            const [lat1, lon1] = coords[i];
            const [lat2, lon2] = coords[(i + 1) % n];
            area += toRad(lon2 - lon1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
        }
        return Math.abs(area * R * R / 2);
    };

    // Convert sqm to user-friendly display string
    const sqmToDisplay = (sqm) => {
        if (!sqm || sqm <= 0) return null;
        const ac = sqm / 4047.0;
        const gt = sqm / 101.17;
        const ha = sqm / 10000.0;
        // Display values always in English for input fields;
        // localized for display-only drawn area panel
        const _d = (v, dec) => lang !== 'en' ? localNum(v.toFixed(dec), lang) : v.toFixed(dec);
        const _di = (v) => lang !== 'en' ? localNum(Math.round(v).toLocaleString(), lang) : Math.round(v).toLocaleString();
        const acres_str = ac < 0.1 ? null : `${_d(ac, 2)}`;
        const guntha_str = gt < 1 ? null : `${_d(gt, 1)}`;
        const ha_str = ha < 0.1 ? null : `${_d(ha, 3)} Ha`;
        const sqm_str = `${_di(sqm)}`;
        return { ac, gt, ha, sqm, acres_str, guntha_str, ha_str, sqm_str };
    };

    // ── Load scan history from localStorage on mount ───────────
    useEffect(() => {
        try {
            const saved = localStorage.getItem('bhuhit_scan_history');
            if (saved) setScanHistory(JSON.parse(saved));
        } catch (_) { }
    }, []);

    // ── Body class: activates body.bhuhit-dashboard-active CSS rules ──
    // Adds class on mount → removes on unmount so no CSS bleeds to other pages
    useEffect(() => {
        document.body.classList.add('bhuhit-dashboard-active');
        return () => {
            document.body.classList.remove('bhuhit-dashboard-active');
            // Clean up Chrome TTS pause-fix interval on unmount
            if (chromePauseFixRef.current) clearInterval(chromePauseFixRef.current);
        };
    }, []);

    // ── Load farm boundary from sessionStorage (set by SearchPlant.scanFarm) ──
    // ── Load farm boundary from sessionStorage (set by SearchPlant.scanFarm) ──
    // Handles BOTH formats DB sends:
    //   [{lat:10.93, lng:78.11}, ...]   — JSON objects (your current DB)
    //   [[78.11, 10.93], ...]           — [lon,lat] arrays (older format)
    // Also fixes typo longitudes like 778.xx → 78.xx
    useEffect(() => {
        if (!_ssBndry) return;
        try {
            const raw = JSON.parse(_ssBndry);
            if (!Array.isArray(raw) || raw.length < 3) return;
            const coords = raw.map(c => {
                let lat, lon;
                if (c && typeof c === 'object' && !Array.isArray(c)) {
                    // Format: {lat, lng} objects — your DB format
                    lat = parseFloat(c.lat ?? c.latitude ?? 0);
                    lon = parseFloat(c.lng ?? c.lon ?? c.longitude ?? 0);
                } else if (Array.isArray(c) && c.length >= 2) {
                    // Format: [lon, lat] arrays
                    lat = parseFloat(c[1]);
                    lon = parseFloat(c[0]);
                } else { return null; }
                // Fix typo lon like 778.115 → 78.115
                if (lon > 180) lon = parseFloat(String(lon).replace(/^7+8/, '78'));
                if (isNaN(lat) || isNaN(lon)) return null;
                return [lat, lon];
            }).filter(Boolean);
            if (coords.length >= 3) {
                setBoundary(coords);
                setIsUserDrawn(true);
                const sqm = calcPolygonAreaSqm(coords);
                if (sqm > 0) setDrawnAreaSqm(sqm);
            }
        } catch (_) { /* invalid JSON — auto-generate from lat/lon/area */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ── Auto-scan: fires the instant boundary is ready — NO delay needed ───
    // Pattern: watch boundary state. When scanfarmlat exists in sessionStorage
    // AND boundary becomes a valid polygon (≥3 points), fire once immediately.
    // This is instant — no setTimeout, no guessing how long parsing takes.
    const autoScanFired = useRef(false);
    useEffect(() => {
        if (autoScanFired.current) return;             // already fired
        if (!_ssLat || !_ssLon) return;               // no farm from SearchPlant
        if (!boundary || boundary.length < 3) return; // boundary not ready yet — wait
        autoScanFired.current = true;
        handleAnalyze();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundary]);  // re-runs every time boundary changes — fires once when ready

    // ── Auto-boundary ──────────────────────────────────────────
    // CRITICAL: Only run when NO farm polygon exists from sessionStorage.
    // If _ssBndry has a polygon, the boundary useEffect above already
    // set the real farm polygon — do NOT override it with a square.
    useEffect(() => {
        if (!lat || !lon || !acres) return;
        // Skip: farm polygon already loaded from DB/sessionStorage
        if (_ssBndry) return;
        const v = parseFloat(acres);
        const m2 = unit === 'acres' ? v * 4047 : unit === 'guntha' ? v * 101.17 : v;
        const side = Math.sqrt(m2 * 0.75);
        const lr = (parseFloat(lat) * Math.PI) / 180;
        const dLat = (side / 2) / 111320;
        const dLon = (side / 2) / (111320 * Math.cos(lr));
        setIsUserDrawn(false);
        setBoundary([
            [+lat + dLat, +lon - dLon],
            [+lat + dLat, +lon + dLon],
            [+lat - dLat, +lon + dLon],
            [+lat - dLat, +lon - dLon],
        ]);
    }, [lat, lon, acres, unit]);

    useEffect(() => {
        if (polyRef.current?.pm) polyRef.current.pm.enable({ allowSelfIntersection: false });
    }, [boundary]);

    // Recalculate polygon area whenever boundary changes
    useEffect(() => {
        if (!boundary || boundary.length < 3) { setDrawnAreaSqm(null); return; }
        const sqm = calcPolygonAreaSqm(boundary);
        setDrawnAreaSqm(sqm > 0 ? sqm : null);
    }, [boundary]);

    // ── API call ───────────────────────────────────────────────
    const scanInFlight = useRef(false);   // [PERF] blocks duplicate parallel scans
    const handleAnalyze = async () => {
        if (scanInFlight.current) return;  // autoScan + button click can overlap
        scanInFlight.current = true;
        setLoading(true); setError(null);
        try {
            // Guard: boundary may be null if autoScan fires before boundary useEffect
            if (!boundary || boundary.length < 3) {
                setError('Farm boundary not ready. Please wait a moment and try again.');
                scanInFlight.current = false;
                setLoading(false);
                return;
            }
            // ── Convert any Indic digits to ASCII before sending to API ──
            // Users may paste/type native digits; the API always expects English numbers.
            const toEng = (str) => {
                if (!str) return str;
                return String(str)
                    // Devanagari (Marathi/Hindi): ०-९
                    .replace(/[०-९]/g, d => '०१२३४५६७८९'.indexOf(d))
                    // Telugu: ౦-౯
                    .replace(/[౦-౯]/g, d => '౦౧౨౩౪౫౬౭౮౯'.indexOf(d));
            };
            const safeFloat = (v) => parseFloat(toEng(v));
            const poly = boundary.map(c => [parseFloat(c[1]), parseFloat(c[0])]);
            poly.push(poly[0]);
            const API_BASE = process.env.REACT_APP_API_URL || 'https://bhuhit.com/api';
            const _payload = {
                latitude: safeFloat(lat),
                longitude: safeFloat(lon),
                startDate: toEng(startDate),
                endDate: toEng(endDate),
                size_val: safeFloat(acres),
                unit,
                user_polygon: poly, task: 'analyze',
                crop_name_hint: cropsName.trim() || null,
            };
            // [PERF] serve repeat scans from memory; otherwise call the API
            const _ck = JSON.stringify(_payload);
            const _hit = _scanCache.get(_ck);
            let res;
            if (_hit && Date.now() - _hit.t < SCAN_CACHE_TTL_MS) {
                res = { data: _hit.d };
            } else {
                res = await axios.post(`${API_BASE}/Agri/analyze`, _payload);
                if (res.data && res.data.status !== 'ERROR') {
                    _scanCache.set(_ck, { t: Date.now(), d: res.data });
                    if (_scanCache.size > 10) _scanCache.delete(_scanCache.keys().next().value);
                }
            }

            if (res.data) {
                console.log('[Bhuhit] Scan result received:', res.data?.status, '| Health:', res.data?.stats?.health_Metric, '| Crop:', res.data?.crop_Identification?.crop_Primary);
                const _st = res.data?.stats || {};
                const _forecastKeys = Object.keys(_st).filter(k => k.toLowerCase().includes('forecast') || k.toLowerCase().includes('temp') || k.toLowerCase().includes('rain') || k.toLowerCase().includes('precip'));
                console.log('[Bhuhit] Forecast keys in stats:', _forecastKeys);
                console.log('[Bhuhit] forecast_7day_rain_mm:', _st.forecast_7day_rain_mm, '| forecast_7day_temp_max:', _st.forecast_7day_temp_max, '| forecast_7day_temp_min:', _st.forecast_7day_temp_min);
                console.log('[Bhuhit] weather_model_used:', _st.weather_model_used, '| top-level forecast keys:', Object.keys(res.data || {}).filter(k => k.toLowerCase().includes('forecast')));
                setData(res.data);
                // Scroll sidebar to crop results after scan
                setTimeout(() => {
                    const el = document.getElementById('crop-id-anchor');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
                // ── Save to scan history ─────────────────────
                const scanEntry = {
                    id: Date.now(),
                    timestamp: new Date().toISOString(),
                    lat: +lat, lon: +lon,
                    acres: +acres, unit,
                    // [FIX — found via live testing] boundary was never saved,
                    // so restoreScan() could only bring back acres/unit (a flat
                    // number) while the "Drawn Area" box kept showing whatever
                    // polygon was already on screen from a DIFFERENT farm —
                    // a real, visible Farm Size vs Drawn Area mismatch.
                    boundary: boundary || null,
                    cropName: cropsName.trim() || null,
                    startDate, endDate,
                    // Summary fields for history card
                    result: {
                        /*crop: res.data?.crop_Identification?.crop_Primary || res.data?.Detected_Crop || cropName || 'Unknown',*/
                        ndvi: res.data?.stats?.health_Metric,
                        orgScore: res.data?.organic_Alert?.organic_Score,
                        moisture: res.data?.stats?.soil_Moisture_Percent,
                        alerts: 0, // will compute below
                    }
                };
                // Count alerts
                const alertCount = (res.data.alerts?.length || 0)
                    + (res.data.flood?.flood_Detected ? 1 : 0)
                    + (res.data.health?.stress_Types?.length || 0);
                scanEntry.result.alerts = alertCount;

                setScanHistory(prev => {
                    const updated = [scanEntry, ...prev].slice(0, 10); // keep last 10
                    try { localStorage.setItem('bhuhit_scan_history', JSON.stringify(updated)); } catch (_) { }
                    return updated;
                });
            } else throw new Error('No data received.');
        } catch (e) {
            const errMsg = e.response?.data?.message
                || e.response?.data?.details
                || (e.code === 'ERR_NETWORK' ? 'Cannot connect to server. Check API is running on correct port.' : null)
                || e.message
                || 'Scan failed';
            setError("Please click on button'SMART FARM SCAN'");
            console.error('[Bhuhit Scan Error]', errMsg);
            //return handleAnalyze();
        } finally {
            scanInFlight.current = false;
            setLoading(false);
        }
    };

    // ── Restore a past scan ───────────────────────────────────
    const restoreScan = (entry) => {
        setLat(entry.lat);
        setLon(entry.lon);
        setAcres(entry.acres);
        setUnit(entry.unit);
        // [FIX] was entry.cropsName, which never existed on the saved
        // record (saved as entry.cropName) — crop name silently reset to
        // blank on every restore.
        setCropName(entry.cropName || '');
        setStartDate(entry.startDate);
        setEndDate(entry.endDate);
        // [FIX — found via live testing] Restore the actual drawn polygon
        // (now saved in scanEntry.boundary) and recompute its area, using
        // the SAME helper the live-drawing flow uses — so "Drawn Area"
        // always matches the restored Farm Size instead of showing a
        // stale polygon left over from a different farm.
        if (Array.isArray(entry.boundary) && entry.boundary.length >= 3) {
            setBoundary(entry.boundary);
            setIsUserDrawn(true);
            const sqm = calcPolygonAreaSqm(entry.boundary);
            setDrawnAreaSqm(sqm > 0 ? sqm : null);
        } else {
            // Older history entries saved before this fix have no boundary
            // — clear any stale polygon rather than leave a mismatched one
            // on screen, and fall back to the flat acres/unit value only.
            setBoundary(null);
            setIsUserDrawn(false);
            setDrawnAreaSqm(null);
        }
        setShowHistory(false);
        // Optionally re-trigger scan automatically:
        // handleAnalyze();
    };

    // ── Delete one scan from history ──────────────────────────
    const deleteScan = (id, e) => {
        e.stopPropagation();
        setScanHistory(prev => {
            const updated = prev.filter(s => s.id !== id);
            try { localStorage.setItem('bhuhit_scan_history', JSON.stringify(updated)); } catch (_) { }
            return updated;
        });
    };

    // ── Clear all history ─────────────────────────────────────
    const clearHistory = () => {
        setScanHistory([]);
        try { localStorage.removeItem('bhuhit_scan_history'); } catch (_) { }
    };

    // ── Derived values ─────────────────────────────────────────
    const s = data?.stats || {};

    // [FIX — Sugarcane/long-duration-crop trend window] Dynamic label
    // reflecting the ACTUAL Start-to-End Date span, instead of a hardcoded
    // "3-MONTH" shown for every crop regardless of the real window.
    const trendWindowLabel = (() => {
        try {
            const days = Math.round((new Date(endDate) - new Date(startDate)) / 864e5);
            if (!Number.isFinite(days) || days <= 0) return t.sixMonth;
            const months = Math.round(days / 30);
            if (months <= 1) return lang === 'en' ? '1-MONTH' : t.sixMonth.replace(/\d+/, '1');
            const numStr = lang === 'en' ? String(months) : localNum(String(months), lang);
            return t.sixMonth.replace(/[\d०-९]+/, numStr);
        } catch (_) { return t.sixMonth; }
    })();
    const ndvi = s.health_Metric;
    const surf = s.soil_Moisture_Percent;
    const rootZ = s.root_Zone_Moisture;
    // Crop display: user-provided name takes priority over engine detection
    // Engine detection shown as secondary (next stage: 200-crop model)
    const engineCrop = data?.crop_Identification?.crop_Primary || data?.Detected_Crop || null;
    const cropP = cropsName?.trim() || engineCrop || null;
    const cropIsOverride = cropsName?.trim() && engineCrop && cropsName.trim().toLowerCase() !== engineCrop.toLowerCase();
    const cropConf = data?.crop_Identification?.crop_Confidence ?? data?.confidence_Score ?? null;
    // Localized crop name for display — use translated name from API if available
    const _cropLocal = () => resolveCropName(cropP, data?.crop_Identification, lang);
    const cropPLocal = _cropLocal();
    const orgScore = data?.organic_Alert?.organic_Score;
    const orgGrade = data?.organic_Alert?.organic_Grade?.split('—')[0]?.trim();
    const orgColor = orgScore >= 80 ? '#39FF14' : orgScore >= 60 ? '#FFB800' : '#FF4A6E';

    const nLabel = (idx, l = lang) => {
        if (!idx) return '—';
        const lvl = idx < 0.35 ? 'LOW' : idx > 0.65 ? 'HIGH' : 'OPT';
        const lvlTrans = {
            LOW: { mr: 'कमी', hi: 'कम', te: 'తక్కువ', en: 'LOW' },
            OPT: { mr: 'योग्य', hi: 'उचित', te: 'సరైన', en: 'OPT' },
            HIGH: { mr: 'जास्त', hi: 'अधिक', te: 'అధికం', en: 'HIGH' },
        };
        const lvlLabel = lvlTrans[lvl][l] || lvlTrans[lvl].en;
        const numStr = l !== 'en' ? localNum(idx.toFixed(2), l) : idx.toFixed(2);
        return `${lvlLabel} (${numStr})`;
    };
    const nitColor = idx => !idx ? '#34D399' : idx < 0.35 ? '#FF4A6E' : idx > 0.65 ? '#FFB800' : '#34D399';

    // ── Build alerts with deduplication ──────────────────────
    const alerts = (() => {
        if (!data) return [];
        const seen = new Set();
        const L = [];
        const add = (obj) => {
            // Use _transKey as dedup key if present (avoids issues with non-ASCII messages)
            const key = obj._transKey
                ? `${obj.type}_${obj._transKey}`
                : (obj.message || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
            if (seen.has(key)) return;
            seen.add(key);
            L.push(obj);
        };

        // 1. Engine top-level alerts — assign _transKey for known types so they translate
        (data.alerts || []).forEach(a => {
            const type = a.type || 'default';
            let transKey = null, transVars = {};
            if (type === 'flood') {
                transKey = 'flood';
                const m = (a.message || '').match(/SAR VV[:\s]+([-.0-9]+)/i);
                transVars = { sarDb: m?.[1] ?? 'N/A' };
            } else if (type === 'drought') {
                transKey = 'drought';
                const m = (a.message || '').match(/VCI[:\s]+([0-9.]+)/i);
                transVars = { vci: m?.[1] ?? 'N/A' };
            } else if (type === 'organic') {
                transKey = 'organic';
                transVars = { orgScore: data.organic_Alert?.organic_Score, fc: data.organic_Alert?.fail_Count };
            } else if (type === 'disease') {
                transKey = 'disease';
                transVars = { dp: data.disease_Pest_Alert };
            }
            add({
                type,
                message: a.message || '',
                severity: a.severity || (type === 'flood' ? 'high' : 'medium'),
                ...(transKey ? { _transKey: transKey, _vars: transVars } : {})
            });
        });

        // 2. Health stress types (often duplicates data.alerts — deduped by seen Set)
        (data.health?.stress_Types || []).forEach(msg => add({
            type: 'health', message: msg, severity: 'medium'
        }));

        // 3. Flood — NDWI cross-check reduces false positives from wet soil
        const _ndwiFlood = s.ndwi ?? -99;
        if (data.flood?.flood_Detected && (_ndwiFlood > 0.05 || _ndwiFlood === -99)) add({
            type: 'flood',
            message: `Flood detected in farm area — SAR VV: ${data?.flood?.sar_VV_Flood_dB?.toFixed(1) ?? 'N/A'} dB`,
            severity: 'high',
            _transKey: 'flood',
            _vars: { sarDb: data?.flood?.sar_VV_Flood_dB?.toFixed(1) ?? 'N/A' }
        });

        // 4. Drought
        const dl = data.drought?.drought_Level;
        if (dl && !['Normal', 'N/A', 'No MODIS Data', 'None'].includes(dl)) add({
            type: 'drought',
            message: `${dl} — VCI: ${data?.drought?.vci?.toFixed(1) ?? 'N/A'}`,
            severity: 'high',
            _transKey: 'drought',
            _vars: { vci: data?.drought?.vci?.toFixed(1) ?? 'N/A' }
        });

        // 5. Disease / Pest — guard: skip if cloud coverage > 40% (false anomaly)
        const dp = data?.disease_Pest_Alert;
        const _cloudFrac = s.cloud_Coverage_Fraction ?? 0;
        const _anomalyValid = dp?.ndvi_Anomaly != null && Math.abs(dp.ndvi_Anomaly) > 0.05 && _cloudFrac < 0.4;
        if (dp?.disease_Pest_Risk &&
            !['None Detected', 'Insufficient baseline data', 'None'].includes(dp.disease_Pest_Risk) &&
            _anomalyValid) {
            add({
                type: 'disease',
                message: `Disease/Pest: ${dp.disease_Pest_Risk} — Crop health drop ${dp.ndvi_Anomaly?.toFixed(3) ?? 'N/A'}`,
                severity: dp.disease_Pest_Risk === 'Severe' ? 'high' : 'medium',
                _transKey: 'disease',
                _vars: { dp, ndvi: s.health_Metric }
            });
            (dp.alert_Flags || []).forEach(f => add({ type: 'disease', message: `${f.flag}: ${f.action}`, severity: 'medium' }));
        }

        // 6. Engine irrigation flag
        if (s.root_Zone_Irrigation_Flag) add({
            type: 'health',
            message: `Irrigation needed — root zone at ${rootZ?.toFixed(1)}%`,
            severity: 'medium',
            _transKey: 'irr',
            _vars: { rootZ }
        });

        // 7. Organic fails
        const fc = data.organic_Alert?.fail_Count || 0;
        if (fc > 0) {
            add({
                type: 'organic',
                message: `Organic ${orgScore?.toFixed(1)} — ${fc} criteria failed`,
                severity: fc > 5 ? 'high' : 'low',
                _transKey: 'organic',
                _vars: { orgScore, fc }
            });
            // NOTE: raw top_Issues (English criteria strings) are NOT added here —
            // they are already displayed translated in the Organic Goals section below.
        }

        // ── Farm Advisory Alerts (computed from existing data) ──
        const ndviV = s.health_Metric;
        const crop = engineCrop || cropsName || 'next crop';

        if (ndviV != null && ndviV < 0.2 && ndviV >= 0)
            add({
                type: 'advisory',
                message: `Sowing window: Crop health score ${ndviV.toFixed(2)} — field bare/prepared. Ideal to sow ${crop}.`,
                severity: 'info',
                _transKey: 'sowing',
                _vars: { ndvi: ndviV, crop }
            });

        if (ndviV != null && ndviV >= 0.3 && ndviV <= 0.65 && (s.humidity || 0) > 60)
            add({
                type: 'advisory',
                message: `Organic spraying advised: humidity ${Math.round(s.humidity)}% — high disease pressure. Apply neem oil / bio-fungicide.`,
                severity: 'medium',
                _transKey: 'spray',
                _vars: { moisture: Math.round(s.humidity) }
            });

        if (ndviV != null && ndviV > 0.7 && (s.evi || 0) > 0.5)
            add({
                type: 'advisory',
                message: `Weed/grass cutting due: dense crop canopy (health ${ndviV.toFixed(2)}, EVI ${s.evi.toFixed(2)}). Clear bunds to reduce pest habitat.`,
                severity: 'low',
                _transKey: 'weed',
                _vars: { ndvi: ndviV, evi: s.evi }
            });

        const _calEntry2 = (typeof CROP_CALENDAR !== 'undefined' && CROP_CALENDAR)
            ? (CROP_CALENDAR[engineCrop] || CROP_CALENDAR['Unknown'] || {})
            : {};
        const _moistLow = _calEntry2.alertThresholds?.moisture_low ?? 15;
        if (rootZ != null && rootZ < _moistLow && !s.root_Zone_Irrigation_Flag)
            add({
                type: 'health',
                message: `Irrigation critical: root zone at ${rootZ.toFixed(1)}% — schedule within 24–48 hrs.`,
                severity: 'high',
                _transKey: 'irr_crit',
                _vars: { rootZ }
            });

        return L;
    })();

    const highCnt = alerts.filter(a => a.severity === 'high').length;

    // ════════════════════════════════════════════════════════════
    // CHART DATA
    // ════════════════════════════════════════════════════════════

    // 1. NDVI Trend
    const ndviRaw = s.temporal_NDVI || s.temporalNDVI || [];
    // [REMOVED] The June-28-2026 hardcoded chart cutoff has been removed.
    // It was added earlier when the engine had a genuine bug truncating
    // NDVI data early; now that the engine (V5.4.0) correctly returns
    // data through the current month, the chart should show everything
    // the engine sends rather than artificially hiding recent points.
    // Filter: keep valid NDVI points. Allow slightly negative (water bodies show -0.5 to -0.1)
    // Only exclude completely invalid/missing data
    const ndviValid = ndviRaw.filter(d => d?.ndvi != null && d.ndvi > -1.0);
    // Separate healthy vs stressed for coloring
    const ndviHasVeg = ndviValid.some(d => d.ndvi >= 0.2);
    const ndviValues = ndviValid.map(d => d.ndvi);
    const averageNDVI = ndviValues.reduce((sum, value) => sum + value, 0) / ndviValues.length;
    //const averageEVI2 = averageNDVI * 0.90;
    //const averageCropGrowth = averageEVI2;          // 0.5805
    //const averageCropStress = 1 - averageEVI2;      // 0.4195
    //const cropGrowthPercentage = averageEVI2 * 100;           // 58.05%
    //const cropStressPercentage = (1 - averageEVI2) * 100;     // 41.95%
    //const cropMoisturePercentage = averageNDVI * 75;
    //const averageCropNitrogen = averageNDVI * 35;

    // [FIX P1b] Single source of truth for the 4 "current date" metric boxes —
    // always the LATEST AVAILABLE dated reading, never a multi-month average.
    // IMPORTANT: the engine's "current scan" fields (health_Metric, hls_*)
    // are NOT always actually the most recent date — get_best_imagery_recursive
    // can land on an older clear scene while the NDVI time series already has
    // a newer point (e.g. 24 Jul). Previously this code trusted health_Metric
    // whenever it was non-null, even if it was older than the latest history
    // point — that's exactly why the sidebar showed 0.22 while the trend
    // chart correctly showed 0.44 for 24 Jul. Fix: compare dates explicitly
    // and use whichever source is genuinely the most recent; never mix an
    // older engine value with a newer history date or vice-versa.
    //
    // [FIX P2] hls_CROPGROWTH/hls_NDMI/hls_CROPSTRESS come from a SEPARATE
    // satellite search (HLS S30) than health_Metric's optical stream, so
    // they can legitimately have a different scene date. The engine now
    // returns that real date as `hls_Scene_Date` — use it for THIS group's
    // comparison instead of assuming it shares health_Metric's image_Date.
    const sortedNdvi = [...ndviValid].sort((a, b) => new Date(a.date) - new Date(b.date));
    const latestPoint = sortedNdvi.length ? sortedNdvi[sortedNdvi.length - 1] : null;
    const latestNDVI = latestPoint ? latestPoint.ndvi : null;
    const latestDate = latestPoint ? latestPoint.date : null;
    const latestEVI2 = latestNDVI != null ? latestNDVI * 0.90 : null;
    const latestStressPct = latestEVI2 != null ? (1 - latestEVI2) * 100 : null;

    const engineScanDate = s.image_Date || s.scan_Date || null;   // optical stream (health_Metric)
    const hlsScanDate = s.hls_Scene_Date || engineScanDate;    // HLS stream (growth/moisture/stress)

    const _isNewer = (candidateDateStr, baselineDateStr) => {
        const c = candidateDateStr ? new Date(candidateDateStr).getTime() : -Infinity;
        const b = baselineDateStr ? new Date(baselineDateStr).getTime() : -Infinity;
        return c > b;
    };

    const healthHistoryIsNewer = _isNewer(latestDate, engineScanDate) && latestNDVI != null;
    const curCropHealth = healthHistoryIsNewer ? latestNDVI : (s.health_Metric ?? latestNDVI);
    const curCropHealthDate = healthHistoryIsNewer ? latestDate : (s.health_Metric != null ? engineScanDate : latestDate);

    // [FIX] PRIMARY source for Growth/Moisture/Stress: the engine's new
    // same-date bundle (latest_CropGrowth/latest_CropMoisture/
    // latest_CropStress_Value), derived from the EXACT SAME best-recent
    // image as health_Metric — no separate HLS search, so no date-mismatch
    // is even possible. Falls back to the old hls_*-based per-field
    // date-comparison logic only if an older engine response lacks these
    // fields (e.g. before this fix is deployed).
    let curCropGrowth, curCropGrowthDate;
    let curCropStressValue, curCropStressPctForBar, curCropStressDate;
    let curCropMoisture, curCropMoistureDate;

    const hasLatestBundle = s.latest_CropGrowth != null || s.latest_CropMoisture != null || s.latest_CropStress_Value != null;

    if (hasLatestBundle) {
        // All three share ONE real date by construction — no per-field
        // comparison needed, and no risk of a mismatched-date blend.
        const bundleDate = s.latest_Reading_Date || engineScanDate;
        curCropGrowth = s.latest_CropGrowth != null ? s.latest_CropGrowth / 100 : latestEVI2;
        curCropGrowthDate = s.latest_CropGrowth != null ? bundleDate : latestDate;
        curCropStressValue = s.latest_CropStress_Value != null ? s.latest_CropStress_Value : (latestEVI2 != null ? (1 - latestEVI2) : null);
        curCropStressPctForBar = s.latest_CropStress_Pct != null ? s.latest_CropStress_Pct : (curCropStressValue != null ? curCropStressValue * 100 : null);
        curCropStressDate = s.latest_CropStress_Value != null ? bundleDate : latestDate;
        curCropMoisture = s.hls_NDMI; //s.latest_CropMoisture != null ? s.latest_CropMoisture : (s.hls_NDMI != null ? s.hls_NDMI : (s.ndmi != null ? s.ndmi : null)); // null is honest ("—"), not faked
        curCropMoistureDate = bundleDate;
    } else {
        const hlsHistoryIsNewer = _isNewer(latestDate, hlsScanDate) && latestNDVI != null;
        curCropGrowth = hlsHistoryIsNewer ? latestEVI2 : (s.hls_CROPGROWTH != null ? s.hls_CROPGROWTH / 100 : latestEVI2);
        curCropGrowthDate = hlsHistoryIsNewer ? latestDate : (s.hls_CROPGROWTH != null ? hlsScanDate : latestDate);
        curCropStressValue = hlsHistoryIsNewer ? (latestEVI2 != null ? (1 - latestEVI2) : null) : (s.hls_CROPSTRESS != null ? s.hls_CROPSTRESS / 100 : (latestEVI2 != null ? (1 - latestEVI2) : null));
        curCropStressPctForBar = curCropStressValue != null ? curCropStressValue * 100 : null;
        curCropStressDate = hlsHistoryIsNewer ? latestDate : (s.hls_CROPSTRESS != null ? hlsScanDate : latestDate);
        curCropMoisture = hlsHistoryIsNewer ? null : (s.hls_NDMI != null ? s.hls_NDMI : (s.ndmi != null ? s.ndmi : null));
        curCropMoistureDate = hlsHistoryIsNewer ? latestDate : hlsScanDate;
    }

    // One representative "as of" date for the metrics-grid header.
    const metricsAsOfDate = curCropHealthDate;
    const _fmtAsOf = (d) => {
        if (!d) return null;
        const locale = lang === 'mr' ? 'mr-IN' : lang === 'hi' ? 'hi-IN' : lang === 'te' ? 'te-IN' : 'en-IN';
        try { return new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }); }
        catch (_) { return d; }
    };

    // alert(averageNDVI);
    const ndviData = {
        labels: ndviValid.map(d => {
            const locale = lang === 'mr' ? 'mr-IN' : lang === 'hi' ? 'hi-IN' : lang === 'te' ? 'te-IN' : 'en-IN';
            return new Date(d.date).toLocaleString(locale, { month: 'short', day: 'numeric' });
        }),
        datasets: [{
            label: t.ndvi,
            data: ndviValid.map(d => d.ndvi),
            borderColor: ndviHasVeg ? '#39FF14' : '#00D4FF',
            backgroundColor: ndviHasVeg ? 'rgba(57,255,20,0.07)' : 'rgba(0,212,255,0.07)',
            fill: true, tension: 0.42, pointHoverRadius: 5,
            borderWidth: 1.8,
            // [V5.4.0] Estimate points (SAR-derived, filling monsoon cloud gaps)
            // get a distinct triangle marker + purple border so they're never
            // visually confused with real optical NDVI readings.
            pointRadius: ndviValid.map(d => d.is_Estimate ? 5 : 2),
            pointStyle: ndviValid.map(d => d.is_Estimate ? 'triangle' : 'circle'),
            pointBorderColor: ndviValid.map(d => d.is_Estimate ? '#9B59B6' : 'transparent'),
            pointBorderWidth: ndviValid.map(d => d.is_Estimate ? 2 : 0),
            pointBackgroundColor: ndviValid.map(d =>
                d.is_Estimate ? '#C39BD3'
                    : (d.ndvi >= 0.5 ? '#39FF14' : d.ndvi >= 0.2 ? '#FFB800' : '#FF4A6E')
            ),
        }]
    };
    // Auto-scale Y: if water body, allow negatives; if crop, show 0–1
    const ndviMin = ndviHasVeg ? 0.0 : Math.min(-0.6, ...(ndviValid.map(d => d.ndvi)));
    const ndviMax = 1.0;
    const ndviOpts = mkChart({
        scales: {
            x: { ticks: { color: '#b8d0e8', font: { size: 13 }, maxRotation: 45 }, grid: { color: 'rgba(255,255,255,0.04)' } },
            y: {
                min: Math.floor(ndviMin * 10) / 10,
                max: ndviMax,
                ticks: { color: '#b8d0e8', font: { size: 13 }, callback: v => lang !== 'en' ? localNum(v.toFixed(1), lang) : v.toFixed(1) },
                grid: {
                    color: ctx => ctx.tick.value === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.04)'
                }
            },
        },
        plugins: {
            ...mkChart().plugins,
            tooltip: {
                ...mkChart().plugins.tooltip,
                callbacks: {
                    label: ctx => {
                        const v = ctx.parsed.y?.toFixed(3);
                        const locV = lang !== 'en' ? localNum(v, lang) : v;
                        const status = ctx.parsed.y >= 0.5 ? (lang === 'mr' ? 'निरोगी' : lang === 'hi' ? 'स्वस्थ' : lang === 'te' ? 'ఆరోగ్యకరం' : 'Healthy')
                            : ctx.parsed.y >= 0.2 ? (lang === 'mr' ? 'मध्यम' : lang === 'hi' ? 'मध्यम' : lang === 'te' ? 'మధ్యస్థం' : 'Moderate')
                                : (lang === 'mr' ? 'ताण' : lang === 'hi' ? 'तनाव' : lang === 'te' ? 'ఒత్తిడి' : 'Stressed');
                        return ` ${ctx.dataset.label}: ${locV} — ${status}`;
                    },
                    // [V5.3.5/V5.4.0] Extra tooltip line showing which satellite source(s) fed
                    // this point. IMPORTANT: a single-source day is completely normal — even in
                    // dry season, on any given date only one of the 3 optical streams may have
                    // had a clear pass while the others didn't. That is NOT a "gap" being
                    // "filled" — it's routine coverage. The word "gap-fill" is reserved ONLY for
                    // genuine rescue-tier points (SAR-derived estimate, or the Tier-3 soft-cloud
                    // rescue source) — never for an everyday single-satellite optical reading.
                    afterLabel: ctx => {
                        const pt = ndviValid[ctx.dataIndex];
                        if (!pt) return '';
                        // [V5.4.0] SAR-derived estimate points get their own explanatory line
                        if (pt.is_Estimate) {
                            const label = lang === 'mr' ? '\u26a0 उपग्रह अंदाज (ढगांमुळे) \u2014 SAR रडार आधारित'
                                : lang === 'hi' ? '\u26a0 उपग्रह अनुमान (बादलों के कारण) \u2014 SAR रडार आधारित'
                                    : lang === 'te' ? '\u26a0 ఉపగ్రహ అంచనా (మేఘాల వల్ల) \u2014 SAR రాడార్ ఆధారితం'
                                        : '\u26a0 Satellite estimate (cloud gap) \u2014 SAR radar-based, not optical';
                            return ` ${label}`;
                        }
                        if (!pt.sources_Used || pt.sources_Used.length === 0) return '';
                        const srcLabel = pt.sources_Used.join(' + ');
                        const isRescueTier = pt.sources_Used.includes('S2-soft-cloud-weighted');
                        const crossValidated = (pt.n_Points || pt.sources_Used.length) > 1;
                        if (crossValidated) {
                            const tag = lang === 'mr' ? 'तपासलेले' : lang === 'hi' ? 'सत्यापित'
                                : lang === 'te' ? 'ధృవీకరించబడింది' : 'cross-validated';
                            return ` ${srcLabel} (${tag})`;
                        }
                        if (isRescueTier) {
                            const tag = lang === 'mr' ? 'भरलेले (ढग असूनही)' : lang === 'hi' ? 'भरा गया (बादलों के बावजूद)'
                                : lang === 'te' ? 'పూరించబడింది (మేఘాలున్నా)' : 'rescued (cloud-probability weighted)';
                            return ` ${srcLabel} (${tag})`;
                        }
                        // Ordinary single-source day — neutral wording, no "gap"/"filled" implication
                        return ` ${srcLabel}`;
                    }
                }
            }
        }
    });

    // 2. Soil Moisture
    const moistData = {
        labels: [t.surface05, t.rootZone0100],
        datasets: [{
            label: t.moistureLabel,
            data: [surf || 0, rootZ || 0],
            backgroundColor: ['rgba(0,212,255,0.55)', 'rgba(56,189,248,0.45)'],
            borderColor: ['#00D4FF', '#38BDF8'], borderWidth: 1.5, borderRadius: 5,
        }]
    };
    const moistOpts = mkChart({
        scales: {
            x: { ticks: { color: '#b8d0e8', font: { size: 13 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
            y: {
                min: 0, max: 100,
                ticks: { color: '#b8d0e8', font: { size: 13 }, callback: v => `${lang !== 'en' ? localNum(String(v), lang) : v}%` },
                grid: { color: 'rgba(255,255,255,0.04)' }
            },
        },
        plugins: {
            ...mkChart().plugins,
            tooltip: {
                ...mkChart().plugins.tooltip,
                callbacks: {
                    label: ctx => {
                        const v = ctx.parsed.y?.toFixed(1);
                        const locV = lang !== 'en' ? localNum(v, lang) : v;
                        return ` ${ctx.dataset.label}: ${locV}%`;
                    }
                }
            }
        }
    });

    // 3. 7-Day Forecast [V5.3.0 — extended from 3-day]
    // Check stats block first, then top-level data keys
    // FIX: empty array [] is truthy in JS — use .length check, not || fallback
    const _pick = (...arrs) => arrs.find(a => Array.isArray(a) && a.length > 0) || [];
    // ── 7-Day Forecast — Step 1: extract raw arrays ([] is truthy, use _pick) ──
    const p7raw = _pick(s.forecast_7day_rain_mm, s.forecast_Precip_3day_mm, data?.forecast_7day_rain_mm, data?.forecast_Precip_3day_mm);
    const tmax7raw = _pick(s.forecast_7day_temp_max, s.forecast_Temp_Max, data?.forecast_7day_temp_max, data?.forecast_Temp_Max);
    const tmin7raw = _pick(s.forecast_7day_temp_min, s.forecast_Temp_Min, data?.forecast_7day_temp_min, data?.forecast_Temp_Min);
    // Step 2: fill missing arrays so chart always has data after scan
    const _fLen = Math.max(p7raw.length, tmax7raw.length, tmin7raw.length, data ? 7 : 0);
    const p7 = p7raw.length > 0 ? p7raw : (data ? Array(_fLen).fill(0) : []);
    const tmax7 = tmax7raw.length > 0 ? tmax7raw : (data ? Array(_fLen).fill(null) : []);
    const tmin7 = tmin7raw.length > 0 ? tmin7raw : (data ? Array(_fLen).fill(null) : []);
    // const p3 = p7; const tmax = tmax7; const tmin = tmin7; // removed — aliases unused
    // Step 3: compute axis bounds BEFORE building chart data/options
    const tminSafe = tmin7.filter(v => v != null && !isNaN(v));
    const tmaxSafe = tmax7.filter(v => v != null && !isNaN(v));
    const _tmin = tminSafe.length ? Math.min(...tminSafe) : 15;
    const _tmax = tmaxSafe.length ? Math.max(...tmaxSafe) : 42;
    const _rainVals = p7.map(v => (v != null && !isNaN(v)) ? Number(v) : 0);
    const _rainMax = Math.max(10, ..._rainVals) * 1.4;  // min 10mm so bars visible
    // Step 4: labels — 7 days from today
    const fDays = Math.max(_fLen, data ? 7 : 3, 3);
    const fLabels = Array.from({ length: fDays }, (_, i) => {
        const d = new Date(); d.setDate(d.getDate() + i);
        const locale = lang === 'mr' ? 'mr-IN' : lang === 'hi' ? 'hi-IN' : lang === 'te' ? 'te-IN' : 'en-IN';
        return d.toLocaleString(locale, { weekday: 'short', day: 'numeric' });
    });
    // Step 5: UV index for extra insight row (if available)
    const uvRaw = _pick(s.forecast_uv_index_7day, data?.forecast_uv_index_7day);
    // Step 6: dataset + options
    const fcastData = {
        labels: fLabels,
        datasets: [
            {
                type: 'bar', label: t.rainMm, data: _rainVals, yAxisID: 'y', order: 3,
                // When 0mm: show a very faint ghost bar so axis is visible; real rain = solid blue
                backgroundColor: _rainVals.map(v =>
                    v >= 25 ? 'rgba(0,80,255,0.90)' :
                        v >= 10 ? 'rgba(0,150,255,0.75)' :
                            v >= 2 ? 'rgba(0,212,255,0.50)' :
                                v > 0 ? 'rgba(0,212,255,0.30)' : 'rgba(0,212,255,0.08)'),
                borderColor: _rainVals.map(v => v > 0 ? '#00D4FF' : 'rgba(0,212,255,0.20)'),
                borderWidth: 1, borderRadius: 3,
                minBarLength: 3,  // always render a tiny bar so datalabels can anchor even at 0mm
                datalabels: {
                    display: true,
                    anchor: 'end',
                    align: 'top',
                    offset: 2,
                    clamp: true,
                    color: ctx => (_rainVals[ctx.dataIndex] > 0 ? '#00D4FF' : 'rgba(255,200,50,0.80)'),
                    font: ctx => ({ size: _rainVals[ctx.dataIndex] > 0 ? 10.5 : 13, weight: 'bold' }),
                    formatter: v => v > 0 ? `${lang !== 'en' ? localNum(v.toFixed(0), lang) : v.toFixed(0)}mm` : '☀'
                }
            },
            {
                type: 'line', label: t.maxC, data: tmax7, yAxisID: 'y1', order: 1,
                borderColor: '#FF6B35', backgroundColor: 'rgba(255,107,53,0.05)',
                tension: 0.35, pointRadius: 3, pointBackgroundColor: '#FF6B35',
                pointBorderColor: '#1a1f2e', pointBorderWidth: 1.5, borderWidth: 2.5, fill: false,
                datalabels: {
                    display: true, anchor: 'top', align: 'end', offset: 3,
                    color: '#FF6B35', font: { size: 11, weight: 'bold' },
                    formatter: v => v != null ? `${lang !== 'en' ? localNum(String(Math.round(v)), lang) : Math.round(v)}°` : '',
                    clamp: true
                }
            },
            {
                type: 'line', label: t.minC, data: tmin7, yAxisID: 'y1', order: 2,
                borderColor: '#38BDF8', backgroundColor: 'rgba(56,189,248,0.04)',
                tension: 0.35, pointRadius: 3, pointBackgroundColor: '#38BDF8',
                pointBorderColor: '#1a1f2e', pointBorderWidth: 1.5, borderWidth: 2,
                borderDash: [5, 3], fill: false,
                datalabels: {
                    display: true, anchor: 'bottom', align: 'start', offset: 3,
                    color: '#38BDF8', font: { size: 11, weight: 'bold' },
                    formatter: v => v != null ? `${lang !== 'en' ? localNum(String(Math.round(v)), lang) : Math.round(v)}°` : '',
                    clamp: true
                }
            },
        ]
    };
    const fcastOpts = {
        ...mkChart(),
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 24, bottom: 4, left: 2, right: 12 } },
        plugins: {
            ...mkChart().plugins,
            legend: {
                display: true, position: 'top',
                labels: { color: '#b8d0e8', font: { size: 11 }, boxWidth: 10, padding: 8 }
            },
            tooltip: {
                callbacks: {
                    title: items => fLabels[items[0]?.dataIndex] || '',
                    label: ctx => {
                        const _l = v => lang !== 'en' ? localNum(String(v), lang) : String(v);
                        if (ctx.dataset.label === t.rainMm) return ` 🌧 ${t.tooltipRain}: ${_l((ctx.parsed.y ?? 0).toFixed(1))} mm`;
                        if (ctx.dataset.label === t.maxC) return ` 🌡 ${t.tooltipMax}: ${_l(ctx.parsed.y?.toFixed(1) ?? '—')}°C`;
                        if (ctx.dataset.label === t.minC) return ` 🌡 ${t.tooltipMin}: ${_l(ctx.parsed.y?.toFixed(1) ?? '—')}°C`;
                        return ctx.dataset.label;
                    },
                    afterBody: items => {
                        const i = items[0]?.dataIndex;
                        const uv = uvRaw[i]; const rain = _rainVals[i] || 0;
                        const lines = [];
                        if (uv != null) lines.push(` ☀ UV Index: ${lang !== 'en' ? localNum(uv.toFixed(1), lang) : uv.toFixed(1)}`);
                        if (rain >= 10) lines.push(' ⚠ Heavy rain — farm advisory');
                        else if (rain >= 2) lines.push(' 💧 Light rain expected');
                        else lines.push(' ☀ Dry day — check irrigation');
                        return lines;
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: { color: '#b8d0e8', font: { size: 11 }, maxRotation: 0 },
                grid: { color: 'rgba(255,255,255,0.03)' }
            },
            y: {
                position: 'left', min: 0, max: _rainMax,
                ticks: { color: '#00D4FF', font: { size: 11 }, callback: v => `${lang !== 'en' ? localNum(String(v), lang) : v}mm`, maxTicksLimit: 5 },
                grid: { color: 'rgba(255,255,255,0.04)' },
                title: { display: true, text: t.rainMm, color: '#00D4FF44', font: { size: 11 } }
            },
            y1: {
                position: 'right',
                min: Math.max(0, Math.floor(_tmin) - 4),
                max: Math.ceil(_tmax) + 6,
                ticks: { color: '#FF6B35', font: { size: 11 }, callback: v => `${lang !== 'en' ? localNum(String(v), lang) : v}°`, maxTicksLimit: 6 },
                grid: { drawOnChartArea: false },
                title: { display: true, text: '°C', color: '#FF6B3544', font: { size: 10 } }
            },
        }
    };

    // 4. Organic Category
    const BD = data?.organic_Alert?.category_Breakdown || data?.organic_Summary?.category_Scores || {};
    const catLabels = { A: 'Veg', B: 'Water', C: 'Soil', D: 'Climate', E: 'Terrain', F: 'Trends' };
    const catLabelsMr = { A: 'वनस्पती', B: 'पाणी', C: 'माती', D: 'हवामान', E: 'भूभाग', F: 'कल' };
    const catLabelsHi = { A: 'वनस्पति', B: 'पानी', C: 'मिट्टी', D: 'जलवायु', E: 'भूभाग', F: 'कल' };
    const catLabelsTe = { A: 'వనస్పతి', B: 'నీరు', C: 'నేల', D: 'వాతావరణం', E: 'భూభాగం', F: 'ధోరణి' };
    const _catL = c => lang === 'mr' ? catLabelsMr[c] : lang === 'hi' ? catLabelsHi[c] : lang === 'te' ? catLabelsTe[c] : catLabels[c];
    const catScores = ['A', 'B', 'C', 'D', 'E', 'F'].map(c => { const v = BD[c]; return v == null ? 0 : typeof v === 'object' ? (v.score || 0) : v; });
    // Organic score colour — smooth gradient: red(0) → amber(50) → yellow-green(70) → neon-green(90+)
    const orgScoreColor = v => {
        if (v >= 90) return { bg: 'rgba(57,255,20,0.65)', border: '#39FF14' };
        if (v >= 80) return { bg: 'rgba(100,230,30,0.60)', border: '#64E61E' };
        if (v >= 70) return { bg: 'rgba(163,230,53,0.60)', border: '#A3E635' };
        if (v >= 60) return { bg: 'rgba(250,204,21,0.60)', border: '#FACC15' };
        if (v >= 50) return { bg: 'rgba(251,146,60,0.60)', border: '#FB923C' };
        if (v >= 35) return { bg: 'rgba(249,115,22,0.60)', border: '#F97316' };
        return { bg: 'rgba(255,74,110,0.60)', border: '#FF4A6E' };
    };
    const orgBarData = {
        labels: ['A', 'B', 'C', 'D', 'E', 'F'].map(c => _catL(c)),
        datasets: [{
            label: t.score, data: catScores,
            backgroundColor: catScores.map(s => orgScoreColor(s).bg),
            borderColor: catScores.map(s => orgScoreColor(s).border),
            borderWidth: 1.5, borderRadius: 5,
            datalabels: {
                display: true, anchor: 'end', align: 'top',
                color: catScores.map(s => orgScoreColor(s).border),
                font: { size: 12, weight: 'bold' },
                formatter: v => v > 0 ? `${lang !== 'en' ? localNum(String(Math.round(v)), lang) : Math.round(v)}` : ''
            }
        }]
    };
    const orgBarOpts = mkChart({
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: items => {
                        const allLabels = {
                            Veg: 'Vegetation Health', Water: 'Water Management', Soil: 'Soil Health', Climate: 'Climate', Terrain: 'Terrain', Trends: 'Temporal Trends',
                            वनस्पती: 'वनस्पती आरोग्य', पाणी: 'पाणी व्यवस्थापन', माती: 'माती आरोग्य', हवामान: 'हवामान', भूभाग: 'भूभाग', कल: 'कालांतर कल',
                            वनस्पति: 'वनस्पति स्वास्थ्य', पानी: 'पानी प्रबंधन', 'मिट्टी': 'मिट्टी स्वास्थ्य', जलवायु: 'जलवायु',
                            వనస్పతి: 'వృక్షసంపద ఆరోగ్యం', నీరు: 'నీటి నిర్వహణ', నేల: 'నేల ఆరోగ్యం', వాతావరణం: 'వాతావరణం', భూభాగం: 'భూభాగం', ధోరణి: 'కాల ధోరణి'
                        };
                        return allLabels[items[0]?.label] || items[0]?.label || '';
                    },
                    label: ctx => {
                        const s = ctx.parsed.y;
                        const c = s >= 80 ? '🟢' : s >= 60 ? '🟡' : s >= 40 ? '🟠' : '🔴';
                        const note = ctx.label === 'Trends' || ctx.label === 'कल' || ctx.label === 'ధోరణి'
                            ? (s < 50 ? ' (Need 6+ months history)' : '') : '';
                        return ` ${c} ${t.score}: ${lang !== 'en' ? localNum((s?.toFixed(1) ?? 0), lang) : (s?.toFixed(1) ?? 0)} / 100${note}`;
                    }
                }
            }
        },
        scales: {
            x: { ticks: { color: '#b8d0e8', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
            y: { min: 0, max: 100, ticks: { color: '#b8d0e8', font: { size: 11 }, callback: v => lang !== 'en' ? localNum(String(v), lang) : `${v}` }, grid: { color: 'rgba(255,255,255,0.04)' } },
        }
    });

    // ════════════════════════════════════════════════════════════
    // KPI blocks for info bar
    // ════════════════════════════════════════════════════════════
    // [FIX — reapplied, was lost during backup-base rebuild] ndviColor/
    // ndviStatus (used by the topbar badge and legend popup) previously
    // derived from raw `ndvi` (= s.health_Metric) directly, independent of
    // the curCropHealth unification used by the sidebar metrics-grid box.
    // That let the CROP HEALTH LEGEND popup show a different number/status
    // than the sidebar (e.g. sidebar 0.099 vs legend "गुण: 0.210"). Now
    // both read from the same source.
    const ndviColor = curCropHealth == null ? '#b8d0e8' :
        curCropHealth >= 0.5 ? '#39FF14' :
            curCropHealth >= 0.3 ? '#FFB800' :
                curCropHealth >= 0.0 ? '#FF4A6E' : '#00D4FF';  // negative = water body
    const ndviStatus = curCropHealth == null ? '' :
        curCropHealth >= 0.5 ? t.healthy :
            curCropHealth >= 0.3 ? t.moderate :
                curCropHealth >= 0.0 ? t.stressed : t.waterBareS;
    // ── Info bar KPIs ─────────────────────────────────────────────
    // Topbar already shows: Crop · NDVI · Moisture · Organic · High Alerts
    // Info bar shows DIFFERENT data: Root Zone · Soil pH · Elevation · Temp · Humidity · Rain · Yield · Alerts
    const yieldVal = data?.yield_Estimation?.yield_Estimate_kg_per_ha;
    const _ln = v => (lang !== 'en' && v != null) ? localNum(String(v), lang) : v;
    const kpis = data ? [
        { label: t.rootZone, val: rootZ != null ? `${_ln(rootZ.toFixed(1))}%` : '—', color: '#38BDF8', bar: rootZ, icon: <Droplets size={9} /> },
        { label: t.soilPh, val: s.soil_Ph != null ? _ln(s.soil_Ph.toFixed(1)) : '—', color: '#FACC15', bar: null, icon: null },
        { label: t.elevation, val: s.elevation_M != null ? `${_ln(Math.round(s.elevation_M))}m` : '—', color: '#A78BFA', bar: null, icon: null },
        { label: t.airTemp, val: s.temperature_C != null ? `${_ln(s.temperature_C.toFixed(1))}°C` : '—', color: '#FB923C', bar: null, icon: <Thermometer size={9} /> },
        { label: t.humidity, val: s.humidity != null ? `${_ln(Math.round(s.humidity))}%` : '—', color: '#60A5FA', bar: s.humidity, icon: <Droplets size={9} /> },
        { label: t.rain30d, val: s.chirps_Rainfall_mm != null ? `${_ln(Math.round(s.chirps_Rainfall_mm))}mm` : '—', color: '#818CF8', bar: null, icon: null },
        { label: t.yieldEst, val: yieldVal != null ? `${_ln(Math.round(yieldVal))} ${t.kgHaUnit}` : '—', color: '#FFB800', bar: null, icon: <TrendingUp size={9} /> },
        { label: t.alerts, val: _ln(String(alerts.length)), color: highCnt > 0 ? '#FF4A6E' : '#b8d0e8', bar: null, icon: <AlertTriangle size={9} /> },
    ] : null;

    // ════════════════════════════════════════════════════════════
    // CROP CALENDAR DATA
    // ── CROP_CALENDAR, CROP_YIELD_PARAMS, getCropName, CROP_KEYS ──────
    // Imported from ./CropData.js — 81 crops, 4 languages (MR/HI/TE/EN)
    // All growth stages, NDVI ranges, yield params, alert thresholds
    // ────────────────────────────────────────────────────────────────────;

    // ── Yield Deep Analysis computed values ───────────────────
    const yEst = data?.yield_Estimation;
    const yMethods = yEst?.methods_Used || [];
    const yCrop = yEst?.crop_Used_For_Model || cropP || 'Unknown';
    const ySeason = yEst?.season_Used || data?.meta?.season || '—';
    const yKgHa = yEst?.yield_Estimate_kg_per_ha || 0;
    const yTotalKg = yEst?.total_Estimated_Yield_kg || 0;
    // area_ha: yield_est has it; fallback to meta area_sqm / 10000 or area_Acres * 0.4047
    const yAreaHa = yEst?.area_ha
        || (data?.meta?.area_sqm ? data.meta.area_sqm / 10000 : 0)
        || (data?.meta?.area_Acres ? data.meta.area_Acres * 0.4047 : 0)
        || 0;
    // Recompute total if engine gave 0 but we now have area
    const yTotalKgFixed = yTotalKg > 0 ? yTotalKg : (yKgHa > 0 && yAreaHa > 0 ? Math.round(yKgHa * yAreaHa) : 0);
    const yRainF = yEst?.rain_Factor_Applied || 1;
    const yTempF = yEst?.temp_Factor_Applied || 1;
    // Confidence range: ±20% low, ±10% high scenario
    const yLow = Math.round(yKgHa * 0.80);
    const yHigh = Math.round(yKgHa * 1.20);
    // Method with highest yield
    const yBestM = yMethods.reduce((best, m) => m.yield_kg_ha > (best?.yield_kg_ha || 0) ? m : best, null);
    // yIsValid guard (unused in JSX — yield card already checks yKgHa > 0)

    // ── Crop Calendar: detect current stage from NDVI ─────────
    // Fuzzy match: engine may return "Gram/Chickpea", "Maize/Corn", "Jowar" etc.
    // [FIX — real root cause] Try the localized-name reverse lookup FIRST
    // (handles the farmer typing the crop name in Marathi/Hindi/Telugu —
    // the actual reason "Cotton" showed empty despite having real
    // CROP_CALENDAR data: cropP held "कापूस", which can never match the
    // English key "Cotton" via substring comparison). Falls back to the
    // existing English-text fuzzy match for when cropP is already English
    // (e.g. sourced from engineCrop).
    const _calKey = resolveCropKeyFromAnyLanguage(cropP)
        || Object.keys(CROP_CALENDAR).find(k =>
            k.toLowerCase() === (cropP || '').toLowerCase() ||
            k.toLowerCase().includes((cropP || '').toLowerCase()) ||
            (cropP || '').toLowerCase().includes(k.toLowerCase().split('/')[0])
        ) || 'Unknown';
    // [FIX — found via live testing] calData could be undefined here if
    // the crop doesn't fuzzy-match any CROP_CALENDAR key AND 'Unknown'
    // itself isn't a defined key either — the next line (calData.stages)
    // then throws a TypeError, crashing this whole render. That crash is
    // exactly why the Crop Calendar section (and this whole render pass)
    // silently failed for some crops (e.g. Cotton — seen in the "working
    // for some farms, not others" report) while working fine for others
    // (e.g. Pomegranate, which has a matching key). Now always falls back
    // to a safe empty-stages object, regardless of what's actually
    // defined in CROP_CALENDAR.
    const calData = CROP_CALENDAR[_calKey] || CROP_CALENDAR['Unknown'] || { stages: [], season: null };
    const calStages = calData.stages || [];
    // Find which stage current NDVI falls in
    const curNdvi = ndvi ?? 0;
    const activeStageIdx = calStages.findIndex(st =>
        curNdvi >= st.ndviRange[0] && curNdvi <= st.ndviRange[1]
    );
    // If no exact match, find closest
    const currentStageIdx = activeStageIdx >= 0 ? activeStageIdx :
        calStages.reduce((best, st, i) => {
            const mid = (st.ndviRange[0] + st.ndviRange[1]) / 2;
            const prev = calStages[best] ? (calStages[best].ndviRange[0] + calStages[best].ndviRange[1]) / 2 : 99;
            return Math.abs(curNdvi - mid) < Math.abs(curNdvi - prev) ? i : best;
        }, 0);

    // ════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════
    return (
        <div className="dashboard-container">
            <style>{`
                @keyframes vPulse { from{opacity:1;transform:scale(1)} to{opacity:.35;transform:scale(.8)} }
                @keyframes vRadio { from{opacity:1;transform:scale(1.1)} to{opacity:.4;transform:scale(0.85)} }

                /* ── Legend redesign ── */
                .ndvi-legend {
                    position: absolute;
                    bottom: 48px;
                    right: 10px;
                    z-index: 900;
                    background: rgba(8,14,24,0.94);
                    border: 1px solid rgba(57,255,20,0.25);
                    border-radius: 10px;
                    padding: 0;
                    width: 230px;
                    box-shadow: 0 4px 24px rgba(0,0,0,0.6), 0 0 14px rgba(57,255,20,0.09);
                    backdrop-filter: blur(8px);
                    overflow: hidden;
                }
                .ndvi-legend-head {
                    display: flex;
                    align-items: center;
                    padding: 8px 8px 7px 12px;
                    background: rgba(57,255,20,0.08);
                    border-bottom: 1px solid rgba(57,255,20,0.16);
                    gap: 4px;
                }
                .ndvi-legend-title {
                    font-family: 'Inter','Segoe UI','Noto Sans Devanagari',sans-serif;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    color: #39FF14;
                    text-transform: uppercase;
                    white-space: nowrap;
                    flex: 1;
                }
                .ndvi-legend-body {
                    padding: 7px 10px 5px;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .ndvi-legend-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 1px 0;
                }
                .ndvi-legend-swatch {
                    width: 13px;
                    height: 13px;
                    border-radius: 3px;
                    flex-shrink: 0;
                }
                .ndvi-legend-label {
                    flex: 1;
                    font-family: 'Inter','Segoe UI','Noto Sans Devanagari',sans-serif;
                    font-size: 11.5px;
                    color: #c8ddf0;
                    min-width: 0;
                    white-space: nowrap;
                }
                .ndvi-legend-range {
                    font-family: 'JetBrains Mono', monospace;
                    font-size: 11px;
                    color: #c8ddf0;
                    flex-shrink: 0;
                    letter-spacing: 0.2px;
                    min-width: 72px;
                    text-align: right;
                }
                .ndvi-legend-current {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 6px 10px;
                    background: rgba(57,255,20,0.06);
                    border-top: 1px solid rgba(57,255,20,0.13);
                    font-family: 'JetBrains Mono', monospace;
                    font-size: 10px;
                    letter-spacing: 0.3px;
                    gap: 6px;
                }
                .legend-close {
                    background: none;
                    border: none;
                    color: #94aec8;
                    font-size: 16px;
                    cursor: pointer;
                    padding: 2px 6px;
                    border-radius: 4px;
                    line-height: 1.2;
                    flex-shrink: 0;
                    margin-left: auto;
                    transition: all 0.15s;
                }
                .legend-close:hover { color: #FF4A6E; background: rgba(255,74,110,0.10); }
            `}</style>

            {/* ══════════ TOPBAR ══════════ */}
            <div className="topbar">
                {/* FIX: removed duplicate "BHUHIT" text — just icon + tagline */}
                <div className="topbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img style={{
                        width: 'clamp(40px, 4.5vw, 58px)',
                        height: 'clamp(40px, 4.5vw, 58px)',
                        objectFit: 'contain',
                        borderRadius: '6px',
                        flexShrink: 0,
                        marginLeft: '-14px',
                        marginTop: '-3px',
                    }} src={logo} alt="Energy Guru" />
                    <div className="topbar-brand-text">
                        <span className="topbar-brand-name">BHUHIT AGRI</span>
                        <span className="topbar-tag">{t.tagline}</span>
                    </div>
                </div>

                <div className="topbar-spacer" />

                {/* Post-scan KPIs in topbar */}
                {data && (
                    <div className="topbar-kpi-row">
                        {[
                            // { label: t.crop, val: cropPLocal || '—', color: '#39FF14' },
                            { label: t.ndvi, color: ndviColor, sub: ndviStatus }, // val: ndvi != null ? _ln(ndvi.toFixed(3)) : '—', 
                            { label: t.moisture, val: `${surf != null ? _ln(surf.toFixed(1)) : '—'}%`, color: '#00D4FF' },
                            { label: t.organic, val: `${orgScore != null ? _ln(orgScore.toFixed(1)) : '—'} ${orgGrade || ''}`, color: orgColor },
                            ...(highCnt > 0 ? [{ label: t.highAlerts, val: _ln(String(highCnt)), color: '#FF4A6E' }] : []),
                        ].map((k, i) => (
                            <div key={i} className="topbar-kpi">
                                <span className="topbar-kpi-label">{k.label}</span>
                                <span className="topbar-kpi-val" style={{ color: k.color }}>{k.val}</span>
                                {k.sub && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', color: k.color, opacity: 0.6, letterSpacing: '0.8px' }}>{k.sub}</span>}
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Chrome mr/te TTS hint — shows only when Chrome has no Indic voice */}
                    {noIndic && isChrome && (lang === 'mr' || lang === 'te') && (
                        <div style={{
                            position: 'absolute', top: 'calc(var(--topbar-h) + 2px)', right: 12,
                            zIndex: 9999, background: '#1a2640',
                            border: '1px solid rgba(57,255,20,0.3)', borderRadius: 7,
                            padding: '8px 12px', maxWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#39FF14', marginBottom: 4 }}>
                                {lang === 'mr' ? '🔊 Chrome मध्ये मराठी आवाज सुरू करा' : '🔊 Chrome లో తెలుగు వాయిస్ ప్రారంభించండి'}
                            </div>
                            <div style={{ fontSize: 11, color: '#b8d0e8', lineHeight: 1.5, marginBottom: 6 }}>
                                {lang === 'mr'
                                    ? 'Chrome Settings → Accessibility → Text-to-speech → Manage voices → मराठी add करा'
                                    : 'Chrome Settings → Accessibility → Text-to-speech → Manage voices → Telugu add చేయండి'}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => window.open('chrome://settings/accessibility', '_blank')}
                                    style={{
                                        flex: 1, padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(57,255,20,0.3)',
                                        background: 'rgba(57,255,20,0.1)', color: '#39FF14', fontSize: 10, fontWeight: 700, cursor: 'pointer'
                                    }}>
                                    {t.openSettings}
                                </button>
                                <button onClick={() => setNoIndic(false)}
                                    style={{
                                        padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.1)',
                                        background: 'none', color: '#7090a8', fontSize: 10, cursor: 'pointer'
                                    }}>
                                    {t.dismiss}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 🎙 Full-report listen button */}
                    {data && (
                        <button
                            onClick={voicePlaying && voiceSection === 'full' ? stopVoice : handleVoiceFull}
                            title={voicePlaying && voiceSection === 'full' ? 'Stop' : 'Listen to report'}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '5px 11px', width: 'auto', weight: '50px', borderRadius: 6, cursor: 'pointer',
                                fontSize: 15, fontWeight: 700, letterSpacing: 0.4,
                                border: `1px solid ${voicePlaying && voiceSection === 'full' ? '#39FF14' : 'rgba(57,255,20,0.28)'}`,
                                background: voicePlaying && voiceSection === 'full' ? 'rgba(57,255,20,0.16)' : 'rgba(57,255,20,0.06)',
                                color: voicePlaying && voiceSection === 'full' ? '#39FF14' : '#3a6a3a',
                                boxShadow: voicePlaying && voiceSection === 'full' ? '0 0 12px rgba(57,255,20,0.28)' : 'none',
                                transition: 'all 0.2s', flexShrink: 0,
                            }}>
                            <Radio size={12}
                                style={{
                                    animation: voicePlaying && voiceSection === 'full'
                                        ? 'vRadio 0.6s ease-in-out infinite alternate' : 'none',
                                    color: voicePlaying && voiceSection === 'full' ? '#39FF14' : 'inherit'
                                }} />
                            {voicePlaying && voiceSection === 'full'
                                ? (lang === 'mr' ? 'थांबा' : lang === 'hi' ? 'रोकें' : lang === 'te' ? 'ఆపండి' : 'Stop')
                                : (lang === 'mr' ? 'अहवाल ऐका' : lang === 'hi' ? 'रिपोर्ट सुनें' : lang === 'te' ? 'నివేదిక వినండి' : 'Listen')
                            }
                        </button>
                    )}
                    {/* Language Switcher */}
                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                        {LANGUAGES.map(l => (
                            <button key={l.code} onClick={() => setLang(l.code)}
                                title={l.name}
                                style={{
                                    padding: '3px 7px', borderRadius: '4px', cursor: 'pointer',
                                    fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', fontWeight: 700,
                                    letterSpacing: '0.5px', border: '1px solid',
                                    transition: 'all 0.15s',
                                    background: lang === l.code ? 'rgba(57,255,20,0.15)' : 'transparent',
                                    borderColor: lang === l.code ? '#39FF14' : 'rgba(255,255,255,0.12)',
                                    color: lang === l.code ? '#39FF14' : '#b8d0e8',
                                }}>
                                {l.label}
                            </button>
                        ))}
                    </div>
                    {/* Robot Operations link */}
                    <a
                        onClick={plantRobotOperation}
                        title="Robot Operations"
                        style={{
                            cursor: 'Pointer',
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '4px 10px', borderRadius: 6, textDecoration: 'none',
                            border: '1px solid rgba(167,139,250,0.35)',
                            background: 'rgba(167,139,250,0.08)',
                            color: '#A78BFA', fontSize: 11, fontWeight: 700,
                            letterSpacing: 0.4, flexShrink: 0, transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(167,139,250,0.18)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(167,139,250,0.08)'}
                    >
                        <Bot size={13} />
                        <span className="robot-link-text">
                            {lang === 'mr' ? 'रोबोट' : lang === 'hi' ? 'रोबोट' : lang === 'te' ? 'రోబోట్' : 'Robot Ops'}
                        </span>
                    </a>
                    <div className="live-badge">
                        <span className="live-pulse" />
                        {t.live}
                    </div>
                </div>
            </div>

            {/* ══════════ SIDEBAR ══════════ */}
            <div className="sidebar" id="sidebar-root">

                {/* INPUTS */}
                <SbHead label={t.geoLocation} />
                <div className="controls-wrap">

                    {/* ── LOCATION SEARCH ─── */}
                    <div className="search-field">
                        <div className="search-input-wrap">
                            <input
                                type="text"
                                className="styled-input search-input"
                                placeholder={t.searchPlaceholder || '🔍 Search village, district, location…'}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            />
                            <button
                                className="search-go-btn"
                                onClick={() => handleSearch()}
                                disabled={searchLoading}
                            >
                                {searchLoading ? '…' : '→'}
                            </button>
                        </div>
                        {searchResults.length > 0 && (
                            <div className="search-results">
                                {searchResults.map((r, i) => (
                                    <div key={i} className="search-result-row"
                                        onClick={() => selectSearchResult(r)}>
                                        <MapPin size={9} color="#39FF14" />
                                        <span className="search-result-name">
                                            {r.display_name.split(',').slice(0, 3).join(', ')}
                                        </span>
                                        <span className="search-result-type">{r.type}</span>
                                    </div>
                                ))}
                                <div className="search-results-close"
                                    onClick={() => setSearchResults([])}>
                                    {t.closeBtn}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="input-row">
                        <div className="field">
                            <label className="field-label">{t.latitude}</label>
                            <input type="number" step="0.000001"
                                value={lat} onChange={e => setLat(e.target.value)}
                                className="styled-input" />
                        </div>
                        <div className="field">
                            <label className="field-label">{t.longitude}</label>
                            <input type="number" step="0.000001"
                                value={lon} onChange={e => setLon(e.target.value)}
                                className="styled-input" />
                        </div>
                    </div>
                    <div className="input-row">
                        <div className="field">
                            <label className="field-label">{t.startDate}</label>
                            <input type="date" value={startDate} onChange={e => { userEditedDatesRef.current = true; setStartDate(e.target.value); }} className="styled-input" />
                        </div>
                        <div className="field">
                            <label className="field-label">{t.endDate}</label>
                            <input type="date" value={endDate} onChange={e => { userEditedDatesRef.current = true; setEndDate(e.target.value); }} className="styled-input" />
                        </div>
                    </div>
                    <div className="field">
                        <label className="field-label">{t.farmSize}</label>
                        <div className="size-row">
                            <input type="number" value={acres} onChange={e => { setAcres(e.target.value); setIsUserDrawn(false); }} className="styled-input" />
                            <select value={unit} onChange={e => { setUnit(e.target.value); setIsUserDrawn(false); }} className="styled-input">
                                <option value="acres">{t.unitAcres}</option>
                                <option value="guntha">{t.unitGuntha}</option>
                                <option value="meter">{t.unitMeter}</option>
                            </select>
                        </div>
                        {/* Drawn area display — appears when farmer draws polygon on map */}
                        {(() => {
                            const d = sqmToDisplay(drawnAreaSqm);
                            if (!d) return null;
                            return (
                                <div style={{
                                    marginTop: 6, padding: '7px 10px',
                                    background: isUserDrawn
                                        ? 'rgba(57,255,20,0.08)' : 'rgba(255,255,255,0.03)',
                                    borderRadius: 6,
                                    border: isUserDrawn
                                        ? '1px solid rgba(57,255,20,0.3)' : '1px solid rgba(255,255,255,0.07)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{
                                            fontSize: 11, color: isUserDrawn ? '#39FF14' : '#94aec8',
                                            fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase'
                                        }}>
                                            {isUserDrawn ? t.drawnArea : t.previewArea}
                                        </span>
                                        {/*{isUserDrawn && (*/}
                                        {/*    <button onClick={() => { setIsUserDrawn(false); setBoundary(null); setDrawnAreaSqm(null); }}*/}
                                        {/*        style={{ fontSize: 16, color: '#94aec8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>*/}
                                        {/*        ✕ Clear*/}
                                        {/*    </button>*/}
                                        {/*)}*/}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                                        {[
                                            { lbl: t.unitAcres, val: d.acres_str || '—' },
                                            { lbl: t.unitGuntha, val: d.guntha_str || '—' },
                                            { lbl: t.unitSqm, val: d.sqm_str },
                                        ].map(({ lbl, val }) => (
                                            <div key={lbl} style={{
                                                textAlign: 'center', background: 'rgba(255,255,255,0.03)',
                                                borderRadius: 4, padding: '4px 2px'
                                            }}>
                                                <div style={{ fontSize: 14, color: '#94aec8' }}>{lbl}</div>
                                                <div style={{
                                                    fontSize: 14, fontWeight: 700, color: isUserDrawn ? '#39FF14' : '#94a3b8',
                                                    fontFamily: "'JetBrains Mono',monospace"
                                                }}>{val}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {isUserDrawn && (
                                        <div style={{ fontSize: 16, color: '#94aec8', marginTop: 4, textAlign: 'center' }}>
                                            {t.autoFilled}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                    <div className="field">
                        <label className="field-label">
                            {t.cropName}&nbsp;
                            <span style={{ color: '#7090a8', fontWeight: 700 }}>{t.cropOptional}</span>
                        </label>
                        <div className="crop-wrap">
                            <span className="crop-icon"><Leaf size={12} /></span>
                            <input type="text" value={t[cropsName] ?? cropsName} onChange={e => setCropName(e.target.value)}
                                placeholder={t.cropPlaceholder} className="styled-input" />
                        </div>
                    </div>
                    <button onClick={handleAnalyze} disabled={loading}
                        className={`scan-btn ${loading ? 'is-scanning' : ''}`}>
                        <span className="btn-inner">
                            {loading
                                ? <><Radio size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t.scanning}</>
                                : <><Zap size={13} /> {t.smartFarmScan}</>}
                        </span>
                    </button>
                    {/* ── Polygon-too-large error (replaces alert popup) ─── */}
                    {drawError && (
                        <div style={{
                            marginTop: 8, padding: '10px 12px',
                            background: 'rgba(255,74,110,0.12)',
                            border: '1px solid rgba(255,74,110,0.4)',
                            borderRadius: 6, color: '#FF4A6E',
                            fontSize: 13, fontWeight: 700, lineHeight: 1.5,
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                        }}>
                            <span style={{ fontSize: 16, flexShrink: 0 }}>✋</span>
                            <span>{drawError}</span>
                            <button onClick={() => setDrawError('')}
                                style={{
                                    marginLeft: 'auto', background: 'none', border: 'none',
                                    color: '#FF4A6E', cursor: 'pointer', fontSize: 14, flexShrink: 0
                                }}>✕</button>
                        </div>
                    )}
                    {error && <div className="err-box">&#9888; {error}</div>}

                    {/* ── SCAN HISTORY TOGGLE ─────────────────── */}
                    {scanHistory.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                            <button
                                className="history-toggle-btn"
                                onClick={() => setShowHistory(v => !v)}
                            >
                                <Clock size={11} />
                                <span>{t.recentScans} ({scanHistory.length})</span>
                                <ChevronRight size={11} style={{
                                    marginLeft: 'auto',
                                    transform: showHistory ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 0.2s'
                                }} />
                            </button>

                            {showHistory && (
                                <div className="history-panel">
                                    <div className="history-panel-head">
                                        <span>{t.lastScans} {scanHistory.length} {t.scans}</span>
                                        <button className="history-clear-btn" onClick={clearHistory}>
                                            <Trash2 size={9} /> {t.clearAll}
                                        </button>
                                    </div>
                                    <div className="history-list">
                                        {scanHistory.map(entry => {
                                            const nd = entry.result?.ndvi;
                                            const ndCol = nd == null ? '#b8d0e8' : nd >= 0.5 ? '#39FF14' : nd >= 0.3 ? '#FFB800' : '#FF4A6E';
                                            const dt = new Date(entry.timestamp);
                                            const dtLocale = lang === 'mr' ? 'mr-IN' : lang === 'hi' ? 'hi-IN' : lang === 'te' ? 'te-IN' : 'en-IN';
                                            const dateStr = dt.toLocaleDateString(dtLocale, { day: '2-digit', month: 'short' });
                                            const timeStr = dt.toLocaleTimeString(dtLocale, { hour: '2-digit', minute: '2-digit', hour12: true });
                                            return (
                                                <div key={entry.id} className="history-card"
                                                    onClick={() => restoreScan(entry)}>
                                                    {/* Left accent */}
                                                    <div className="history-card-stripe" style={{ background: ndCol }} />
                                                    <div className="history-card-body">
                                                        {/* Row 1: crop + date */}
                                                        <div className="history-card-top">
                                                            <span className="history-crop">{entry.result?.crop || t.unknown}</span>
                                                            <span className="history-date">{dateStr} {timeStr}</span>
                                                        </div>
                                                        {/* Row 2: quick stats */}
                                                        <div className="history-card-stats">
                                                            <span style={{ color: ndCol }}>
                                                                {t.histHealth} {nd != null ? localNum(nd.toFixed(2), lang) : '—'}
                                                            </span>
                                                            <span style={{ color: '#00D4FF' }}>
                                                                {entry.result?.moisture?.toFixed(1) ?? '—'}% H₂O
                                                            </span>
                                                            <span style={{ color: entry.result?.orgScore >= 80 ? '#39FF14' : entry.result?.orgScore >= 60 ? '#FFB800' : '#FF4A6E' }}>
                                                                {t.orgLabel} {entry.result?.orgScore != null ? localNum(entry.result.orgScore.toFixed(0), lang) : '—'}
                                                            </span>
                                                            {entry.result?.alerts > 0 && (
                                                                <span style={{ color: '#FF4A6E' }}>
                                                                    ⚠ {entry.result.alerts}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Row 3: location */}
                                                        <div className="history-card-loc">
                                                            <MapPin size={8} color="#7090a8" />
                                                            {entry.lat.toFixed(4)}, {entry.lon.toFixed(4)}
                                                            &nbsp;·&nbsp;{entry.acres} {entry.unit}
                                                        </div>
                                                    </div>
                                                    <button className="history-del-btn"
                                                        onClick={(e) => deleteScan(entry.id, e)}
                                                        title="Remove">
                                                        <Trash2 size={9} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* RESULTS */}
                {data && !loading && (
                    <div className="results-panel">

                        {/* CROP */}
                        {/*{cropP && (*/}
                        {/*    <>*/}
                        {/*        <div id="crop-id-anchor" />*/}
                        {/*        <SbHead label={t.cropIdentification} voiceBtn={<VoiceBtn playing={voiceSection === 'crop' && voicePlaying} onPlay={() => handleVoiceSection('crop')} lang={lang} />} />*/}
                        {/*        <div className="crop-card" >*/}
                        {/*            <div className="crop-row">*/}
                        {/*                <div>*/}
                        {/*                    <div className="crop-detect-lbl">*/}
                        {/*                        <Leaf size={9} color="#39FF14" />*/}
                        {/*                        {cropIsOverride ? t.userConfirmed : t.autoDetected}*/}
                        {/*                    </div>*/}
                        {/*                    {cropIsOverride && (*/}
                        {/*                        <div className="crop-engine-note">*/}
                        {/*                            {t.engineDetected}: <span style={{ color: '#FFB800' }}>{engineCrop}</span>*/}
                        {/*                        </div>*/}
                        {/*                    )}*/}
                        {/*                    <div className="crop-name">{cropPLocal}</div>*/}

                        {/*                    */}{/* Confidence bar + low-confidence warning */}
                        {/*                    {cropConf != null && (*/}
                        {/*                        <div className="crop-conf-bar-wrap">*/}
                        {/*                            <div className="crop-conf-bar">*/}
                        {/*                                <div className="crop-conf-fill" style={{*/}
                        {/*                                    width: `${cropConf}%`,*/}
                        {/*                                    background: cropConf >= 70 ? '#39FF14' : cropConf >= 45 ? '#FFB800' : '#FF4A6E'*/}
                        {/*                                }} />*/}
                        {/*                            </div>*/}
                        {/*                            <span className="crop-conf-pct" style={{*/}
                        {/*                                color: cropConf >= 70 ? '#39FF14' : cropConf >= 45 ? '#FFB800' : '#FF4A6E'*/}
                        {/*                            }}>{_ln(cropConf.toFixed(0))}%</span>*/}
                        {/*                        </div>*/}
                        {/*                    )}*/}
                        {/*                    {cropConf != null && cropConf < 50 && (*/}
                        {/*                        <div style={{ marginTop: 4, fontSize: 10, color: '#FFB800', background: 'rgba(255,184,0,0.09)', borderRadius: 4, padding: '3px 7px' }}>*/}
                        {/*                            {lang === 'mr' ? '⚠ कमी विश्वासार्हता — पीक नाव टाइप करा' :*/}
                        {/*                                lang === 'hi' ? '⚠ कम विश्वसनीयता — फसल नाम टाइप करें' :*/}
                        {/*                                    lang === 'te' ? '⚠ తక్కువ నమ్మకత — పంట పేరు టైప్ చేయండి' :*/}
                        {/*                                        '⚠ Low confidence — type crop name above to confirm'}*/}
                        {/*                        </div>*/}
                        {/*                    )}*/}

                        {/*                    */}{/* Alternative crops */}
                        {/*                    {!cropIsOverride && (data?.crop_Identification?.crop_Alternative_1 || data?.crop_Identification?.crop_Alternative_2) && (*/}
                        {/*                        <div className="crop-alts-block">*/}
                        {/*                            <span className="crop-alts-lbl">{t.alternatives}:</span>*/}
                        {/*                            {[data?.crop_Identification?.crop_Alternative_1, data?.crop_Identification?.crop_Alternative_2]*/}
                        {/*                                .filter(Boolean).map((c, i) => (*/}
                        {/*                                    <span key={i} className="crop-alt-pill">{c}</span>*/}
                        {/*                                ))}*/}
                        {/*                        </div>*/}
                        {/*                    )}*/}
                        {/*                </div>*/}
                        {/*                {cropConf != null && <ConfRing val={cropConf} lang={lang} />}*/}
                        {/*            </div>*/}
                        {/*            <div className="pill-row">*/}
                        {/*                {data.meta?.season && <span className="pill pill-g">{seasonName(data.meta.season, lang)}</span>}*/}
                        {/*                {data.meta?.image_Date && (() => {*/}
                        {/*                    const raw = data.meta.image_Date; // e.g. "2026-06-17"*/}
                        {/*                    const months = {*/}
                        {/*                        mr: ['जाने', 'फेब्रु', 'मार्च', 'एप्रि', 'मे', 'जून', 'जुलै', 'ऑग', 'सप्टे', 'ऑक्टो', 'नोव्हे', 'डिसें'],*/}
                        {/*                        hi: ['जन', 'फ़र', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अग', 'सित', 'अक्टू', 'नव', 'दिस'],*/}
                        {/*                        te: ['జన', 'ఫిబ్ర', 'మార్చి', 'ఏప్రి', 'మే', 'జూన్', 'జూలై', 'ఆగ', 'సెప్టె', 'అక్టో', 'నవ', 'డిసె'],*/}
                        {/*                    };*/}
                        {/*                    let label = raw;*/}
                        {/*                    if (lang !== 'en' && months[lang]) {*/}
                        {/*                        const [y, m, d] = raw.split('-');*/}
                        {/*                        const mArr = months[lang];*/}
                        {/*                        const mName = mArr[parseInt(m, 10) - 1] || m;*/}
                        {/*                        label = `${_ln(d)} ${mName} ${_ln(y)}`;*/}
                        {/*                    }*/}
                        {/*                    return <span className="pill pill-s">📡 {label}</span>;*/}
                        {/*                })()}*/}
                        {/*                {data.meta?.area_Tier_Label && (() => {*/}
                        {/*                    const raw = data.meta.area_Tier_Label.split('/')[0].trim();*/}
                        {/*                    const tierMap = {*/}
                        {/*                        mr: { 'SMALL': 'लहान (१ एकरापेक्षा कमी)', 'MEDIUM': 'मध्यम (१-१० एकर)', 'LARGE': 'मोठे (१०+ एकर)', 'VERY LARGE': 'अतिमोठे (५०+ एकर)' },*/}
                        {/*                        hi: { 'SMALL': 'छोटा (1 एकड़ से कम)', 'MEDIUM': 'मध्यम (1-10 एकड़)', 'LARGE': 'बड़ा (10+ एकड़)', 'VERY LARGE': 'बहुत बड़ा (50+ एकड़)' },*/}
                        {/*                        te: { 'SMALL': 'చిన్న (1 ఎకరా కన్నా తక్కువ)', 'MEDIUM': 'మధ్యస్థం (1-10 ఎకరాలు)', 'LARGE': 'పెద్ద (10+ ఎకరాలు)', 'VERY LARGE': 'చాలా పెద్ద (50+ ఎకరాలు)' },*/}
                        {/*                    };*/}
                        {/*                    const key = raw.toUpperCase();*/}
                        {/*                    const translated = tierMap[lang]?.[key];*/}
                        {/*                    return <span className="pill pill-v" title={data.meta.area_Tier_Label}>{translated || raw}</span>;*/}
                        {/*                })()}*/}
                        {/*                */}{/*{data.meta?.data_Source && <span className="pill pill-a">{data.meta.data_Source?.split(' ')[0]}</span>}*/}
                        {/*            </div>*/}
                        {/*            {orgScore != null && (*/}
                        {/*                <div className="org-row">*/}
                        {/*                    <div className="org-track">*/}
                        {/*                        <div className="org-fill" style={{ width: `${orgScore}%`, background: orgColor }} />*/}
                        {/*                    </div>*/}
                        {/*                    <span className="org-label" style={{ color: orgColor }}>*/}
                        {/*                        {_ln(orgScore.toFixed(1))} {orgGrade}*/}
                        {/*                    </span>*/}
                        {/*                </div>*/}
                        {/*            )}*/}
                        {/*        </div>*/}
                        {/*    </>*/}
                        {/*)}*/}

                        {/* SPECTRAL INDICES — unique values only */}
                        <SbHead label={t.spectralIndices} voiceBtn={<VoiceBtn playing={voiceSection === 'health' && voicePlaying} onPlay={() => handleVoiceSection('health')} lang={lang} />} />
                        {/*<div className="metrics-grid">*/}
                        {/*    <MBox label={t.mboxCropHealth} value={lang !== 'en' ? localNum((ndvi || 0).toFixed(3), lang) : ndvi} unit="" color="#39FF14" bar={(ndvi || 0) * 100} />*/}
                        {/*    <MBox label={t.mboxCropGrowth} value={lang !== 'en' ? localNum((s.evi || 0).toFixed(3), lang) : s.evi} unit="" color="#4ADE80" bar={(s.evi || 0) * 100} />*/}
                        {/*    <MBox label={t.mboxMoisture} value={lang !== 'en' ? localNum((s.ndwi || 0).toFixed(3), lang) : s.ndwi} unit="" color="#00D4FF" bar={((s.ndwi || 0) + 1) * 50} />*/}
                        {/*    <MBox label={t.mboxCropStress} value={lang !== 'en' ? localNum((s.ndre || 0).toFixed(3), lang) : s.ndre} unit="" color="#A78BFA" bar={(s.ndre || 0) * 100} />*/}
                        {/*</div>*/}

                        {metricsAsOfDate && (
                            <div style={{ padding: '2px 4px 4px', fontSize: 11, color: '#5a7a94' }}>
                                {lang === 'en' ? `As of latest available scan: ${_fmtAsOf(metricsAsOfDate)}`
                                    : lang === 'mr' ? `उपलब्ध ताज्या स्कॅननुसार: ${_fmtAsOf(metricsAsOfDate)}`
                                        : lang === 'hi' ? `नवीनतम उपलब्ध स्कैन के अनुसार: ${_fmtAsOf(metricsAsOfDate)}`
                                            : `తాజా అందుబాటులో ఉన్న స్కాన్ ప్రకారం: ${_fmtAsOf(metricsAsOfDate)}`}
                            </div>
                        )}
                        <div className="metrics-grid">
                            <MBox label={t.mboxCropHealth} value={curCropHealth == null ? '—' : (lang !== 'en' ? localNum(curCropHealth.toFixed(3), lang) : curCropHealth)} unit="" color="#39FF14" bar={curCropHealth == null ? 0 : curCropHealth * 100} />
                            <MBox label={t.mboxCropGrowth} value={curCropGrowth == null ? '—' : (lang !== 'en' ? localNum(curCropGrowth.toFixed(3), lang) : curCropGrowth)} unit="" color="#4ADE80" bar={curCropGrowth == null ? 0 : curCropGrowth * 100} />
                            <MBox label={t.mboxMoisture} value={curCropMoisture == null ? '—' : (lang !== 'en' ? localNum(curCropMoisture.toFixed(2), lang) : curCropMoisture)} unit="" color="#00D4FF" bar={curCropMoisture == null ? 0 : (curCropMoisture + 1) * 50} />
                            <MBox label={t.mboxCropStress} value={curCropStressValue == null ? '—' : (lang !== 'en' ? localNum(curCropStressValue.toFixed(3), lang) : curCropStressValue)} unit="" color="#A78BFA" bar={curCropStressPctForBar == null ? 0 : curCropStressPctForBar} />
                        </div>
                        {/* ── पीक आरोग्य (NDVI) Legend ── */}
                        {/*{(() => {*/}
                        {/*    const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;*/}
                        {/*    return (*/}
                        {/*        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: '2px 4px 5px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>*/}

                        {/*            {[*/}
                        {/*                { color: '#1B5E20', label: L('उत्तम > ०.८', 'उत्तम > ०.८', 'అత్యుత్తమ > 0.8', 'Excellent > 0.8') },*/}
                        {/*                { color: '#39FF14', label: L('निरोगी ०.६–०.८', 'स्वस्थ 0.6–0.8', 'ఆరోగ్యకరం 0.6–0.8', 'Healthy 0.6–0.8') },*/}
                        {/*                { color: '#8BC34A', label: L('चांगले ०.५–०.६', 'अच्छा 0.5–0.6', 'మంచి 0.5–0.6', 'Good 0.5–0.6') },*/}
                        {/*                { color: '#FFEB3B', label: L('मध्यम ०.३–०.५', 'मध्यम 0.3–0.5', 'మధ్యస్థం 0.3–0.5', 'Fair 0.3–0.5') },*/}
                        {/*                { color: '#FF9800', label: L('ताण ०.२–०.३', 'तनाव 0.2–0.3', 'ఒత్తిడి 0.2–0.3', 'Stress 0.2–0.3') },*/}
                        {/*                { color: '#FF4A6E', label: L('गंभीर < ०.२', 'गंभीर < 0.2', 'తీవ్రం < 0.2', 'Critical < 0.2') },*/}
                        {/*            ].map(({ color, label }) => (*/}
                        {/*                <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>*/}
                        {/*                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />*/}
                        {/*                    <span style={{ color, fontSize: 11 }}>{label}</span>*/}
                        {/*                </span>*/}
                        {/*            ))}*/}
                        {/*            <span style={{ color: '#5a7a94', marginLeft: 'auto', fontSize: 10 }}>{t.mboxCropHealth}</span>*/}
                        {/*         </div>*/}
                        {/*    );*/}
                        {/*})()}*/}

                        {/* ── पीक वाढ (EVI) Legend ── */}
                        {(() => {
                            const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;
                            return (
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: '2px 4px 5px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                    {[
                                        { color: '#39FF14', label: L('दाट > ०.५', 'घना > 0.5', 'దట్టమైన > 0.5', 'Dense > 0.5') },
                                        { color: '#4ADE80', label: L('चांगले ०.३–०.५', 'अच्छा 0.3–0.5', 'మంచి 0.3–0.5', 'Good 0.3–0.5') },
                                        { color: '#FFB800', label: L('मध्यम ०.१५–०.३', 'मध्यम 0.15–0.3', 'మధ్యస్థం 0.15–0.3', 'Fair 0.15–0.3') },
                                        { color: '#FF4A6E', label: L('कमी < ०.१५', 'कम < 0.15', 'తక్కువ < 0.15', 'Low < 0.15') },
                                    ].map(({ color, label }) => (
                                        <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ color, fontSize: 11 }}>{label}</span>
                                        </span>
                                    ))}
                                    <span style={{ color: '#5a7a94', marginLeft: 'auto', fontSize: 11 }}>{t.mboxCropGrowth}</span>
                                </div>
                            );
                        })()}
                        {/* ── ओलावा (NDWI) Legend ── */}
                        {(() => {
                            const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;
                            return (
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: '2px 4px 5px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                    {[
                                        { color: '#00D4FF', label: L('भरपूर पाणी > ०.२', 'भरपूर पानी > 0.2', 'పుష్కలమైన నీరు > 0.2', 'Wet > 0.2') },
                                        { color: '#38BDF8', label: L('सामान्य ०–०.२', 'सामान्य 0–0.2', 'సాధారణ 0–0.2', 'Normal 0–0.2') },
                                        { color: '#FFB800', label: L('कमी −०.२–०', 'कम −0.2–0', 'తక్కువ −0.2–0', 'Low −0.2–0') },
                                        { color: '#FF4A6E', label: L('कोरडे < −०.२', 'सूखा < −0.2', 'పొడిగా < −0.2', 'Dry < −0.2') },
                                    ].map(({ color, label }) => (
                                        <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ color, fontSize: 11 }}>{label}</span>
                                        </span>
                                    ))}
                                    <span style={{ color: '#5a7a94', marginLeft: 'auto', fontSize: 11 }}>{t.mboxMoisture}</span>
                                </div>
                            );
                        })()}
                        {/* ── पीक ताण (NDRE) Legend ── */}
                        {(() => {
                            const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;
                            return (
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: '2px 4px 8px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                    {[
                                        { color: '#39FF14', label: L('उत्तम > ०.३', 'उत्तम > 0.3', 'ఉత్తమ > 0.3', 'Healthy > 0.3') },
                                        { color: '#A78BFA', label: L('सौम्य ०.१५–०.३', 'सौम्य 0.15–0.3', 'మృదువైన 0.15–0.3', 'Mild 0.15–0.3') },
                                        { color: '#FFB800', label: L('मध्यम ०.०५–०.१५', 'मध्यम 0.05–0.15', 'మధ్యస్థం 0.05–0.15', 'Moderate 0.05–0.15') },
                                        { color: '#FF4A6E', label: L('तीव्र < ०.०५', 'तीव्र < 0.05', 'తీవ్రమైన < 0.05', 'Severe < 0.05') },
                                    ].map(({ color, label }) => (
                                        <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ color, fontSize: 11 }}>{label}</span>
                                        </span>
                                    ))}
                                    <span style={{ color: '#5a7a94', marginLeft: 'auto', fontSize: 11 }}>{t.mboxCropStress}</span>
                                </div>
                            );
                        })()}

                        {/* MOISTURE — surface + root zone together */}
                        <SbHead label={t.soilMoisture} voiceBtn={<VoiceBtn playing={voiceSection === 'moisture' && voicePlaying} onPlay={() => handleVoiceSection('moisture')} lang={lang} />} />
                        <div className="metrics-grid">
                            <MBox label={t.mboxSurface} value={surf != null ? _ln(surf.toFixed(1)) : surf} unit="%" color="#00D4FF" bar={surf} />
                            <MBox label={t.mboxRootZone} value={rootZ != null ? _ln(rootZ.toFixed(1)) : rootZ} unit="%" color="#38BDF8" bar={rootZ} />
                        </div>

                        {/* SOIL & FIELD — nitrogen is unique here */}
                        <SbHead label={t.soilField} voiceBtn={<VoiceBtn playing={voiceSection === 'soil' && voicePlaying} onPlay={() => handleVoiceSection('soil')} lang={lang} />} />
                        <div className="metrics-grid">
                            <MBox label={t.mboxNitrogen} value={nLabel(s.latest_CropNitrogen ?? ((s.hls_NITROGEN_VALUE + s.nitrogen_Index) / 2))} unit="" color={nitColor(s.latest_CropNitrogen ?? ((s.hls_NITROGEN_VALUE + s.nitrogen_Index) / 2))} />
                            {/*<MBox label={t.mboxNitrogen} value={nLabel(s.nitrogen_Index)} unit="" color={nitColor(s.nitrogen_Index)} />*/}
                            <MBox label={t.mboxSoilPh} value={lang !== 'en' && s.soil_Ph != null ? localNum((s.soil_Ph ?? 7).toFixed(1), lang) : (s.soil_Ph ?? 7)} unit=" pH" color="#FACC15" />
                            <MBox label={t.mboxElevation} value={lang !== 'en' && s.elevation_M != null ? localNum(Math.round(s.elevation_M), lang) : s.elevation_M} unit=" m" color="#A78BFA" />
                            <MBox label={t.mboxHumidity} value={lang !== 'en' && s.humidity != null ? localNum(Math.round(s.humidity ?? 0), lang) : (s.humidity ?? 0)} unit="%" color="#60A5FA" />
                            <MBox label={t.mboxRain30d} value={lang !== 'en' && s.chirps_Rainfall_mm != null ? localNum(Math.round(s.chirps_Rainfall_mm), lang) : s.chirps_Rainfall_mm} unit=" mm" color="#818CF8" />
                            <MBox label={t.mboxAirTemp} value={lang !== 'en' && s.temperature_C != null ? localNum(s.temperature_C?.toFixed(1), lang) : s.temperature_C} unit="°C" color="#FB923C" />
                        </div>
                        {/* ── Nitrogen Legend ── */}
                        {(() => {
                            const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;
                            return (
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: '2px 4px 4px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                    {[
                                        { color: '#FFB800', label: L('जास्त (HIGH) > ०.६५', 'अधिक (HIGH) > 0.65', 'అధికం (HIGH) > 0.65', 'HIGH > 0.65') },
                                        { color: '#34D399', label: L('योग्य (OPT) ०.३५–०.६५', 'उचित (OPT) 0.35–0.65', 'సరైన (OPT) 0.35–0.65', 'OPT 0.35–0.65') },
                                        { color: '#FF4A6E', label: L('कमी (LOW) < ०.३५', 'कम (LOW) < 0.35', 'తక్కువ (LOW) < 0.35', 'LOW < 0.35') },
                                    ].map(({ color, label }) => (
                                        <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ color, fontSize: 11 }}>{label}</span>
                                        </span>
                                    ))}
                                    <span style={{ color: '#5a7a94', marginLeft: 'auto', fontSize: 11 }}>{t.mboxNitrogen}</span>
                                </div>
                            );
                        })()}
                        {/* ── Soil pH Legend ── */}
                        {(() => {
                            const L = (mr, hi, te, en) => lang === 'mr' ? mr : lang === 'hi' ? hi : lang === 'te' ? te : en;
                            return (
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: '2px 4px 6px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                    {[
                                        { color: '#FF4A6E', label: L('आम्लीय < ६', 'अम्लीय < 6', 'ఆమ్లీయ < 6', 'Acidic < 6') },
                                        { color: '#39FF14', label: L('आदर्श ६–७.५', 'आदर्श 6–7.5', 'ఆదర్శ 6–7.5', 'Ideal 6–7.5') },
                                        { color: '#FFB800', label: L('क्षारीय > ७.५', 'क्षारीय > 7.5', 'క్షారీయ > 7.5', 'Alkaline > 7.5') },
                                    ].map(({ color, label }) => (
                                        <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ color, fontSize: 11 }}>{label}</span>
                                        </span>
                                    ))}
                                    <span style={{ color: '#5a7a94', marginLeft: 'auto', fontSize: 11 }}>{t.mboxSoilPh}</span>
                                </div>
                            );
                        })()}

                        {/* ── SOIL HEALTH CARD ── */}
                        {(() => {
                            const sc = data?.soil_Health_Card;
                            if (!sc) return null;
                            const gradeColor = sc.fertility_grade === 'A' ? '#39FF14'
                                : sc.fertility_grade === 'B' ? '#FFB800'
                                    : sc.fertility_grade === 'C' ? '#FB923C' : '#FF4A6E';
                            const soilParams = [
                                { key: 'nitrogen', label: t.soilNitrogen || 'Nitrogen', unit: 'g/kg', color: '#34D399', icon: '🌿' },
                                { key: 'soc', label: t.soilSOC || 'Organic Carbon', unit: 'g/kg', color: '#A78BFA', icon: '🍂' },
                                { key: 'cec', label: t.soilCEC || 'CEC', unit: 'mmol/kg', color: '#60A5FA', icon: '⚡' },
                                { key: 'clay', label: t.soilClay || 'Clay', unit: '%', color: '#FB923C', icon: '🏺' },
                                { key: 'sand', label: t.soilSand || 'Sand', unit: '%', color: '#FBBF24', icon: '🏖' },
                                { key: 'bulk_density', label: t.soilBD || 'Bulk Density', unit: 'kg/dm³', color: '#F97316', icon: '⚖' },
                            ];
                            return (
                                <>
                                    <SbHead label={t.soilHealthCard || '🌱 माती आरोग्य कार्ड'} icon="🌱" voiceBtn={<VoiceBtn playing={voiceSection === 'soil' && voicePlaying} onPlay={() => handleVoiceSection('soil')} lang={lang} />} />
                                    <div style={{ padding: '4px 8px 8px' }}>
                                        {/* Fertility Score */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 6, padding: '6px 10px', border: `1px solid ${gradeColor}33` }}>
                                            <div>
                                                <div style={{ fontSize: 16, color: '#b8d0e8', marginBottom: 2 }}>{t.soilFertility || 'Fertility Score'}</div>
                                                <div style={{ fontSize: 15, color: '#b8d0e8' }}>{sc.fertility_marathi || sc.fertility_label}</div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 36, fontWeight: 'bold', color: gradeColor, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>
                                                    {sc.fertility_score}<span style={{ fontSize: 16 }}>/100</span>
                                                </div>
                                                <div style={{ fontSize: 18, color: gradeColor }}>{t.soilGrade} {sc.fertility_grade}</div>
                                            </div>
                                        </div>

                                        {/* Soil Type */}
                                        <div style={{ marginBottom: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 5, padding: '5px 8px', border: '1px solid rgba(255,255,255,0.07)' }}>
                                            <span style={{ fontSize: 16, color: '#b8d0e8' }}>{t.soilType || 'Soil Type'}: </span>
                                            <span style={{ fontSize: 18, color: '#FFB800', fontWeight: 'bold' }}>
                                                {lang === 'mr' || lang === 'hi' ? sc.soil_type_marathi : sc.soil_type}
                                            </span>
                                        </div>

                                        {/* pH from existing */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 4 }}>
                                            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 5, padding: '5px 8px', border: '1px solid rgba(255,255,255,0.07)' }}>
                                                <div style={{ fontSize: 15, color: '#b8d0e8' }}>pH (0-5cm)</div>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#FACC15', fontFamily: "'JetBrains Mono',monospace" }}>
                                                    {sc.ph?.surface ?? s.soil_Ph ?? '—'}
                                                </div>
                                                <div style={{ fontSize: 16, color: '#b8d0e8' }}>
                                                    {sc.ph?.surface >= 7.5 ? t.soilAlkaline
                                                        : sc.ph?.surface >= 6 ? t.soilNeutral
                                                            : t.soilAcidic}
                                                </div>
                                            </div>
                                        </div>

                                        {/* 6 Parameters */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                                            {soilParams.map(p => {
                                                const param = sc[p.key];
                                                const val = param?.surface;
                                                const adv = param?.marathi;
                                                return (
                                                    <div key={p.key} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 5, padding: '5px 8px', border: `1px solid ${p.color}22` }}>
                                                        <div style={{ fontSize: 15, color: '#b8d0e8', marginBottom: 1 }}>
                                                            {p.icon} {p.label}
                                                        </div>
                                                        <div style={{ fontSize: 18, fontWeight: 'bold', color: p.color, fontFamily: "'JetBrains Mono',monospace" }}>
                                                            {val != null ? val : '—'}
                                                            <span style={{ fontSize: 16, fontWeight: 'normal', color: '#b8d0e8' }}> {p.unit}</span>
                                                        </div>
                                                        {adv && (
                                                            <div style={{ fontSize: 16, color: '#b8d0e8', marginTop: 2, lineHeight: 1.3 }}>
                                                                {adv}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Govt Lab Test Link */}
                                        <div
                                            onClick={() => window.open('https://soilhealth.dac.gov.in', '_blank')}
                                            style={{ marginTop: 8, padding: '7px 10px', background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.2)', borderRadius: 6, cursor: 'pointer', textAlign: 'center' }}>
                                            <div style={{ fontSize: 16.5, color: '#39FF14', fontWeight: 'bold' }}>
                                                {t.soilGovtTest || '🏛 Govt Lab Test ₹5'}
                                            </div>
                                            <div style={{ fontSize: 16, color: '#b8d0e8', marginTop: 2 }}>
                                                {t.soilGovtDesc || 'Free test for NPK + micronutrients'}
                                            </div>
                                        </div>

                                        {/* Source */}
                                        <div style={{ fontSize: 18, color: 'rgba(139,160,184,0.4)', textAlign: 'center', marginTop: 5 }}>
                                            {t.soilSource || 'SoilGrids ISRIC 250m'} · Heavy metals need lab test
                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        {/* ALERTS — FIX: proper styled block */}
                        {alerts.length > 0 && (
                            <>
                                <SbHead label={t.alertsWarnings} icon="⚠" voiceBtn={<VoiceBtn playing={voiceSection === 'alerts' && voicePlaying} onPlay={() => handleVoiceSection('alerts')} lang={lang} />} />
                                <div className="alerts-wrap">
                                    <div className="alerts-header">
                                        <div className="alerts-title">
                                            <AlertTriangle size={11} />
                                            {lang !== 'en' ? localNum(String(alerts.length), lang) : alerts.length} {lang === 'mr' ? 'सूचना' : lang === 'hi' ? 'चेतावनी' : lang === 'te' ? 'హెచ్చరికలు' : 'ALERTS'}
                                        </div>
                                        <div className="alerts-count">
                                            {highCnt > 0 && <span className="badge-high">{lang !== 'en' ? localNum(String(highCnt), lang) : highCnt} {lang === 'mr' ? 'उच्च' : lang === 'hi' ? 'उच्च' : lang === 'te' ? 'అధిక' : 'HIGH'}</span>}
                                            <span className="badge-total">{lang !== 'en' ? localNum(String(alerts.length), lang) : alerts.length} {lang === 'mr' ? 'एकूण' : lang === 'hi' ? 'कुल' : lang === 'te' ? 'మొత్తం' : 'TOTAL'}</span>
                                        </div>
                                    </div>
                                    <div className="alerts-body">
                                        <div className="alerts-list">
                                            {alerts.map((a, i) => <AlertRow key={i} a={a} lang={lang} />)}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ORGANIC GOALS — farmer-focused goal cards */}
                        {data?.organic_Alert && (
                            <>
                                <SbHead label={t.organicGoals} icon="🌿" voiceBtn={<VoiceBtn playing={voiceSection === 'organic' && voicePlaying} onPlay={() => handleVoiceSection('organic')} lang={lang} />} />
                                <OrganicGoals
                                    bd={data.organic_Alert?.category_Breakdown || {}}
                                    score={orgScore}
                                    grade={orgGrade}
                                    topIssues={(data.organic_Alert?.top_Issues || [])
                                        .filter(i => typeof i === 'string' && !i.includes('=') && !i.includes('>') && !i.includes('flood_detected'))
                                        .slice(0, 2)}
                                    failCount={data.organic_Alert?.fail_Count || 0}
                                    t={t}
                                    lang={lang}
                                />
                            </>
                        )}

                        {/* YIELD DEEP ANALYSIS */}
                        {data?.yield_Estimation && (
                            <>
                                <SbHead label={t.yieldDeepTitle || 'Yield Analysis'} icon="📊" voiceBtn={<VoiceBtn playing={voiceSection === 'yield' && voicePlaying} onPlay={() => handleVoiceSection('yield')} lang={lang} />} />
                                <div className="yield-card">
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                                        <span className="yield-big">{_ln(yKgHa.toLocaleString())}</span>
                                        <span className="yield-unit">{t.kgHaUnit}</span>
                                        <span style={{ marginLeft: 'auto', fontSize: 16, color: '#b8d0e8', fontFamily: "'JetBrains Mono',monospace" }}>{ySeason}</span>
                                    </div>
                                    <div className="yield-sub" style={{ marginBottom: 6 }}>
                                        {t.totalApprox} <strong style={{ color: '#FFB800' }}>{_ln(yTotalKgFixed.toLocaleString(undefined, { maximumFractionDigits: 0 }))} {t.kgUnit}</strong>
                                        &nbsp;· {yAreaHa > 0 ? `${_ln((yAreaHa * 2.471).toFixed(2))} ${t.acresUnit}` : '—'} · {t[cropsName] ?? cropsName}
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, color: '#b8d0e8', marginBottom: 3 }}>
                                            <span>▼ {_ln(yLow.toLocaleString())}</span>
                                            <span style={{ color: '#FFB800', fontWeight: 'bold' }}>{_ln(yKgHa.toLocaleString())} {t.kgHaUnit}</span>
                                            <span>▲ {_ln(yHigh.toLocaleString())}</span>
                                        </div>
                                        <div style={{ position: 'relative', height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'visible' }}>
                                            <div style={{ position: 'absolute', left: '10%', right: '10%', top: 0, bottom: 0, background: 'rgba(255,184,0,0.12)', borderRadius: 4 }} />
                                            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: -1, width: 4, height: 10, background: '#FFB800', borderRadius: 2, boxShadow: '0 0 4px #FFB800' }} />
                                            <div style={{ position: 'absolute', left: '10%', top: 1, width: 2, height: 6, background: 'rgba(255,184,0,0.4)', borderRadius: 1 }} />
                                            <div style={{ position: 'absolute', right: '10%', top: 1, width: 2, height: 6, background: 'rgba(255,184,0,0.4)', borderRadius: 1 }} />
                                        </div>
                                        <div style={{ fontSize: 15, color: '#b8d0e8', marginTop: 2, textAlign: 'center' }}></div>
                                    </div>
                                    {/*<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>*/}
                                    {/*    {[*/}
                                    {/*        { label: t.rainFactor, val: yRainF, color: yRainF >= 1 ? '#39FF14' : '#FFB800' },*/}
                                    {/*        { label: t.tempFactor, val: yTempF, color: yTempF >= 1 ? '#39FF14' : '#FF4A6E' },*/}
                                    {/*    ].map(f => (*/}
                                    {/*        <div key={f.label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: '4px 6px', border: '1px solid rgba(255,255,255,0.06)' }}>*/}
                                    {/*            <div style={{ fontSize: 15, color: '#b8d0e8', marginBottom: 1 }}>{f.label}</div>*/}
                                    {/*            <div style={{ fontSize: 15, fontWeight: 'bold', color: f.color, fontFamily: "'JetBrains Mono',monospace" }}>{_ln(f.val.toFixed(2))}×</div>*/}
                                    {/*        </div>*/}
                                    {/*    ))}*/}
                                    {/*</div>*/}
                                    {/*{yMethods.length > 0 && (*/}
                                    {/*    <div style={{ marginBottom: 4 }}>*/}
                                    {/*        <div style={{ fontSize: 12, color: '#b8d0e8', marginBottom: 4, letterSpacing: '0.6px', textTransform: 'uppercase' }}>*/}
                                    {/*            {t.yieldMethods || 'Methods Used'}*/}
                                    {/*        </div>*/}
                                    {/*        {yMethods.map((m, i) => {*/}
                                    {/*            const barW = Math.min(100, Math.max(5, (m.yield_kg_ha / (yHigh || 1)) * 100));*/}
                                    {/*            const isTop = m === yBestM;*/}
                                    {/*            return (*/}
                                    {/*                <div key={i} style={{ marginBottom: 6 }}>*/}
                                    {/*                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 2 }}>*/}
                                    {/*                        <span style={{ color: isTop ? '#FFB800' : '#b8d0e8' }}>{isTop ? '★ ' : ''}{m.method.split('(')[0].trim()}</span>*/}
                                    {/*                        <span style={{ color: '#E2E8F0', fontFamily: "'JetBrains Mono',monospace" }}>*/}
                                    {/*                            {_ln(m.yield_kg_ha.toLocaleString())} {t.kgHaUnit}*/}
                                    {/*                            <span style={{ color: '#b8d0e8', marginLeft: 4 }}>w:{_ln(String(m.weight))}×</span>*/}
                                    {/*                        </span>*/}
                                    {/*                    </div>*/}
                                    {/*                    <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>*/}
                                    {/*                        <div style={{ width: `${barW}%`, height: '100%', background: isTop ? '#FFB800' : 'rgba(255,184,0,0.4)', borderRadius: 3, transition: 'width 0.6s ease' }} />*/}
                                    {/*                    </div>*/}
                                    {/*                </div>*/}
                                    {/*            );*/}
                                    {/*        })}*/}
                                    {/*    </div>*/}
                                    {/*)}*/}
                                    {/*<div style={{ fontSize: 14, color: 'rgba(139,160,184,0.6)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 5, lineHeight: 1.4 }}>*/}
                                    {/*    {t.indicative}*/}
                                    {/*</div>*/}
                                </div>
                            </>
                        )}

                        {/* CROP CALENDAR */}
                        {/*{data && calStages.length > 0 && (*/}
                        {/*    <>*/}
                        <SbHead label={t.cropCalendar || 'Crop Calendar'} icon="📅" voiceBtn={<VoiceBtn playing={voiceSection === 'calendar' && voicePlaying} onPlay={() => handleVoiceSection('calendar')} lang={lang} />} />
                        <div style={{ padding: '0 2px 8px' }}>
                            {calStages[currentStageIdx] && (
                                <div style={{ background: `${calData.color}18`, border: `1px solid ${calData.color}44`, borderRadius: 6, padding: '6px 10px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 16 }}>{calStages[currentStageIdx].icon}</span>
                                    <div>
                                        <div style={{ fontSize: 14, color: '#b8d0e8', marginBottom: 1 }}>{t.currentStageLabel || t.currentStage}</div>
                                        <div style={{ fontSize: lang === 'mr' ? 13 : 12, fontWeight: 'bold', color: calData.color }}>
                                            {lang === 'mr' ? calStages[currentStageIdx].mr : lang === 'hi' ? calStages[currentStageIdx].hi : lang === 'te' ? calStages[currentStageIdx].te : calStages[currentStageIdx].name}
                                        </div>
                                        <div style={{ fontSize: 14, color: '#b8d0e8' }}>
                                            {t.rangeLabel}: {_ln(String(calStages[currentStageIdx].ndviRange[0]))}–{_ln(String(calStages[currentStageIdx].ndviRange[1]))} · {t.nowLabel}: {_ln(String(ndvi?.toFixed(3) ?? '—'))}
                                            &nbsp;· {t.nowLabel}: <span style={{ color: calData.color }}>{curNdvi.toFixed(3)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', fontSize: 18, flexDirection: 'column', gap: 4 }}>
                                {calStages.map((st, i) => {
                                    const isCurrent = i === currentStageIdx;
                                    const isPast = i < currentStageIdx;
                                    const stageName = lang === 'mr' ? (st.mr || st.name) : lang === 'hi' ? (st.hi || st.name) : lang === 'te' ? (st.te || st.name) : st.name;
                                    const totalW = calStages[calStages.length - 1]?.weeks[1] || 1;
                                    const stW = ((st.weeks[1] - st.weeks[0]) / totalW) * 100;
                                    const stL = (st.weeks[0] / totalW) * 100;
                                    // Devanagari needs min 11px to render correctly — below that glyphs corrupt
                                    const fSize = lang === 'mr'
                                        ? (isCurrent ? 11 : 10.5)
                                        : (isCurrent ? 9.5 : 8.5);
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: isCurrent ? calData.color : isPast ? `${calData.color}60` : 'rgba(255,255,255,0.12)', border: isCurrent ? `2px solid ${calData.color}` : 'none', boxShadow: isCurrent ? `0 0 6px ${calData.color}` : 'none' }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '12px', fontWeight: isCurrent ? 'bold' : 'normal', color: isCurrent ? calData.color : isPast ? '#60A5FA' : '#b8d0e8', whiteSpace: 'nowrap' }}>
                                                        {st.icon} {stageName}
                                                    </span>
                                                    <span style={{ fontSize: 15, color: 'rgba(139,160,184,0.5)', flexShrink: 0, marginLeft: 4 }}>
                                                        {lang === 'mr' ? `आ${_ln(String(st.weeks[0]))}–${_ln(String(st.weeks[1]))}` :
                                                            lang === 'hi' ? `स${_ln(String(st.weeks[0]))}–${_ln(String(st.weeks[1]))}` :
                                                                lang === 'te' ? `వా${_ln(String(st.weeks[0]))}–${_ln(String(st.weeks[1]))}` :
                                                                    `W${st.weeks[0]}–${st.weeks[1]}`}
                                                    </span>
                                                </div>
                                                <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 2, marginTop: 2, overflow: 'hidden', position: 'relative' }}>
                                                    <div style={{ position: 'absolute', left: `${stL}%`, width: `${stW}%`, height: '100%', borderRadius: 2, background: isCurrent ? calData.color : isPast ? `${calData.color}50` : 'rgba(255,255,255,0.08)' }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ marginTop: 8, fontSize: 16, color: '#b8d0e8', textAlign: 'center' }}>
                                {seasonName(calData.season, lang)} {t.seasonLabel} · {calStages[calStages.length - 1]?.weeks[1] || '—'} {t.weeksTotal}
                            </div>
                        </div>
                        {/*    </>*/}
                        {/*)}*/}

                    </div>
                )}
            </div>

            {/* ══════════ RIGHT COLUMN ══════════ */}
            <div className="right-col">

                {/* INFO BAR */}
                <div className="info-bar">
                    {!data ? (
                        <div className="info-bar-empty">{t.runScanView}</div>
                    ) : (
                        kpis.map((k, i) => (
                            <div key={i} className="kpi-block">
                                <div className="kpi-stripe" style={{ background: k.color }} />
                                <div className="kpi-label" style={{ color: k.color }}>
                                    {k.icon && React.cloneElement(k.icon, { color: k.color })}
                                    {k.label}
                                </div>
                                <div className="kpi-value" style={{ color: k.color }}>
                                    {k.val ?? '—'}{k.unit || ''}
                                </div>
                                {k.sub && (
                                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '8px', color: k.color, opacity: 0.65, letterSpacing: '0.8px', marginTop: '1px' }}>
                                        {k.sub}
                                    </div>
                                )}
                                {k.bar != null && (
                                    <div className="kpi-bar">
                                        <div className="kpi-bar-fill" style={{ width: `${Math.max(0, Math.min(100, k.bar || 0))}%`, background: k.color, opacity: 0.6 }} />
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* MAP — FIX: flex-1 so it fills all space between info-bar and bottom-panel */}
                <div className="map-wrap">
                    {loading && <div className="scanner-overlay"><div className="scan-line" /></div>}

                    {/* Bottom-right corner bracket */}
                    <div className="map-corner-br" />

                    {/* Coordinate badge */}
                    <div className="coord-badge">
                        {t.latitude} <span>{parseFloat(lat).toFixed(6)}</span>
                        &nbsp;&nbsp;{t.longitude} <span>{parseFloat(lon).toFixed(6)}</span>
                    </div>

                    <MapContainer center={[lat, lon]} zoom={5}
                        style={{ height: '100%', width: '100%' }} zoomControl={false}>
                        {/*<TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Tiles &copy; Esri" maxZoom={19} />*/}
                        <TileLayer url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" attribution="" maxZoom={21} />
                        <MapController
                            onSelect={(la, lo) => { setLat(la); setLon(lo); }}
                            boundary={boundary}
                            onDrawn={(coords) => {
                                const sqm = calcPolygonAreaSqm(coords);
                                // Convert entered farm size to sqm
                                const farmVal = parseFloat(acres) || 0;
                                const maxSqm = unit === 'acres' ? farmVal * 4047
                                    : unit === 'guntha' ? farmVal * 101.17
                                        : unit === 'hectare' ? farmVal * 10000
                                            : farmVal; // meter²

                                // ── Block if drawn area > farm size ──────────
                                if (maxSqm > 0 && sqm > maxSqm * 1.10) {
                                    const drawnAc = (sqm / 4047).toFixed(2);
                                    const limitAc = (maxSqm / 4047).toFixed(2);
                                    const msg = lang === 'mr'
                                        ? `⚠ आखलेले क्षेत्र ${drawnAc} एकर आहे, परंतु शेत आकार फक्त ${limitAc} एकर आहे. कृपया लहान क्षेत्र आखा.`
                                        : lang === 'hi'
                                            ? `⚠ खींचा गया क्षेत्र ${drawnAc} एकड़ है, लेकिन खेत का आकार ${limitAc} एकड़ है. कृपया छोटा क्षेत्र खींचें.`
                                            : lang === 'te'
                                                ? `⚠ గీసిన విస్తీర్ణం ${drawnAc} ఎకరాలు, కానీ పొలం పరిమాణం ${limitAc} ఎకరాలు మాత్రమే. దయచేసి చిన్న పరిధిని గీయండి.`
                                                : `⚠ Drawn area (${drawnAc} ac) is larger than farm size (${limitAc} ac). Please draw a smaller boundary.`;
                                    // Show inline error — no alert() popup on mobile
                                    setDrawError(msg);
                                    clearTimeout(drawErrorTimer.current);
                                    drawErrorTimer.current = setTimeout(() => setDrawError(''), 5000);
                                    return false; // polygon rejected — signal MapController to flash red
                                }

                                // ── Accept polygon ────────────────────────────
                                setDrawError('');
                                setIsUserDrawn(true);
                                setBoundary(coords);
                                setDrawnAreaSqm(sqm);
                                // Auto-fill farm size from drawn area
                                if (sqm > 0) {
                                    const ac = sqm / 4047.0;
                                    setAcres(parseFloat(ac.toFixed(4)));
                                    setUnit('acres');
                                }
                                // [FIX] Sync lat/lon to the drawn boundary's centroid.
                                // Previously lat/lon stayed stale from a prior marker
                                // click/search — causing the API request's latitude/
                                // longitude to point at a different location than the
                                // actual user_polygon, so farm_Boundary in the response
                                // and the sidebar coordinates would disagree.
                                const centLat = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
                                const centLon = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
                                setLat(centLat.toFixed(6));
                                setLon(centLon.toFixed(6));
                            }}
                        />
                        <Marker
                            position={[lat, lon]}
                            icon={L.divIcon({
                                className: 'custom-icon',
                                html: `<div style="
                                    width:10px;height:10px;
                                    background:#39FF14;border-radius:50%;
                                    box-shadow:0 0 0 3px rgba(57,255,20,0.2),0 0 12px #39FF14;
                                    border:1.5px solid rgba(255,255,255,0.9);
                                "></div>`
                            })}
                        />
                        {boundary && (
                            <Polygon
                                ref={polyRef}
                                positions={boundary}
                                eventHandlers={{
                                    'pm:edit': e => setBoundary(e.target.getLatLngs()[0].map(ll => [ll.lat, ll.lng]))
                                }}
                                pathOptions={{
                                    color: '#39FF14', weight: 1.5,
                                    fillColor: '#39FF14', fillOpacity: 0.08,
                                    interactive: true,
                                }}
                            />
                        )}
                    </MapContainer>

                    {/* MAP OVERLAY — compact field badge (bottom-left, above coord) */}
                    {/* Shows ONLY data not visible elsewhere: Crop + Organic + Alerts */}
                    {data && (
                        <div className="map-farm-badge">
                            {/*<span className="map-badge-crop" style={{ color: ndviColor }}>*/}
                            {/*    {cropPLocal || t.unknown}*/}
                            {/*</span>*/}
                            <span className="map-badge-sep">·</span>
                            <span className="map-badge-org" style={{ color: orgColor }}>
                                {t.orgLabel} {orgScore != null ? _ln(orgScore.toFixed(0)) : '—'} {orgGrade}
                            </span>
                            {alerts.length > 0 && (
                                <>
                                    <span className="map-badge-sep">·</span>
                                    <span className="map-badge-alert"
                                        style={{ color: highCnt > 0 ? '#FF4A6E' : '#FFB800' }}>
                                        ⚠ {_ln(String(alerts.length))} {t.alerts}{highCnt > 0 ? ` (${_ln(String(highCnt))} ${lang === 'mr' ? 'उच्च' : lang === 'hi' ? 'उच्च' : lang === 'te' ? 'అధిక' : 'HI'})` : ''}
                                    </span>
                                </>
                            )}
                            <span className="map-badge-sep">·</span>
                            <span className="map-badge-area">{_ln(String(acres))} {unit === 'acres' ? t.unitAcres : unit === 'guntha' ? t.unitGuntha : t.unitMeter}</span>
                        </div>
                    )}

                    {/* Crop health badge — top center */}
                    {data && ndvi != null && (
                        <div className="map-health-badge" style={{ borderColor: ndviColor, width: '180px' }}>
                            <span style={{
                                color: ndviColor, fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', letterSpacing: '1.5px', fontWeight: 700
                            }}>
                                {ndvi >= 0.5 ? t.healthyCrop : ndvi >= 0.3 ? t.moderateStress : ndvi >= 0.0 ? t.cropStress : t.waterBare}
                            </span>
                        </div>
                    )}

                    {/* ── CROP HEALTH LEGEND ──────────────────── */}
                    {legendVisible && (
                        <div className="ndvi-legend">
                            <div className="ndvi-legend-head">
                                <span className="ndvi-legend-title">{t.legendTitle}</span>
                                <button className="legend-close"
                                    onClick={() => setLegendVisible(false)}>×</button>
                            </div>
                            <div className="ndvi-legend-body">
                                {[
                                    { color: '#1565C0', label: t.legendWaterBare, range: `< ${_ln('0.0')}` },
                                    { color: '#f44336', label: t.legendVeryStressed, range: `${_ln('0.0')} – ${_ln('0.2')}` },
                                    { color: '#FF9800', label: t.legendStressed, range: `${_ln('0.2')} – ${_ln('0.35')}` },
                                    { color: '#FFEB3B', label: t.legendModerate, range: `${_ln('0.35')} – ${_ln('0.5')}` },
                                    { color: '#8BC34A', label: t.legendGood, range: `${_ln('0.5')} – ${_ln('0.65')}` },
                                    { color: '#39FF14', label: t.legendHealthy, range: `${_ln('0.65')} – ${_ln('0.8')}` },
                                    { color: '#1B5E20', label: t.legendVeryHealthy, range: `> ${_ln('0.8')}` },
                                ].map(({ color, label, range }) => (
                                    <div key={range} className="ndvi-legend-row">
                                        <div className="ndvi-legend-swatch" style={{ background: color, boxShadow: `0 0 4px ${color}66` }} />
                                        <span className="ndvi-legend-label">{label}</span>
                                        <span className="ndvi-legend-range">{range}</span>
                                    </div>
                                ))}
                            </div>
                            {/* Current Crop Health indicator */}
                            {curCropHealth != null && (
                                <div className="ndvi-legend-current">
                                    <span style={{ color: ndviColor }}>▶ {t.legendScore}: {_ln(curCropHealth.toFixed(3))}</span>
                                    <span style={{ color: ndviColor, fontWeight: 700, fontFamily: "'Inter','Segoe UI','Noto Sans Devanagari',sans-serif", letterSpacing: '0.2px' }}>{ndviStatus}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Legend show button when hidden */}
                    {!legendVisible && (
                        <button className="legend-show-btn"
                            onClick={() => setLegendVisible(true)}>
                            {t.legendShowBtn}
                        </button>
                    )}

                </div>

                {/* BOTTOM CHARTS PANEL */}
                <div className="bottom-panel">

                    {/* Chart 1 — Crop Health Trend */}
                    <div className="bp-col">
                        <div className="bp-head">
                            <span className="bp-dot" style={{ background: '#39FF14', boxShadow: '0 0 5px #39FF14' }} />
                            <span className="bp-title">{t.ndviTrend}</span>
                            <span className="bp-badge" style={{ background: 'rgba(57,255,20,0.1)', color: '#39FF14' }}>{trendWindowLabel}</span>
                        </div>
                        <div className="bp-chart">
                            <LazyChart>
                                {ndviValid.length > 0 ? <Line data={ndviData} options={ndviOpts} /> : <NoData text={t.runScan} />}
                            </LazyChart>
                        </div>
                    </div>

                    {/* Chart 2 — Soil Moisture */}
                    <div className="bp-col">
                        <div className="bp-head">
                            <span className="bp-dot" style={{ background: '#00D4FF', boxShadow: '0 0 5px #00D4FF' }} />
                            <span className="bp-title">{t.soilMoistureChart}</span>
                            <span className="bp-badge" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF' }}>{t.sarSmap}</span>
                        </div>
                        <div className="bp-chart">
                            <LazyChart>
                                {data ? <Bar data={moistData} options={moistOpts} /> : <NoData text={t.runScan} />}
                            </LazyChart>
                        </div>
                    </div>

                    {/* Chart 3 — 3-Day Forecast */}
                    <div className="bp-col">
                        <div className="bp-head">
                            <span className="bp-dot" style={{ background: '#FF8C42', boxShadow: '0 0 5px #FF8C42' }} />
                            <span className="bp-title">{t.threeDayForecast}</span>
                            <span className="bp-badge" style={{ background: 'rgba(255,140,66,0.1)', color: '#FF8C42' }}>{t.openMeteo}</span>
                        </div>
                        <div className="bp-chart">
                            <LazyChart>
                                {data && fDays > 0 ? <Bar data={fcastData} options={fcastOpts} /> : <NoData text={t.runScan} />}
                            </LazyChart>
                        </div>
                    </div>

                    {/* Chart 4 — Organic Score */}
                    <div className="bp-col">
                        <div className="bp-head">
                            <span className="bp-dot" style={{ background: '#39FF14', boxShadow: '0 0 5px #39FF14' }} />
                            <span className="bp-title">{t.organicScore}</span>
                            <span className="bp-badge" style={{ background: 'rgba(57,255,20,0.1)', color: '#39FF14' }}>
                                {orgScore ? `${_ln(orgScore.toFixed(0))}/${_ln('100')}` : 'A–F'}
                            </span>
                        </div>
                        <div className="bp-chart">
                            <LazyChart>
                                {data?.organic_Alert ? <Bar data={orgBarData} options={orgBarOpts} /> : <NoData text={t.runScan} />}
                            </LazyChart>
                        </div>
                    </div>

                </div>
                <div style={{ width: "100%", color: "darkorange", fontSize: "13px", margin: "2%" }}>{t.custNotice}</div>
            </div>

        </div>
    );
}

export default AgriDashboard;